import { supabase } from "./supabaseClient";
import { ProductListing } from "./types";
import { Database } from "./database.types";

type BookingRow = Database["public"]["Tables"]["bookings"]["Row"];
type ListingRow = Database["public"]["Tables"]["listings"]["Row"];
type WishlistRow = Database["public"]["Tables"]["wishlists"]["Row"];

// Shape actually held in the `wishlist` state: each row from the
// `wishlists` table, enriched with its joined `listings` record (itself
// carrying `seller_applications`), or `null` if the listing was deleted.
// This is deliberately narrower than `WishlistRow` (no `buyer_id` /
// `created_at`) and adds the `listings` field `fetchWishlist` attaches —
// it does not represent an actual `wishlists` table row.
type WishlistItem = {
  id: WishlistRow["id"];
  listing_id: WishlistRow["listing_id"];
  listings: (ListingRow & { seller_applications: any | null }) | null;
};

// Shape of the realtime "postgres_changes" event payload for the listings
// table, defined locally (rather than imported from @supabase/supabase-js)
// so it doesn't depend on that package being listed as a direct dependency.
type ListingsChangePayload = {
  eventType: "INSERT" | "UPDATE" | "DELETE";
  new: ListingRow;
  old: Partial<ListingRow>;
};
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { gsap } from "gsap";
import { motion, AnimatePresence } from "framer-motion";
import SellerPortal from "./SellerPortal";
import StorePage from "./StorePage";
import {
  getInitialStoreCode,
  setPendingStore,
  readPendingStore,
  clearPendingStore,
} from "./storeLinks";
import StaggeredMenu, { StaggeredMenuBurgerIcon } from "./StaggeredMenu";
import Loader from "./Loader";
import Preloader_002 from "./Preloader_002";
import TileTransition from "./TileTransition";
import BlurText from "./BlurText";
import StarRating from "./StarRating";
import { SmoothInput } from "./SmoothInput";
import ScrollToTopButton from "./ScrollToTopButton";
import CountySelectDialog, { CountyFilterButton } from "./CountySelectDialog";
import { TermsAcceptanceSheet, LegalReaderSheet } from "./LegalSheets";
import {
  MENU_LABEL_TO_DOC,
  MENU_LABEL_TO_MAILTO,
  hasAcceptedTerms,
  recordTermsAcceptance,
} from "./LegalContent";
import type { LegalDocId } from "./LegalContent";
import {
  ALL_COUNTIES_LABEL,
  COUNTIES,
  listingMatchesCounty,
  resolveListingCounty,
} from "./KenyaCounties";
import {
  CATEGORIES,
  SUBCATEGORY_TO_PARENT,
  DEFAULT_CATEGORY_THEME,
  getThemeColorForRawCategory,
  getCategoryAbbrForRawCategory,
  getCategoryDisplayLabel,
  getSubcategoryDisplayName,
  isListingHiddenFromMarketplace,
} from "./categoryTheme";
import {
  Search,
  LogOut,
  User,
  Home,
  X,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Lock,
  Grid,
  CheckCircle2,
  CreditCard,
  ShoppingCart,
  Coins,
  ShoppingBag,
  SlidersHorizontal,
  ClipboardList,
  ArrowUp,
  Info,
  Menu,
  LifeBuoy,
  HelpCircle,
  Mail,
  Flag,
  Building2,
  MapPin,
  Scale,
  ShieldCheck,
  FileText,
  LucideIcon,
} from "lucide-react";

// `<model-viewer>` is a web component (Google's <model-viewer>), not a React
// component — TS doesn't know its tag/attributes, so this tells JSX to allow
// it. Real runtime behavior comes entirely from the loaded script (see the
// dynamic <script type="module"> injected inside EntryScreen below).
declare global {
  namespace JSX {
    interface IntrinsicElements {
      "model-viewer": any;
    }
  }
}

// Replace these with your hosted (e.g. Supabase Storage) public URLs once
// the two .glb files are uploaded. Everything else just works once these
// point at real files.
const CREDIT_CARD_GLB_URL =
  "https://jnoeucdmnuffzctyhomt.supabase.co/storage/v1/object/public/assets/models/inua_credit_card.glb";
const DELIVERY_BOX_GLB_URL =
  "https://jnoeucdmnuffzctyhomt.supabase.co/storage/v1/object/public/assets/models/IDB%20FINAL.glb?v=3";
// Replace with your hosted URL for the compressed coin model once uploaded.
const GOLD_COIN_GLB_URL =
  "https://jnoeucdmnuffzctyhomt.supabase.co/storage/v1/object/public/assets/models/gold_coin.glb";
// Replace with your hosted URL for the recolored sale tag model once uploaded.
const SALE_TAG_GLB_URL =
  "https://jnoeucdmnuffzctyhomt.supabase.co/storage/v1/object/public/assets/models/sale_tag.glb";

// --- APP THEME (dark / light) ---
// Dark keeps INUA's existing near-black app background with dark-grey
// containers. Light mirrors WhatsApp's light theme — a soft light-grey
// app background with white containers — so containers stay visibly
// distinct from the background in BOTH modes, just with the contrast
// direction flipped (dark: containers lighter than bg; light:
// containers are the crisp white surface against a grey bg, same as
// WhatsApp's own light theme).
type ThemeName = "dark" | "light";
const THEME_STORAGE_KEY = "inua_theme";

export const ThemeContext = React.createContext<{
  theme: ThemeName;
  toggleTheme: () => void;
}>({
  theme: "dark",
  toggleTheme: () => {},
});

export function useTheme() {
  return React.useContext(ThemeContext);
}

function getInitialTheme(): ThemeName {
  if (typeof window === "undefined") return "dark";
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
    return saved === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

// CSS custom properties consumed via Tailwind's `bg-[var(--x)]` /
// `text-[var(--x)]` arbitrary values throughout the buyer screens, so
// every themed surface updates instantly when `data-theme` flips on the
// app's root element — no per-component prop drilling needed for colors.
const THEME_VARS_CSS = `
  [data-theme="dark"] {
    --bg-app: #060609;
    --bg-container: #161619;
    --bg-container-2: #1E1E22;
    --bg-header: #060609;
    --surface-soft: rgba(255,255,255,0.05);
    --surface-soft-hover: rgba(255,255,255,0.1);
    --border-soft: rgba(255,255,255,0.08);
    --border-softer: rgba(255,255,255,0.05);
    --text-primary: #FFFFFF;
    --text-secondary: #9CA3AF;
    --text-muted: #6B7280;
    --scrollbar-thumb: rgba(255,255,255,0.15);
    --scrollbar-thumb-hover: rgba(255,255,255,0.25);
    /* Accent colors for tinted chips (contact card, safety notice, etc.)
       — on the near-black dark background, a bright/light accent shade
       reads clearly against the ~10% tint; the same shade would wash
       out on the near-white light background, so light mode uses a
       darker, more saturated version of each hue instead. */
    --accent-blue: #60A5FA;
    --accent-green: #34D399;
    --accent-amber: #FBBF24;
    color-scheme: dark;
  }
  [data-theme="light"] {
    --bg-app: #F0F2F5;
    --bg-container: #FFFFFF;
    --bg-container-2: #F0F2F5;
    --bg-header: #FFFFFF;
    --surface-soft: rgba(0,0,0,0.035);
    --surface-soft-hover: rgba(0,0,0,0.06);
    --border-soft: rgba(0,0,0,0.08);
    --border-softer: rgba(0,0,0,0.06);
    --text-primary: #111B21;
    --text-secondary: #667781;
    --text-muted: #8696A0;
    --scrollbar-thumb: rgba(0,0,0,0.15);
    --scrollbar-thumb-hover: rgba(0,0,0,0.25);
    --accent-blue: #1D4ED8;
    --accent-green: #15803D;
    --accent-amber: #92400E;
    color-scheme: light;
  }

  /* --- GLOBAL THEME-AWARE SCROLLBARS ---
     "color-scheme" above already flips native browser UI (scrollbars,
     autofill, etc.) to match the theme in most browsers. These rules
     additionally give every scrollable area in the app (any element with
     overflow: auto/scroll, no matter how deep it's nested) INUA's own
     slim, rounded scrollbar look — dark-on-dark in dark mode, dark-on-
     light in light mode — instead of the OS default. Firefox reads the
     scrollbar-color/-width properties; Chrome/Safari/Edge read the
     ::-webkit-scrollbar-* pseudo-elements. No per-component classes
     needed anymore — this covers every current and future scroll area
     under the themed root automatically. */
  [data-theme="dark"] *,
  [data-theme="light"] * {
    scrollbar-width: thin;
    scrollbar-color: var(--scrollbar-thumb) transparent;
  }
  [data-theme="dark"] ::-webkit-scrollbar,
  [data-theme="light"] ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  [data-theme="dark"] ::-webkit-scrollbar-track,
  [data-theme="light"] ::-webkit-scrollbar-track {
    background: transparent;
  }
  [data-theme="dark"] ::-webkit-scrollbar-thumb,
  [data-theme="light"] ::-webkit-scrollbar-thumb {
    background: var(--scrollbar-thumb);
    border-radius: 9999px;
  }
  [data-theme="dark"] ::-webkit-scrollbar-thumb:hover,
  [data-theme="light"] ::-webkit-scrollbar-thumb:hover {
    background: var(--scrollbar-thumb-hover);
  }
`;

// Animated sun/moon theme toggle (adapted from the "twoTone" toggle in
// Skiper UI's theme-toggle collection, https://toggles.dev/) — morphs a
// ring of sun rays into a crescent moon via a clipped circle + Framer
// Motion, reading/writing the app-wide ThemeContext instead of local
// state so it stays in sync no matter where it's rendered.
export function ThemeToggleSwitch({ className = "" }: { className?: string }) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className={cn_theme(
        "rounded-full transition-all duration-300 active:scale-95 shrink-0",
        isDark ? "bg-black text-white" : "bg-white text-black",
        className
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        fill="currentColor"
        strokeLinecap="round"
        viewBox="0 0 32 32"
      >
        <clipPath id="inua-theme-toggle-clip">
          <motion.path
            animate={{ y: isDark ? 10 : 0, x: isDark ? -12 : 0 }}
            transition={{ ease: "easeInOut", duration: 0.35 }}
            d="M0-5h30a1 1 0 0 0 9 13v24H0Z"
          />
        </clipPath>
        <g clipPath="url(#inua-theme-toggle-clip)">
          <motion.circle
            animate={{ r: isDark ? 10 : 8 }}
            transition={{ ease: "easeInOut", duration: 0.35 }}
            cx="16"
            cy="16"
          />
          <motion.g
            animate={{
              rotate: isDark ? -100 : 0,
              scale: isDark ? 0.5 : 1,
              opacity: isDark ? 0 : 1,
            }}
            transition={{ ease: "easeInOut", duration: 0.35 }}
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M16 5.5v-4" />
            <path d="M16 30.5v-4" />
            <path d="M1.5 16h4" />
            <path d="M26.5 16h4" />
            <path d="m23.4 8.6 2.8-2.8" />
            <path d="m5.7 26.3 2.9-2.9" />
            <path d="m5.8 5.8 2.8 2.8" />
            <path d="m23.4 23.4 2.9 2.9" />
          </motion.g>
        </g>
      </svg>
    </button>
  );
}

// Tiny local className joiner — avoids pulling in "@/lib/utils"' `cn`
// (not part of this project) just for this one component.
function cn_theme(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

// --- APP MEGA MENU ---
// Mobile-adapted rework of a desktop "mega menu navbar" component (multi-
// column flyouts, a separate hover-driven desktop nav vs. a slide-in mobile
// drawer) — this whole app is a single mobile-width card at every
// breakpoint, so there's no "desktop" mode to build: it's always the
// bottom-sheet experience, just themed to match INUA (var(--bg-container) /
// var(--text-primary) instead of the original's hardcoded zinc palette) and
// simplified to a flat blur + sheet rather than a hover mega-flyout.
// Shared by both EntryScreen sides (buyer & seller) since it's mounted once
// in EntryScreen's own persistent header, above the buyer/seller switch.
const APP_MENU_SECTIONS: Array<{
  title: string;
  icon: LucideIcon;
  items: string[];
}> = [
  { title: "Marketplace", icon: ShoppingBag, items: ["Home"] },
  {
    title: "Support",
    icon: LifeBuoy,
    items: ["Help / FAQ", "Contact Us", "Report a Concern"],
  },
  { title: "Company", icon: Building2, items: ["About Us"] },
  { title: "Legal", icon: Scale, items: ["Privacy Policy", "Terms of Use"] },
];

// A per-item icon lookup purely for a little visual polish — none of these
// go anywhere (see the component note below), so this is decorative only.
const APP_MENU_ITEM_ICONS: Record<string, LucideIcon> = {
  Home: Home,
  "Help / FAQ": HelpCircle,
  "Contact Us": Mail,
  "Report a Concern": Flag,
  "About Us": Info,
  "Privacy Policy": ShieldCheck,
  "Terms of Use": FileText,
};

function AppMegaMenu({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  // Which page (Terms, Privacy, FAQ, ...) is open on top of the menu.
  const [openDoc, setOpenDoc] = useState<LegalDocId | null>(null);
  useEffect(() => {
    if (!isOpen) setOpenDoc(null);
  }, [isOpen]);

  // Account section (Log out) — only shown while someone is signed in.
  const [signedIn, setSignedIn] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [authError, setAuthError] = useState("");
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSignedIn(!!session);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSignedIn(!!session);
    });
    return () => subscription.unsubscribe();
  }, []);
  useEffect(() => {
    if (!isOpen) {
      setConfirmAll(false);
      setAuthError("");
    }
  }, [isOpen]);

  // "local" ends the session on this device only; "global" ends it on every
  // device the account is signed in on. The App-level auth listener sees
  // SIGNED_OUT and returns the user to the entry screen.
  const handleSignOut = async (scope: "local" | "global") => {
    setSigningOut(true);
    setAuthError("");
    try {
      const { error } = await supabase.auth.signOut({ scope });
      if (error) throw error;
      try {
        localStorage.removeItem("current_buyer_screen");
      } catch {}
      onClose();
    } catch {
      setAuthError("Could not log out. Check your connection and try again.");
    } finally {
      setSigningOut(false);
      setConfirmAll(false);
    }
  };

  return (
    <>
      {/* BACKDROP — the whole point of "expanding" here is this blur, not
          a big desktop flyout, per the brief. */}
      <div
        onClick={onClose}
        aria-hidden={!isOpen}
        className={cn_theme(
          "fixed inset-0 z-[160] bg-black/40 backdrop-blur-xl transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
      />

      {/* SHEET */}
      <div
        role="dialog"
        aria-modal="true"
        aria-hidden={!isOpen}
        className={cn_theme(
          "fixed inset-x-0 bottom-0 z-[161] w-full max-w-md mx-auto",
          "bg-[var(--bg-container)] text-[var(--text-primary)] border-t border-[var(--border-soft)]",
          "rounded-t-3xl shadow-2xl transition-transform duration-300 ease-out",
          isOpen ? "translate-y-0" : "translate-y-full pointer-events-none"
        )}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-2">
          <h2 className="text-base font-black italic tracking-wide">Menu</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="w-9 h-9 rounded-full flex items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--surface-soft-hover)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Each page item (Help / FAQ, Contact Us, About Us, Privacy Policy,
            Terms of Use) opens its text in a reader sheet on top of the menu;
            "Report a Concern" opens an email to INUA; "Home" just closes the
            menu. */}
        <div className="px-6 pb-8 max-h-[65vh] overflow-y-auto">
          {APP_MENU_SECTIONS.map((section) => {
            const SectionIcon = section.icon;
            return (
              <div key={section.title} className="py-4 first:pt-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <SectionIcon size={14} className="text-[var(--text-muted)]" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--text-muted)]">
                    {section.title}
                  </h3>
                </div>
                <div className="flex flex-col ml-0.5 border-l-2 border-[var(--border-soft)] pl-4">
                  {section.items.map((label) => {
                    const ItemIcon = APP_MENU_ITEM_ICONS[label];
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => {
                          // "Report a Concern" opens the email app directly.
                          const mailto = MENU_LABEL_TO_MAILTO[label];
                          if (mailto) {
                            window.location.href = mailto;
                            onClose();
                            return;
                          }
                          const doc = MENU_LABEL_TO_DOC[label];
                          if (doc) setOpenDoc(doc);
                          else onClose();
                        }}
                        className="flex items-center gap-2.5 text-left py-2.5 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                      >
                        {ItemIcon && (
                          <ItemIcon
                            size={15}
                            className="text-[var(--text-muted)] shrink-0"
                          />
                        )}
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {signedIn && (
            <div className="py-4">
              <div className="flex items-center gap-2 mb-1.5">
                <LogOut size={14} className="text-[var(--text-muted)]" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--text-muted)]">
                  Account
                </h3>
              </div>
              <div className="flex flex-col ml-0.5 border-l-2 border-[var(--border-soft)] pl-4">
                <button
                  type="button"
                  disabled={signingOut}
                  onClick={() => handleSignOut("local")}
                  className="flex items-center gap-2.5 text-left py-2.5 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors disabled:opacity-60"
                >
                  <LogOut size={15} className="text-[var(--text-muted)] shrink-0" />
                  {signingOut ? "Logging out…" : "Log out"}
                </button>
                <button
                  type="button"
                  disabled={signingOut}
                  onClick={() =>
                    confirmAll ? handleSignOut("global") : setConfirmAll(true)
                  }
                  className="flex items-center gap-2.5 text-left py-2.5 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors disabled:opacity-60"
                >
                  <LogOut size={15} className="text-[var(--text-muted)] shrink-0" />
                  {confirmAll
                    ? "Tap again to log out everywhere"
                    : "Log out of all devices"}
                </button>
                {authError && (
                  <p role="alert" className="text-xs text-red-500 py-1">
                    {authError}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <LegalReaderSheet
        docId={openDoc}
        onClose={() => setOpenDoc(null)}
        zIndex={170}
      />
    </>
  );
}

// --- BUYER / SELLER SIGN-IN SCREEN ---
// Lives at module level (not inside App) on purpose: a component defined
// inside App is re-created on every App render, which would wipe the
// email / code the person is typing whenever something else (e.g. a live
// listing update) re-rendered App. Everyone — buyers and sellers — signs in
// here. Two ways in: Google, or a one-time code emailed to them.
function BuyerAuth({
  navigateTo,
  showAppToast,
  runTileTransition,
}: {
  navigateTo: (screen: string) => void;
  showAppToast: (toast: { title: string; message: string }) => void;
  runTileTransition: (action: () => void, color: string) => void;
}) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  // Always call the latest versions, even from inside async callbacks.
  const navRef = useRef(navigateTo);
  const tileRef = useRef(runTileTransition);
  navRef.current = navigateTo;
  tileRef.current = runTileTransition;

  // Resend countdown (Supabase allows one code per address per minute).
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin,
          // Always show Google's account chooser, so a shared phone or
          // laptop never silently signs in as whoever used it last.
          queryParams: { prompt: "select_account" },
        },
      });
      if (error) throw error;
    } catch (err: any) {
      showAppToast({
        title: "Sign-in failed",
        message: `Google Sign-In failed: ${err.message}`,
      });
    }
  };

  const sendCode = async () => {
    const addr = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(addr)) {
      setError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: addr,
        options: { shouldCreateUser: true },
      });
      if (otpError) throw otpError;
      setEmail(addr);
      setCode("");
      setStep("code");
      setCooldown(60);
    } catch (err: any) {
      const limited =
        err?.status === 429 || /rate|too many|seconds/i.test(err?.message || "");
      setError(
        limited
          ? "Too many attempts. Wait a minute, then try again."
          : "We could not send the code. Check the address and try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    const token = code.replace(/\s/g, "");
    if (token.length < 6) {
      setError("Enter the code from your email.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token,
        type: "email",
      });
      if (verifyError) throw verifyError;
      // Signed in — same landing as every other successful sign-in.
      tileRef.current(() => navRef.current("buyer-home"), "#1006C5");
    } catch {
      setError("That code is wrong or has expired. Check it or request a new one.");
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border border-[var(--border-soft)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] rounded-xl px-4 py-3 text-base outline-none focus:border-[#ffb800] transition";
  const mainBtnClass =
    "w-full bg-white text-black border border-[var(--border-soft)] font-bold py-3 px-4 rounded-xl text-sm shadow-lg flex items-center justify-center gap-2 hover:bg-white/90 transition disabled:opacity-60";

  return (
    <div className="min-h-screen w-full bg-[var(--bg-app)] flex items-center justify-center p-6 text-[var(--text-primary)]">
      <div className="bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl">
        <h1 className="text-2xl font-black text-[var(--text-primary)] tracking-wider mb-2">
          JOIN{" "}
          <span className="bg-gradient-to-r from-[#1006C5] to-[#ffb800] bg-clip-text text-transparent">
            INUA
          </span>
        </h1>
        <p className="text-xs text-[var(--text-muted)] mb-8 font-medium">
          Create a buyer account to view live listings
        </p>

        {step === "email" ? (
          <div className="flex flex-col gap-3 mb-5 text-left">
            <label className="text-xs font-semibold text-[var(--text-secondary)]">
              Email address
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !busy) sendCode();
                }}
                placeholder="you@example.com"
                className={inputClass + " mt-1.5"}
              />
            </label>
            <button
              type="button"
              onClick={sendCode}
              disabled={busy || !email.trim()}
              className={mainBtnClass}
            >
              {busy ? (
                "Sending…"
              ) : (
                <>
                  <Mail size={16} />
                  Email me a code
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 mb-5 text-left">
            <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
              We sent a code to{" "}
              <span className="font-semibold text-[var(--text-primary)] break-all">
                {email}
              </span>
              . It can take a minute — check your spam folder too.
            </p>
            <label className="text-xs font-semibold text-[var(--text-secondary)]">
              Code
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={10}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !busy) verifyCode();
                }}
                placeholder="Enter the code"
                className={inputClass + " mt-1.5 tracking-[0.3em] text-center"}
              />
            </label>
            <button
              type="button"
              onClick={verifyCode}
              disabled={busy || code.length < 6}
              className={mainBtnClass}
            >
              {busy ? "Checking…" : "Verify and continue"}
            </button>
            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={sendCode}
                disabled={busy || cooldown > 0}
                className="text-[var(--text-secondary)] underline decoration-[var(--border-soft)] disabled:no-underline disabled:opacity-60"
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setError("");
                }}
                className="text-[var(--text-secondary)] underline decoration-[var(--border-soft)]"
              >
                Use a different email
              </button>
            </div>
          </div>
        )}

        {error && (
          <p role="alert" className="text-xs text-red-500 -mt-2 mb-5">
            {error}
          </p>
        )}

        <div className="flex items-center gap-3 mb-5" aria-hidden="true">
          <div className="h-px flex-1 bg-[var(--border-soft)]" />
          <span className="text-xs text-[var(--text-muted)]">or</span>
          <div className="h-px flex-1 bg-[var(--border-soft)]" />
        </div>

        <button
          onClick={handleGoogleSignIn}
          className="w-full bg-white text-black font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-3 hover:bg-white/90 transition shadow-lg mb-6 text-sm"
        >
            <svg
            className="w-5 h-5"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            >
            <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
            />
            <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
            />
            <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
            fill="#FBBC05"
            />
            <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
            />
            </svg>
          Continue with Google
        </button>

        <button
          onClick={() =>
            runTileTransition(() => navigateTo("entry"), "#1006C5")
          }
          className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition underline decoration-[var(--border-soft)]"
        >
          Cancel and go back
        </button>
      </div>
    </div>
  );
}

// --- COACHMARKS (guided first-visit walkthroughs) ---
// A tiny, dependency-free "coach mark" system: a spotlight cutout around a
// target element plus a themed tooltip pointing at it, shown step-by-step
// the very first time a buyer lands on a given screen. Each screen calls
// useCoachTour(key, steps, enabled) with refs on the elements worth
// explaining; "key" is a localStorage namespace so a given tour, once
// shown, never repeats on that device, and "enabled" (see useIsNewBuyer
// below) gates the whole thing to brand-new accounts only.
type CoachStep = {
  ref: React.RefObject<HTMLElement>;
  title: string;
  text: string;
};

// True only for an account Supabase has JUST created — not "new to this
// device", but genuinely new in the database. Supabase's auth user object
// carries both `created_at` (row insert time, fixed forever) and
// `last_sign_in_at` (updated on every login). On a brand-new account's
// very first session those two timestamps land within moments of each
// other; on any later day's login, `last_sign_in_at` has moved well past
// `created_at`. That gap — not a localStorage flag — is what "new buyer"
// means here, so the walkthrough follows the ACCOUNT rather than the
// device: it plays once on a fresh signup and never again after that,
// even on a different phone, and never at all for an existing buyer who
// just logs into a new device.
function useIsNewBuyer() {
  const [isNew, setIsNew] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Manual override for testing: append ?debugNewBuyer=1 to the
        // URL to force every coachmark to show, without needing to
        // create (and delete, from Authentication -> Users, not just
        // your own tables) a fresh Supabase signup each time.
        if (
          typeof window !== "undefined" &&
          window.location.search.includes("debugNewBuyer=1")
        ) {
          console.log(
            "[Coachmarks] debugNewBuyer=1 override active — forcing isNewBuyer = true"
          );
          if (!cancelled) setIsNew(true);
          return;
        }
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        const createdAt = new Date(user.created_at).getTime();
        const lastSignInAt = user.last_sign_in_at
          ? new Date(user.last_sign_in_at).getTime()
          : createdAt;
        // Generous 5-minute tolerance — covers the signup flow itself
        // (email confirmation, etc.) without misclassifying a returning
        // buyer as new.
        const isNewAccount = Math.abs(lastSignInAt - createdAt) < 5 * 60 * 1000;
        console.log(
          "[Coachmarks] created_at:",
          user.created_at,
          "| last_sign_in_at:",
          user.last_sign_in_at,
          "| isNewBuyer:",
          isNewAccount
        );
        if (!cancelled) setIsNew(isNewAccount);
      } catch (err) {
        // If the check fails for any reason, default to NOT showing the
        // tour — safer than accidentally showing it to every buyer.
        console.error("[Coachmarks] useIsNewBuyer check failed:", err);
        if (!cancelled) setIsNew(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return isNew;
}

function useCoachTour(key: string, steps: CoachStep[], enabled = true) {
  const [stepIndex, setStepIndex] = React.useState(-1);
  const storageKey = `inua_coach_seen_${key}`;

  React.useEffect(() => {
    if (!enabled || steps.length === 0) return;
    let seen = false;
    try {
      seen = !!localStorage.getItem(storageKey);
    } catch {
      // Storage unavailable (private browsing, etc.) — just skip the tour
      // rather than showing it every single time.
      seen = true;
    }
    if (seen) return;
    // Give the screen's own entrance animation/layout a beat to settle
    // before measuring target rects, or the spotlight would be misplaced.
    const t = setTimeout(() => setStepIndex(0), 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, storageKey, steps.length]);

  const finish = () => {
    try {
      localStorage.setItem(storageKey, "1");
    } catch {}
    setStepIndex(-1);
  };

  const next = () => {
    setStepIndex((i) => (i + 1 >= steps.length ? -1 : i + 1));
    if (stepIndex + 1 >= steps.length) finish();
  };

  return {
    activeStep: stepIndex >= 0 ? steps[stepIndex] : null,
    stepIndex,
    totalSteps: steps.length,
    next,
    dismiss: finish,
  };
}

function Coachmark({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onDismiss,
}: {
  step: CoachStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onDismiss: () => void;
}) {
  const [rect, setRect] = React.useState<DOMRect | null>(null);
  // Coachmark renders via createPortal straight into document.body — OUTSIDE
  // the app's themed root div that [data-theme="dark"/"light"] and its CSS
  // variables (--bg-container-2, --text-primary, etc.) are scoped to. Those
  // vars simply don't exist out at document.body, so var(--...) silently
  // resolves to nothing there — a transparent background and unset text
  // color, exactly the "instructions aren't visible" bug. Reading the
  // theme directly and hardcoding real colors sidesteps that scoping
  // problem entirely, guaranteeing white text in dark mode / black text
  // in light mode regardless of where in the DOM this ends up mounted.
  const { theme } = useTheme();
  const isDark = theme === "dark";

  React.useLayoutEffect(() => {
    const measure = () => {
      if (step.ref.current) setRect(step.ref.current.getBoundingClientRect());
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [step]);

  if (!rect) return null;

  const pad = 6;
  const viewportH = window.innerHeight;
  const viewportW = window.innerWidth;
  const tooltipWidth = 268;
  const spaceBelow = viewportH - rect.bottom;
  const placeBelow = spaceBelow > 170 || rect.top < 170;
  const left = Math.min(
    Math.max(rect.left + rect.width / 2 - tooltipWidth / 2, 12),
    viewportW - tooltipWidth - 12
  );

  const tooltipBg = isDark ? "#1E1E24" : "#FFFFFF";
  const tooltipBorder = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)";
  const titleColor = isDark ? "#FFFFFF" : "#111B21";
  const bodyColor = isDark ? "#D1D5DB" : "#3F4A50";
  const mutedColor = isDark ? "#9CA3AF" : "#667781";

  return createPortal(
    <div className="fixed inset-0 z-[9998]">
      {/* Dimmed backdrop with a cut-out "spotlight" around the target,
          via a giant box-shadow rather than an SVG mask — much simpler
          to keep in sync with a moving/scrolling rect. */}
      <div
        onClick={onDismiss}
        style={{
          position: "fixed",
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
          borderRadius: 14,
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.75)",
          border: "2px solid #fff",
          pointerEvents: "auto",
          transition: "all 0.25s ease",
        }}
      />
      <div
        style={{
          position: "fixed",
          top: placeBelow ? rect.bottom + pad + 12 : undefined,
          bottom: !placeBelow ? viewportH - rect.top + pad + 12 : undefined,
          left,
          width: tooltipWidth,
          backgroundColor: tooltipBg,
          border: `1px solid ${tooltipBorder}`,
          boxShadow: "0 20px 40px rgba(0,0,0,0.45)",
        }}
        className="rounded-2xl p-4"
      >
        <p className="font-bold text-sm mb-1" style={{ color: titleColor }}>
          {step.title}
        </p>
        <p
          className="text-xs leading-relaxed mb-3"
          style={{ color: bodyColor }}
        >
          {step.text}
        </p>
        <div className="flex items-center justify-between">
          <button
            onClick={onDismiss}
            style={{ color: mutedColor }}
            className="text-[10px] font-semibold uppercase tracking-wider hover:opacity-80 transition-opacity"
          >
            Skip
          </button>
          <div className="flex items-center gap-2">
            <span className="text-[10px]" style={{ color: mutedColor }}>
              {stepIndex + 1}/{totalSteps}
            </span>
            <button
              onClick={onNext}
              style={{ backgroundColor: "#1006C5" }}
              className="px-4 py-1.5 rounded-lg text-white text-xs font-bold hover:brightness-110 active:brightness-95 transition"
            >
              {stepIndex === totalSteps - 1 ? "Done" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// --- WISHLIST LOCAL CACHE ---
// The "Save" heart on a product's details page used to always start from
// a blank slate (isSaved = false) and only learn the real answer after an
// auth check + a DB round trip — visible as a "flash": Save, then ~a
// couple seconds later, Saved. It also meant that state was checked fresh
// on literally every visit, when it almost never changes outside of the
// user's own taps on this same heart button.
//
// This tiny localStorage-backed set is the fix: whenever a listing is
// saved/unsaved (from either the product page's heart button or the
// Account screen's wishlist "x"), the listing id is written here
// immediately. ProductDetailsView then seeds its initial isSaved state by
// reading this cache synchronously on first render — no flash, no "read
// it after a delay" — and only falls back to asking the server for
// listings this cache has never heard of (e.g. saved from another
// device/session). A stale/incorrect entry can only ever result from
// saving on a second device, which self-heals the moment that listing's
// details are opened again anywhere.
const WISHLIST_CACHE_KEY = "inua_saved_listing_ids";

function readSavedListingIdsCache(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(WISHLIST_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function isListingSavedLocally(listingId?: string | null): boolean {
  if (!listingId) return false;
  return readSavedListingIdsCache().has(listingId);
}

function setListingSavedLocally(listingId?: string | null, saved?: boolean) {
  if (!listingId || typeof window === "undefined") return;
  const ids = readSavedListingIdsCache();
  if (saved) {
    ids.add(listingId);
  } else {
    ids.delete(listingId);
  }
  try {
    window.localStorage.setItem(
      WISHLIST_CACHE_KEY,
      JSON.stringify(Array.from(ids))
    );
  } catch {
    // Storage can fail (private browsing, quota, etc.) — the DB stays the
    // source of truth either way, this cache is purely a UI speed-up.
  }
}

// --- EARLY ENTRY-SCREEN ASSET PRELOADING ---
// The credit card / coins / sale tags / delivery box used to take a
// noticeable moment to appear after the entry screen was already visible.
// That's because both the <model-viewer> script tag AND all four .glb
// downloads were only kicked off inside EntryScreen's useEffects — i.e.
// only after React had mounted the component and committed its first
// render. Doing it here instead, at module-evaluation time (this function
// call runs once, the moment this file's JS is parsed/executed — before
// EntryScreen has rendered at all), lets the browser start all of these
// downloads in parallel with React's very first paint rather than after
// it, closing most of that gap.
function preloadEntryScreenAssets() {
  if (typeof document === "undefined") return;

  // Warms up the connection (DNS + TLS handshake) to the Supabase storage
  // host so the .glb requests below — and <model-viewer>'s own later
  // fetches of the same URLs — don't each pay that cost individually.
  if (!document.getElementById("inua-preconnect-storage")) {
    const preconnect = document.createElement("link");
    preconnect.id = "inua-preconnect-storage";
    preconnect.rel = "preconnect";
    preconnect.href = "https://jnoeucdmnuffzctyhomt.supabase.co";
    preconnect.crossOrigin = "anonymous";
    document.head.appendChild(preconnect);
  }

  // Preloads the raw model bytes themselves. `as="fetch"` + `crossOrigin`
  // matches how <model-viewer> actually requests these files, so when it
  // later sets `src=...`, the browser serves that request straight out of
  // its HTTP cache instead of starting the download from scratch.
  [
    CREDIT_CARD_GLB_URL,
    DELIVERY_BOX_GLB_URL,
    GOLD_COIN_GLB_URL,
    SALE_TAG_GLB_URL,
  ].forEach((url, i) => {
    const id = `inua-preload-glb-${i}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "preload";
    link.as = "fetch";
    link.href = url;
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
  });

  // Registers Google's <model-viewer> custom element as early as possible.
  // (Previously this same injection lived inside a useEffect in
  // EntryScreen, which only fires after mount — see the comment above.)
  if (!document.getElementById("model-viewer-script")) {
    const script = document.createElement("script");
    script.id = "model-viewer-script";
    script.type = "module";
    script.src =
      "https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js";
    document.head.appendChild(script);
  }
}
preloadEntryScreenAssets();

// --- AVATAR PRESETS & UTILITIES ---
export const AVATAR_PRESETS = [
  { id: "avatar_1", label: "Initial", color: "bg-orange-500" },
  { id: "avatar_3", label: "Initial", color: "bg-emerald-600" },
  { id: "avatar_4", label: "Initial", color: "bg-purple-600" },
  { id: "avatar_5", label: "Initial", color: "bg-red-600" },
  { id: "avatar_6", label: "Initial", color: "bg-blue-600" },
];

export const getAvatarColor = (avatarId?: string) => {
  const preset = AVATAR_PRESETS.find((a) => a.id === avatarId);
  return preset ? preset.color : "bg-orange-500";
};

// Darkens/lightens a hex colour by `percent` (-100 to 100). Kept around
// as a small utility in case a future theme needs a shade variant; the
// StaggeredMenu sweep itself now uses the category colour directly (see
// getCategoryMenuColors below).
const shadeHexColor = (hex: string, percent: number) => {
  const clean = hex.replace("#", "");
  const num = parseInt(
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean,
    16
  );
  const amt = Math.round(2.55 * percent);
  const r = Math.min(255, Math.max(0, (num >> 16) + amt));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amt));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amt));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

// StaggeredMenu's opening sweep: the category's own colour first, then
// the white panel arrives on top of it and stays — so this only ever
// needs to return a single-colour array per category, no second tone.
export const getCategoryMenuColors = (baseHex?: string) => {
  const base = baseHex || DEFAULT_CATEGORY_THEME.color;
  return [base];
};

// --- SELLER RATING TIERS ---
// Sellers start at a default rolling average of 5/5 until they have reviews.
// Tier is derived from the rolling average of a seller's last 10 review
// submissions (each submission's "overall" score = avg of response + quality).
export const SELLER_RATING_TIERS = {
  elite: {
    label: "Top Rated",
    dotClass: "bg-emerald-500",
    textClass: "text-emerald-400",
  },
  established: {
    label: "Established",
    dotClass: "bg-amber-500",
    textClass: "text-amber-400",
  },
  fairlyReliable: {
    label: "Fairly Reliable",
    dotClass: "bg-rose-500",
    textClass: "text-rose-400",
  },
} as const;

export function getSellerRatingTier(rollingAverage: number) {
  if (rollingAverage >= 4) return SELLER_RATING_TIERS.elite;
  if (rollingAverage >= 2) return SELLER_RATING_TIERS.established;
  return SELLER_RATING_TIERS.fairlyReliable;
}

// A single star rating of 1 or 5 (on either axis) requires the buyer to
// justify it with a verification email + a 15-180 character description.
export function reviewRequiresJustification(
  responseScore: number,
  qualityScore: number
) {
  return (
    responseScore === 1 ||
    responseScore === 5 ||
    qualityScore === 1 ||
    qualityScore === 5
  );
}

// Helper to safely extract seller application data (whether returned as object or array)
const getSellerApp = (listing: any) => {
  if (!listing?.seller_applications) return null;
  return Array.isArray(listing.seller_applications)
    ? listing.seller_applications[0]
    : listing.seller_applications;
};

// Looks a search term up directly against Supabase's `seller_applications`
// table — matching either the `location` column (young professionals) or
// the `university` column (current students / recent graduates). This is
// deliberately a real network round-trip rather than a filter over
// whatever's already sitting in local state, so a location or institution
// search reliably surfaces every matching seller, not just whichever ones
// happen to already be loaded into the buyer feed. Returns the matching
// sellers' ids so callers can cross-reference them against listings.
async function searchSellersByLocationOrInstitution(
  query: string
): Promise<Set<string>> {
  const trimmed = query.trim();
  if (trimmed.length < 2) return new Set();

  const escaped = trimmed.replace(/[%,]/g, ""); // keep the ilike pattern well-formed
  let { data, error } = await supabase
    .from("seller_applications")
    .select("id")
    .or(
      `location.ilike.%${escaped}%,university.ilike.%${escaped}%,county.ilike.%${escaped}%`
    );
  if (error) {
    // `county` column not there yet — fall back to the original lookup.
    ({ data, error } = await supabase
      .from("seller_applications")
      .select("id")
      .or(`location.ilike.%${escaped}%,university.ilike.%${escaped}%`));
  }

  if (error) {
    console.warn("[Search] location/institution lookup failed:", error.message);
    return new Set();
  }
  return new Set((data || []).map((row: any) => row.id));
}

// Shared "I'm looking for..." search bar used by both the main marketplace
// feed and every category screen. Purely prop-driven (no closures over App
// state) so the same component renders correctly in either the dark
// (marketplace) or light (category) theme.
//
// The chips it filters by are passed in via `filterItems` rather than
// assumed to always be the top-level CATEGORIES list — the main
// marketplace feed passes top-level categories, a category screen sitting
// on "All [Category]" passes that category's subcategories, and a category
// screen that's already drilled into one specific subcategory passes
// `filterItems={null}` — there's nothing left to filter by at that point,
// so the bar drops the filter button/panel entirely and is just a plain
// text search.
function MarketplaceSearchBar({
  query,
  onQueryChange,
  placeholder = "Search location, Institution, category...",
  filterLabel = "I'm looking for...",
  filterItems,
  activeFilterIds,
  onToggleFilter,
  onResetFilters,
  onSearch,
  theme,
  greyInLight = false,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  placeholder?: string;
  filterLabel?: string;
  // null/undefined or an empty array = no filter UI at all (plain search).
  filterItems?: { id: string; name: string }[] | null;
  activeFilterIds?: Set<string>;
  onToggleFilter?: (id: string) => void;
  onResetFilters?: () => void;
  // Fires an immediate (non-debounced) database lookup — wired to the
  // magnifying-glass button so tapping it visibly "runs" the search
  // rather than just waiting on the existing live-filter debounce.
  onSearch?: () => void;
  theme: "dark" | "light";
  // Keeps the bar itself grey in light mode (for screens whose header is plain white).
  greyInLight?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const panelInputRef = useRef<HTMLInputElement>(null);
  const isDark = theme === "dark";

  const hasFilters = !!filterItems && filterItems.length > 0;
  const allCats = filterItems || [];
  const activeCats = hasFilters
    ? allCats.filter((c) => activeFilterIds?.has(c.id))
    : [];

  const close = () => setIsOpen(false);
  const runSearch = () => {
    onSearch?.();
    close();
  };

  // While the elevated search overlay is open: autofocus its input,
  // let Escape/Enter close or submit it, and lock the home page behind
  // it from scrolling — all undone the moment it closes.
  useEffect(() => {
    if (!isOpen) return;
    const focusTimer = setTimeout(() => panelInputRef.current?.focus(), 60);
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "Enter") runSearch();
    };
    document.addEventListener("keydown", handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      <div
        className={`rounded-2xl border overflow-hidden transition-colors ${
          isDark
            ? "border-white/25 bg-white/[0.04] backdrop-blur-xl"
            : greyInLight
            ? "border-black/[0.08] bg-neutral-100"
            : "border-black/[0.07] bg-white shadow-[0_4px_20px_-8px_rgba(0,0,0,0.15)]"
        }`}
      >
        <div className="flex items-center gap-1 px-3.5 py-2.5">
          <button
            type="button"
            aria-label="Search"
            onClick={() => (query.trim() ? runSearch() : setIsOpen(true))}
            className={`shrink-0 -m-1 p-1 rounded-md transition-colors ${
              isDark
                ? "text-white hover:text-white active:text-white/80"
                : "text-neutral-700 hover:text-neutral-800 active:text-neutral-900"
            }`}
          >
            <Search size={16} />
          </button>
          <SmoothInput
            type="text"
            placeholder={placeholder}
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onQueryChange(e.target.value)
            }
            onFocus={() => setIsOpen(true)}
            className={`min-w-0 flex-1 bg-transparent px-2.5 text-sm outline-none ${
              isDark
                ? "text-white placeholder:text-white/30"
                : "text-neutral-900 placeholder:text-neutral-400"
            }`}
          />
          {hasFilters && (
            <button
              type="button"
              aria-label="Filter categories"
              onClick={() => setIsOpen(true)}
              className={`ml-auto -mr-1 p-1 transition-colors shrink-0 ${
                isDark
                  ? "text-white hover:text-white/70"
                  : "text-neutral-700 hover:text-neutral-900"
              }`}
            >
              <SlidersHorizontal size={16} />
            </button>
          )}
        </div>
      </div>

      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <motion.div
                key="search-overlay-backdrop"
                className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-20 sm:pt-28"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                style={{
                  background: isDark
                    ? "rgba(6,6,9,0.72)"
                    : "rgba(20,20,24,0.45)",
                  backdropFilter: "blur(14px)",
                  WebkitBackdropFilter: "blur(14px)",
                }}
                onClick={close}
              >
                <motion.div
                  key="search-overlay-panel"
                  onClick={(e) => e.stopPropagation()}
                  initial={{ opacity: 0, y: -14, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.97 }}
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  className={`w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden ${
                    isDark
                      ? "border-white/10 bg-[#0B0B12] shadow-black/60"
                      : "border-black/[0.07] bg-white shadow-black/20"
                  }`}
                >
                  <div className="flex items-center gap-1 px-3.5 py-3">
                    <button
                      type="button"
                      aria-label="Search"
                      onClick={runSearch}
                      className={`shrink-0 -m-1 p-1 rounded-md transition-colors ${
                        isDark
                          ? "text-white/50 hover:text-white active:text-white/80"
                          : "text-neutral-400 hover:text-neutral-800 active:text-neutral-900"
                      }`}
                    >
                      <Search size={17} />
                    </button>
                    <input
                      ref={panelInputRef}
                      type="text"
                      placeholder={placeholder}
                      value={query}
                      onChange={(e) => onQueryChange(e.target.value)}
                      className={`min-w-0 flex-1 bg-transparent px-2.5 text-sm outline-none ${
                        isDark
                          ? "text-white placeholder:text-white/30"
                          : "text-neutral-900 placeholder:text-neutral-400"
                      }`}
                    />
                    <button
                      type="button"
                      aria-label="Close search"
                      onClick={close}
                      className={`shrink-0 -m-1 p-1 rounded-md transition-colors ${
                        isDark
                          ? "text-white/40 hover:text-white/80"
                          : "text-neutral-400 hover:text-neutral-700"
                      }`}
                    >
                      <X size={16} />
                    </button>
                  </div>

                  {hasFilters ? (
                    <div
                      className={`px-3.5 pb-3.5 pt-2.5 border-t max-h-[55vh] overflow-y-auto ${
                        isDark ? "border-white/[0.06]" : "border-black/[0.06]"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-[13px] ${
                            isDark ? "text-white/40" : "text-neutral-400"
                          }`}
                        >
                          {filterLabel}
                        </span>
                        {activeCats.length < allCats.length && (
                          <button
                            type="button"
                            onClick={onResetFilters}
                            className="text-[12px] font-semibold"
                            style={{ color: isDark ? "#93A5FF" : "#1006C5" }}
                          >
                            Reset
                          </button>
                        )}
                      </div>
                      <div className="mt-2.5 flex flex-wrap gap-2">
                        {activeCats.length === 0 ? (
                          <span
                            className={`text-[13px] ${
                              isDark ? "text-white/30" : "text-neutral-400"
                            }`}
                          >
                            All categories removed — tap Reset to search
                            everything again.
                          </span>
                        ) : (
                          activeCats.map((cat) => (
                            <span
                              key={cat.id}
                              className={`flex items-center gap-1.5 rounded-full py-1 pl-2.5 pr-2 text-[13px] ring-1 ring-inset ${
                                isDark
                                  ? "bg-white/[0.06] ring-white/[0.06] text-white/90"
                                  : "bg-black/[0.04] ring-black/[0.04] text-neutral-800"
                              }`}
                            >
                              <span>{cat.name}</span>
                              <button
                                type="button"
                                onClick={() => onToggleFilter?.(cat.id)}
                                aria-label={`Remove ${cat.name}`}
                                className={
                                  isDark
                                    ? "text-white/40 hover:text-white/80 transition-colors"
                                    : "text-neutral-400 hover:text-neutral-700 transition-colors"
                                }
                              >
                                <X size={12} />
                              </button>
                            </span>
                          ))
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={runSearch}
                        className={`mt-3.5 w-full flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold transition-colors ${
                          isDark
                            ? "bg-white text-[#060609] hover:bg-white/90"
                            : "bg-[#1006C5] text-white hover:bg-[#1006C5]/90"
                        }`}
                      >
                        <Search size={15} />
                        Search
                      </button>
                    </div>
                  ) : (
                    <div
                      className={`px-3.5 pb-3.5 pt-2.5 border-t ${
                        isDark ? "border-white/[0.06]" : "border-black/[0.06]"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={runSearch}
                        className={`w-full flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold transition-colors ${
                          isDark
                            ? "bg-white text-[#060609] hover:bg-white/90"
                            : "bg-[#1006C5] text-white hover:bg-[#1006C5]/90"
                        }`}
                      >
                        <Search size={15} />
                        Search
                      </button>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}

// Masks a phone number for display so buyers get a hint of who'll be
// reaching out without the full number being readable/copyable off the
// page — keeps the +country code and the last 3 digits, stars out
// everything in between. e.g. "+254712345456" -> "+254*****456"
//
// Numbers are stored with their country code per isValidInternationalPhone
// (e.g. +254712345456). We assume a 3-digit calling code since this app
// currently only serves Kenya (+254) — extend COUNTRY_CODE_LENGTH below if
// that changes.
const COUNTRY_CODE_LENGTH = 3;

const maskPhoneNumber = (raw?: string) => {
  const trimmed = (raw || "").trim();
  if (!trimmed) return "";

  if (trimmed.startsWith("+")) {
    const digits = trimmed.slice(1).replace(/\D/g, "");
    const countryCode = digits.slice(0, COUNTRY_CODE_LENGTH);
    const rest = digits.slice(COUNTRY_CODE_LENGTH);
    if (rest.length <= 3) return `+${countryCode}${rest}`;
    const end = rest.slice(-3);
    const middle = "*".repeat(rest.length - 3);
    return `+${countryCode}${middle}${end}`;
  }

  // No country code on this number (shouldn't normally happen, but don't
  // leak more of it than intended) — mask everything but the last 3 digits.
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 6) return trimmed;
  const end = digits.slice(-3);
  return `${"*".repeat(digits.length - 3)}${end}`;
};


// Clear screen state on reload so users can choose their path on fresh sessions
if (typeof window !== "undefined") {
  const hasBooted = sessionStorage.getItem("tab_session_active");
  if (hasBooted) {
    localStorage.removeItem("current_buyer_screen");
  } else {
    sessionStorage.setItem("tab_session_active", "true");
  }
}

// --- BUYER PROFILE VIEW ---
// Defined at module scope (not inside App) so it stays mounted across
// re-renders — no accidental unmount/remount, no refetching, no loading flash.
// Bookings screen: service bookings, with a live status badge and a "Check"
// button that reveals the seller's accept/decline message per booking.
function BuyerOrdersView({ onBack }: any) {
  const [buyerId, setBuyerId] = React.useState<string | null>(null);
  const [myBookings, setMyBookings] = React.useState<BookingRow[]>([]);
  const [isLoadingBookings, setIsLoadingBookings] = React.useState(true);
  // The buyer's last 8 completed bookings. Completed ones are hidden from the
  // active list, so this is where they live on afterwards.
  const [bookingHistory, setBookingHistory] = React.useState<any[]>([]);
  // Which booking cards currently have their "Check" message expanded.
  const [expandedBookingIds, setExpandedBookingIds] = React.useState<
    Set<string>
  >(new Set());
  const ordersCardRef = useRef<HTMLDivElement>(null);
  const isNewBuyerOrders = useIsNewBuyer();
  const ordersTour = useCoachTour(
    "orders",
    [
      {
        ref: ordersCardRef,
        title: "Your Bookings",
        text: "Active bookings show here, and Booking History below keeps your last 8 completed ones. The seller keeps a matching record, so a booking can always be traced if anything goes wrong.",
      },
    ],
    isNewBuyerOrders
  );

  const fetchMyBookings = React.useCallback(async (userId: string) => {
    setIsLoadingBookings(true);
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          "*, listings:listing_id(title, price, seller_applications:seller_id(display_name, phone_number, whatsapp_number))"
        )
        .eq("buyer_id", userId)
        // Completed bookings are cleared from the buyer's view entirely —
        // the underlying row stays intact for the seller's own records.
        .neq("status", "completed")
        .order("booking_date", { ascending: true });

      if (!error) setMyBookings(data || []);

      // History: the last 8 completed bookings, newest first.
      const { data: historyData, error: historyError } = await supabase
        .from("bookings")
        .select(
          "id, booking_date, booking_time, listings:listing_id(title, price, seller_applications:seller_id(display_name))"
        )
        .eq("buyer_id", userId)
        .eq("status", "completed")
        .order("booking_date", { ascending: false })
        .order("booking_time", { ascending: false })
        .limit(8);
      if (!historyError) setBookingHistory(historyData || []);
    } catch (err) {
      console.error("Error loading bookings:", err);
    } finally {
      setIsLoadingBookings(false);
    }
  }, []);

  React.useEffect(() => {
    async function loadBuyer() {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          setBuyerId(user.id);
          fetchMyBookings(user.id);
        }
      } catch (err) {
        console.error("Error loading bookings:", err);
      }
    }
    loadBuyer();
  }, [fetchMyBookings]);

  // Live updates: when a seller Confirms, Declines, or Marks Completed,
  // this view reflects it immediately without the buyer needing to refresh.
  React.useEffect(() => {
    if (!buyerId) return;

    const channel = supabase
      .channel(`buyer-bookings-${buyerId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "bookings",
          filter: `buyer_id=eq.${buyerId}`,
        },
        () => {
          fetchMyBookings(buyerId);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [buyerId, fetchMyBookings]);

  const BOOKING_STATUS_LABEL: Record<string, string> = {
    pending: "Requested",
    confirmed: "Accepted",
    declined: "Declined",
  };

  const BOOKING_STATUS_CLASS: Record<string, string> = {
    pending: "bg-amber-500/20 text-amber-400 border border-amber-500/30",
    confirmed: "bg-green-500/20 text-green-400 border border-green-500/30",
    declined: "bg-stone-600/30 text-stone-400 border border-stone-500/30",
  };

  return (
    <div className="min-h-screen w-full max-w-md mx-auto bg-[var(--bg-app)] text-[var(--text-primary)] p-4 pb-24">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="p-1 hover:bg-[var(--surface-soft-hover)] rounded-full transition"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-bold">Bookings</h1>
      </div>

      <div className="max-w-md mx-auto space-y-6">
        <div
          ref={ordersCardRef}
          className="bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-2xl p-5 shadow-xl"
        >
          <h3 className="text-sm font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-4">
            Active Bookings
          </h3>

          {isLoadingBookings ? (
            <div className="text-center py-6">
              <Loader message="Loading your bookings" scale={0.4} />
            </div>
          ) : myBookings.length === 0 ? (
            <p className="text-center text-[var(--text-muted)] text-sm py-6">
              No active service bookings yet.
            </p>
          ) : (
            <div className="space-y-3">
              {myBookings.map((booking: any) => {
                const isExpanded = expandedBookingIds.has(booking.id);
                const sellerApp = booking.listings?.seller_applications;
                const sellerName = sellerApp?.display_name || "The seller";
                const maskedContact =
                  sellerApp?.whatsapp_number || sellerApp?.phone_number
                    ? maskPhoneNumber(
                        sellerApp.whatsapp_number || sellerApp.phone_number
                      )
                    : "";

                const toggleExpanded = () => {
                  setExpandedBookingIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(booking.id)) next.delete(booking.id);
                    else next.add(booking.id);
                    return next;
                  });
                };

                return (
                  <div
                    key={booking.id}
                    className="bg-[var(--bg-container-2)] border border-[var(--border-soft)] rounded-xl p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[var(--text-primary)] font-semibold text-sm">
                          {booking.listings?.title || "Service"}
                        </p>
                        <p className="text-[var(--text-muted)] text-xs mt-0.5">
                          {sellerName}
                        </p>
                        <p className="text-[var(--text-muted)] text-xs mt-0.5">
                          {booking.booking_date} · {booking.booking_time}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                          BOOKING_STATUS_CLASS[booking.status] ||
                          "bg-stone-700/40 text-stone-400 border border-stone-600/30"
                        }`}
                      >
                        {BOOKING_STATUS_LABEL[booking.status] || booking.status}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={toggleExpanded}
                      className="mt-3 text-[11px] font-bold uppercase tracking-wider text-[#ffb800] hover:text-[#e0a200] transition"
                    >
                      {isExpanded ? "Hide" : "Check"}
                    </button>

                    {isExpanded && (
                      <p className="mt-2 text-[var(--text-secondary)] text-xs leading-relaxed">
                        {booking.status === "confirmed" &&
                          `${sellerName} accepted your request.${
                            maskedContact
                              ? ` They can contact you via ${maskedContact}.`
                              : ""
                          }`}
                        {booking.status === "declined" &&
                          `Sorry, ${sellerName} wasn't able to take this booking. Feel free to try another available slot or a different seller.`}
                        {booking.status === "pending" &&
                          `Your request is still waiting on ${sellerName}. Check back here for an update on whether it's accepted or declined.`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {!isLoadingBookings && (
          <div className="bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-2xl p-5 shadow-xl">
            <h3 className="text-sm font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-4">
              Booking History
            </h3>

            {bookingHistory.length === 0 ? (
              <p className="text-center text-[var(--text-muted)] text-sm py-6">
                Your completed bookings will show up here.
              </p>
            ) : (
              <div className="space-y-3">
                {bookingHistory.map((entry: any) => {
                  const price = entry.listings?.price;
                  const priceLabel =
                    typeof price === "number"
                      ? `KES ${price.toLocaleString()}`
                      : price || "";
                  return (
                    <div
                      key={entry.id}
                      className="bg-[var(--bg-container-2)] border border-[var(--border-soft)] rounded-xl p-4 flex items-start justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="text-[var(--text-primary)] font-semibold text-sm truncate">
                          {entry.listings?.title || "Service"}
                        </p>
                        <p className="text-[var(--text-muted)] text-xs mt-0.5">
                          {entry.listings?.seller_applications?.display_name ||
                            "The seller"}
                        </p>
                      </div>
                      {priceLabel && (
                        <p className="shrink-0 text-sm font-bold text-[var(--text-primary)]">
                          {priceLabel}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      {ordersTour.activeStep && (
        <Coachmark
          step={ordersTour.activeStep}
          stepIndex={ordersTour.stepIndex}
          totalSteps={ordersTour.totalSteps}
          onNext={ordersTour.next}
          onDismiss={ordersTour.dismiss}
        />
      )}
    </div>
  );
}

// Dropdown drawn by the app instead of the browser's own <select>. The native
// popup ignores the app theme (white list with white text in dark mode, so
// the options cannot be read on phones); this one uses the theme variables.
function ThemedSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  disabled?: boolean;
  ariaLabel: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [openUp, setOpenUp] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  React.useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  // Bring the chosen option into view when the list opens.
  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const chosen = listRef.current.querySelector(
      '[aria-selected="true"]'
    ) as HTMLElement | null;
    chosen?.scrollIntoView({ block: "nearest" });
  }, [open]);

  const toggle = () => {
    if (disabled) return;
    if (!open && wrapRef.current) {
      // Open upward when there is little room below the field.
      const r = wrapRef.current.getBoundingClientRect();
      const below = window.innerHeight - r.bottom;
      setOpenUp(below < 260 && r.top > below);
    }
    setOpen((o) => !o);
  };

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={wrapRef} className="relative mt-1.5">
      <button
        type="button"
        disabled={disabled}
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={`w-full flex items-center justify-between gap-2 text-left bg-transparent border rounded-xl px-4 py-3 text-base outline-none transition disabled:opacity-50 ${
          open
            ? "border-[#1006C5] ring-2 ring-[#1006C5]/40"
            : "border-[var(--border-soft)] focus-visible:border-[#1006C5] focus-visible:ring-2 focus-visible:ring-[#1006C5]/40"
        } ${
          selected
            ? "text-[var(--text-primary)] font-semibold"
            : "text-[var(--text-muted)]"
        }`}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[var(--text-muted)] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className={`absolute left-0 right-0 z-20 max-h-56 overflow-y-auto rounded-xl border border-[var(--border-soft)] bg-[var(--bg-container)] shadow-2xl py-1 ${
            openUp ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          {options.map((o) => (
            <li key={o.value} role="option" aria-selected={o.value === value}>
              <button
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                  o.value === value
                    ? "bg-[#1006C5] text-white font-semibold"
                    : "text-[var(--text-primary)] hover:bg-[var(--surface-soft-hover)]"
                }`}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Buyer "Make a request" sheet: pick a category, a subcategory, and describe
// what you could not find. Saved to `listing_requests`; sellers never see who
// asked — requests are counted per category/subcategory so we can tell sellers
// when enough buyers want the same thing.
function RequestListingSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  // The sheet is portaled to <body>, outside the themed root div where the
  // [data-theme] CSS variables live. Re-apply the theme on a wrapper so the
  // var(--bg-container) etc. below resolve (otherwise the sheet is see-through).
  const { theme } = useTheme();
  const cats = React.useMemo(() => Object.values(CATEGORIES) as any[], []);
  const [catId, setCatId] = React.useState("");
  const [subId, setSubId] = React.useState("");
  const [county, setCounty] = React.useState("");
  const [desc, setDesc] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [sent, setSent] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setCatId("");
      setSubId("");
      setCounty("");
      setDesc("");
      setError("");
      setSent(false);
      setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  const subs = ((cats.find((c) => c.id === catId)?.subcategories ||
    []) as any[]);

  const submit = async () => {
    if (!catId) return setError("Choose a category.");
    if (subs.length > 0 && !subId) return setError("Choose a subcategory.");
    if (!county) return setError("Choose your county.");
    if (desc.trim().length < 10)
      return setError("Add a few more words so sellers know what you need.");
    setBusy(true);
    setError("");
    try {
      const { error: insertError } = await supabase
        .from("listing_requests")
        .insert({
          category: catId,
          subcategory: subs.length > 0 ? subId : "general",
          county,
          description: desc.trim(),
        });
      if (insertError) {
        setError(
          (insertError as any).code === "23505"
            ? "You already asked for this in the last 24 hours. You can ask again tomorrow, or try a different category or subcategory."
            : "Could not send your request. Check your connection and try again."
        );
        return;
      }
      setSent(true);
    } catch {
      setError("Could not send your request. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const fieldClass =
    "w-full bg-transparent border border-[var(--border-soft)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] rounded-xl px-4 py-3 text-base outline-none focus:border-[#1006C5] focus:ring-2 focus:ring-[#1006C5]/40 transition disabled:opacity-50";

  return createPortal(
    <div data-theme={theme}>
    <div
      className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Make a request"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-[var(--bg-container)] text-[var(--text-primary)] border border-[var(--border-soft)] rounded-t-3xl sm:rounded-2xl p-6 shadow-2xl"
      >
        {sent ? (
          <div className="text-center py-4">
            <h2 className="text-xl font-bold mb-2">Request sent</h2>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-6">
              Thanks. We will let our sellers in this category know. Your name
              is not shared with the sellers.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="w-full bg-[#1006C5] hover:bg-[#0D0599] text-white font-bold py-3 px-4 rounded-xl text-sm transition"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold">Make a request</h2>
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  Ask for something you could not find. Your name is not shared
                  with sellers.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="p-1.5 -mr-1.5 rounded-full hover:bg-[var(--surface-soft-hover)] text-[var(--text-muted)]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="text-xs font-semibold text-[var(--text-secondary)]">
              <span>Category</span>
              <ThemedSelect
                ariaLabel="Category"
                value={catId}
                placeholder="Choose a category"
                options={cats.map((c) => ({ value: c.id, label: c.name }))}
                onChange={(v) => {
                  setCatId(v);
                  setSubId("");
                  setError("");
                }}
              />
            </div>

            <div className="text-xs font-semibold text-[var(--text-secondary)]">
              <span>Subcategory</span>
              <ThemedSelect
                ariaLabel="Subcategory"
                value={subId}
                disabled={!catId || subs.length === 0}
                placeholder={
                  catId && subs.length === 0
                    ? "No subcategories"
                    : "Choose a subcategory"
                }
                options={subs.map((s: any) => ({ value: s.id, label: s.name }))}
                onChange={(v) => {
                  setSubId(v);
                  setError("");
                }}
              />
            </div>

            <div className="text-xs font-semibold text-[var(--text-secondary)]">
              <span>Your county</span>
              <ThemedSelect
                ariaLabel="Your county"
                value={county}
                placeholder="Choose a county"
                options={COUNTIES.map((c) => ({ value: c.name, label: c.name }))}
                onChange={(v) => {
                  setCounty(v);
                  setError("");
                }}
              />
            </div>

            <label className="text-xs font-semibold text-[var(--text-secondary)]">
              What are you looking for?
              <textarea
                value={desc}
                maxLength={300}
                rows={4}
                onChange={(e) => {
                  setDesc(e.target.value);
                  setError("");
                }}
                placeholder="e.g. Fresh fruit salad cups, delivered around campus"
                className={fieldClass + " mt-1.5 resize-none"}
              />
              <span className="block text-right text-[10px] text-[var(--text-muted)] mt-1">
                {desc.length}/300
              </span>
            </label>

            {error && (
              <p role="alert" className="text-xs text-red-500 -mt-2">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="w-full bg-[#1006C5] hover:bg-[#0D0599] text-white font-bold py-3 px-4 rounded-xl text-sm shadow-lg transition disabled:opacity-60"
            >
              {busy ? "Sending…" : "Send request"}
            </button>
          </div>
        )}
      </div>
    </div>
    </div>,
    document.body
  );
}

// Account screen: profile card, wishlist (listings the buyer has saved via
// the heart button on a product's details page), and the seller signup CTA.
// Bookings now live under the separate Orders tab — see BuyerOrdersView.
function BuyerProfileView({
  onBack,
  navigateTo,
  onProductClick,
  showAppToast,
}: any) {
  const { theme } = useTheme();
  const [profile, setProfile] = React.useState<any>(null);
  const [buyerId, setBuyerId] = React.useState<string | null>(null);
  const [wishlist, setWishlist] = React.useState<WishlistItem[]>([]);
  const [isLoadingWishlist, setIsLoadingWishlist] = React.useState(true);
  const accountProfileRef = useRef<HTMLDivElement>(null);
  const accountThemeToggleRef = useRef<HTMLDivElement>(null);
  const accountWishlistRef = useRef<HTMLDivElement>(null);
  const accountRequestRef = useRef<HTMLButtonElement>(null);
  const isNewBuyerAccount = useIsNewBuyer();

  // Sign-out. "local" ends the session on this device; "global" ends it on
  // every device. Global asks for a second tap so it cannot be hit by accident.
  const [signingOut, setSigningOut] = React.useState(false);
  const [confirmAll, setConfirmAll] = React.useState(false);
  const [requestOpen, setRequestOpen] = React.useState(false);
  const handleSignOut = async (scope: "local" | "global") => {
    setSigningOut(true);
    try {
      const { error } = await supabase.auth.signOut({ scope });
      if (error) throw error;
      try {
        localStorage.removeItem("current_buyer_screen");
      } catch {}
      navigateTo("entry");
    } catch {
      showAppToast?.({
        title: "Could not log out",
        message: "Check your connection and try again.",
      });
    } finally {
      setSigningOut(false);
      setConfirmAll(false);
    }
  };
  const accountTour = useCoachTour(
    "account",
    [
      {
        ref: accountProfileRef,
        title: "Your Profile",
        text: "This shows the email address you signed in with.",
      },
      {
        ref: accountThemeToggleRef,
        title: "Light / Dark Mode",
        text: "Switch between light and dark mode from here.",
      },
      {
        ref: accountWishlistRef,
        title: "Wishlist",
        text: "Listings you've saved show up here — tap GO TO to jump straight to a listing.",
      },
      {
        ref: accountRequestRef,
        title: "Make a Request",
        text: "Can't find what you need? Tap here to ask sellers to list it.",
      },
    ],
    isNewBuyerAccount
  );

  const fetchWishlist = React.useCallback(async (userId: string) => {
    setIsLoadingWishlist(true);
    try {
      // Step 1: the buyer's own wishlist rows. Deliberately NOT joining
      // straight to `listings` here (a single
      // `listings:listing_id(...)` embed) — that embed only works if
      // Supabase has an actual foreign-key constraint from
      // wishlists.listing_id -> listings.id registered in its schema
      // cache. If that FK is missing or named differently, PostgREST
      // returns an error for the WHOLE query, and the previous code
      // silently swallowed it (`if (!error) setWishlist(...)`) — so a
      // save could succeed in the DB while the Account screen just
      // never showed it, with nothing in the console to explain why.
      const { data: wishlistRows, error: wishlistError } = await supabase
        .from("wishlists")
        .select("id, listing_id, created_at")
        .eq("buyer_id", userId)
        .order("created_at", { ascending: false });

      if (wishlistError) {
        console.error(
          "[Wishlist] Failed to load wishlist rows:",
          wishlistError
        );
        setWishlist([]);
        return;
      }

      const rows = wishlistRows || [];
      if (rows.length === 0) {
        setWishlist([]);
        return;
      }

      // Step 2: fetch the matching listings, WITHOUT embedding
      // seller_applications this time (see below for why that used to be
      // a single query) — split into a third manual step instead.
      // Selecting "*" (rather than a hand-picked column list) matters here:
      // ProductDetailsView — which "Go To" below hands this object
      // straight to, same as clicking a listing from Buyer Home — branches
      // on fields like `listing_kind` (service vs. physical) and
      // `subcategory`/`description` that a narrower select used to leave
      // out entirely. With those missing, every wishlisted service
      // silently rendered as a physical-product listing (showing a
      // "Contact Seller" button with no booking calendar), even though
      // the same listing opened correctly from the home feed.
      const listingIds = rows.map((r: any) => r.listing_id).filter(Boolean);
      const { data: listingRows, error: listingsError } = await supabase
        .from("listings")
        .select("*")
        .in("id", listingIds);

      if (listingsError) {
        console.error(
          "[Wishlist] Failed to load listings for wishlist:",
          listingsError
        );
      }

      // Step 3: seller info, fetched separately rather than via a
      // `seller_applications:seller_id(...)` embed on the query above.
      // That embed only works if Supabase has the listings.seller_id ->
      // seller_applications.id foreign key registered in its schema
      // cache — if it's missing (or named differently), PostgREST fails
      // the WHOLE listings query, not just the embedded part. That was
      // silently wiping out every item here: a real, still-existing
      // listing would come back as if it didn't exist at all, rendering
      // as "Listing removed" purely because of the embed, with nothing
      // but a console.error to show for it. Same fix as the
      // wishlists->listings hop above: do the join manually instead of
      // trusting an embed that may not exist. Fetching the same columns
      // the home feed's embed does (not just display_name) so
      // ProductDetailsView's contact sheet and category theming work
      // identically no matter which screen a listing was opened from.
      const sellerIds = Array.from(
        new Set(
          (listingRows || []).map((l: any) => l.seller_id).filter(Boolean)
        )
      );
      let sellerAppsById = new Map<string, any>();
      if (sellerIds.length > 0) {
        const { data: sellerRows, error: sellersError } = await supabase
          .from("seller_applications")
          .select(
            "id, display_name, phone_number, whatsapp_number, avatar_id, category, university, location, county"
          )
          .in("id", sellerIds);
        if (sellersError) {
          console.error(
            "[Wishlist] Failed to load seller info for wishlist:",
            sellersError
          );
        } else {
          sellerAppsById = new Map(
            (sellerRows || []).map((s: any) => [s.id, s])
          );
        }
      }

      const listingsById = new Map(
        (listingRows || []).map((l: any) => [
          l.id,
          {
            ...l,
            seller_applications: sellerAppsById.get(l.seller_id) || null,
          },
        ])
      );

      const merged = rows.map((row: any) => ({
        id: row.id,
        listing_id: row.listing_id,
        // A listing that's been deleted since being saved simply won't
        // be in `listingsById` — downstream render code already treats
        // a missing `listings` as "Listing removed", same as before.
        listings: listingsById.get(row.listing_id) || null,
      }));

      setWishlist(merged);
    } catch (err) {
      console.error("Error loading wishlist:", err);
    } finally {
      setIsLoadingWishlist(false);
    }
  }, []);

  React.useEffect(() => {
    async function loadUserData() {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (user) {
          setBuyerId(user.id);
          fetchWishlist(user.id);

          const { data, error } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", user.id)
            .single();

          if (!error && data) {
            setProfile(data);
          } else {
            setProfile({
              full_name: user.user_metadata?.full_name || "Campus User",
              email: user.email || "user@campus.ac.ke",
              avatar_url: user.user_metadata?.avatar_url,
            });
          }
        }
      } catch (err) {
        console.error("Error loading profile:", err);
      }
    }
    loadUserData();
  }, [fetchWishlist]);

  const removeFromWishlist = async (
    wishlistRowId: string,
    listingId?: string
  ) => {
    // Optimistic — pull it off the screen immediately, then confirm with
    // the DB. Chaining `.select()` onto the delete is the important part
    // here: without it, Supabase reports success even when a Row Level
    // Security policy silently matched zero rows (no error is thrown —
    // the DELETE request itself succeeds, it just deletes nothing). That
    // silent no-op was why a removed item could reappear after a reload:
    // the UI removal was real, but nothing had actually been deleted in
    // the database. Checking the returned rows lets a real failure be
    // detected and reverted instead of quietly lying to the buyer.
    const previous = wishlist;
    setWishlist((prev) => prev.filter((w: any) => w.id !== wishlistRowId));
    setListingSavedLocally(listingId, false);
    const { data, error } = await supabase
      .from("wishlists")
      .delete()
      .eq("id", wishlistRowId)
      .select("id");
    if (error || !data || data.length === 0) {
      if (error) {
        console.error("Error removing from wishlist:", error);
      } else {
        console.error(
          "[Wishlist] Delete affected 0 rows — likely blocked by a Row " +
            "Level Security policy on the `wishlists` table."
        );
      }
      setWishlist(previous);
      setListingSavedLocally(listingId, true);
      showAppToast?.({
        title: "Couldn't remove listing",
        message: (error && error.message) || "Please try again.",
      });
    }
  };

  return (
    <div className="h-full w-full bg-[var(--bg-app)] text-[var(--text-primary)] flex flex-col max-w-md mx-auto relative overflow-hidden animate-in fade-in duration-300">
      <div className="flex items-center gap-3 p-4 pb-2 shrink-0">
        <button
          onClick={onBack}
          className="p-1 hover:bg-[var(--surface-soft-hover)] rounded-full transition"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-bold">My Account</h1>
      </div>

      <div className="flex-1 overflow-y-auto p-4 pt-2 pb-24">
        <div className="max-w-md mx-auto space-y-6">
          <div
            ref={accountProfileRef}
            className="bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-2xl p-6 text-center shadow-xl"
          >
            <div className="w-24 h-24 bg-[var(--bg-container-2)] rounded-full mx-auto p-1 border-2 border-indigo-500/30 overflow-hidden mb-4 flex items-center justify-center">
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="Profile Avatar"
                  className="w-full h-full rounded-full object-cover"
                />
              ) : profile ? (
                <User className="h-10 w-10 text-indigo-400" />
              ) : (
                <div className="h-full w-full rounded-full bg-white/10 animate-pulse" />
              )}
            </div>

            <h2 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">
              {profile ? (
                profile.full_name || "Campus User"
              ) : (
                <span className="inline-block h-7 w-40 rounded bg-white/10 animate-pulse align-middle" />
              )}
            </h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {profile ? (
                profile.email || "user@campus.ac.ke"
              ) : (
                <span className="inline-block h-4 w-48 rounded bg-white/10 animate-pulse align-middle mt-1" />
              )}
            </p>

            <div className="mt-5 pt-4 border-t border-[var(--border-soft)] flex flex-col items-center gap-1">
              <button
                type="button"
                disabled={signingOut}
                onClick={() => handleSignOut("local")}
                className="w-full flex items-center justify-center gap-2 border border-[var(--border-soft)] hover:bg-[var(--surface-soft-hover)] text-[var(--text-primary)] font-semibold py-2.5 px-4 rounded-xl text-sm transition disabled:opacity-60"
              >
                <LogOut size={16} />
                {signingOut ? "Logging out…" : "Log out"}
              </button>
              <button
                type="button"
                disabled={signingOut}
                onClick={() =>
                  confirmAll ? handleSignOut("global") : setConfirmAll(true)
                }
                className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] underline decoration-[var(--border-soft)] py-2 transition disabled:opacity-60"
              >
                {confirmAll
                  ? "Tap again to log out everywhere"
                  : "Log out of all devices"}
              </button>
            </div>
          </div>

          {/* APPEARANCE — dark/light theme toggle */}
          <div className="flex items-center justify-between gap-3 bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-2xl p-4 shadow-xl">
            <div>
              <p className="text-sm font-semibold text-[var(--text-primary)]">
                Dark Mode
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">
                {theme === "dark"
                  ? "On — easy on the eyes"
                  : "Off — bright & clean"}
              </p>
            </div>
            <div ref={accountThemeToggleRef}>
              <ThemeToggleSwitch className="size-11 p-2" />
            </div>
          </div>

          {/* WISHLIST (listings saved via the heart button on Product Details) */}
          <div
            ref={accountWishlistRef}
            className="bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-2xl p-5 shadow-xl"
          >
            <h3 className="text-sm font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-4">
              Wishlist
            </h3>

            {isLoadingWishlist ? (
              <div className="text-center py-6">
                <Loader message="Loading your wishlist" scale={0.4} />
              </div>
            ) : wishlist.length === 0 ? (
              <p className="text-center text-[var(--text-muted)] text-sm py-6">
                Nothing saved yet — tap the heart on a listing to add it here.
              </p>
            ) : (
              <div className="space-y-3">
                {wishlist.map((item: any) => {
                  const listing = item.listings;
                  // Deleted listings return a null join; listings that still
                  // exist but are sold/expired/completed keep their data but
                  // aren't bookable anymore. Either way, treat as unavailable.
                  const isAvailable = !!listing && listing.status === "active";
                  const thumb = listing?.photo_urls?.[0] || null;
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 bg-[var(--bg-container-2)] border border-[var(--border-soft)] rounded-xl p-3"
                    >
                      <div className="w-12 h-12 rounded-lg bg-[var(--surface-soft)] overflow-hidden shrink-0">
                        {thumb && (
                          <img
                            src={thumb}
                            alt={listing.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[var(--text-primary)] font-semibold text-sm truncate">
                          {listing?.title || "Listing removed"}
                        </p>
                        {isAvailable ? (
                          <p className="text-[var(--text-muted)] text-xs mt-0.5 truncate">
                            {listing.seller_applications?.display_name ||
                              "Seller"}
                            {listing.price != null && ` · KES ${listing.price}`}
                          </p>
                        ) : (
                          <p className="text-stone-500 text-xs mt-0.5 italic">
                            Unavailable
                          </p>
                        )}
                      </div>
                      {isAvailable ? (
                        <button
                          type="button"
                          onClick={() => onProductClick?.(listing)}
                          style={{ backgroundColor: "#1006C5" }}
                          className="shrink-0 text-[11px] font-bold uppercase tracking-wider text-white transition px-3 py-1.5 rounded-lg hover:brightness-110 active:brightness-95"
                        >
                          Go To
                        </button>
                      ) : (
                        <span className="shrink-0 text-[11px] font-bold uppercase tracking-wider text-stone-600 px-2 py-1">
                          Unavailable
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          removeFromWishlist(item.id, item.listing_id)
                        }
                        className="shrink-0 p-2 hover:bg-[var(--surface-soft-hover)] rounded-full transition text-[var(--text-muted)] hover:text-red-400"
                        aria-label="Remove from wishlist"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="relative bg-gradient-to-b from-[#1A153B] to-[#12162B] border border-indigo-500/20 rounded-2xl p-6 text-center shadow-lg overflow-hidden before:absolute before:top-0 before:left-0 before:right-0 before:h-[2px] before:bg-gradient-to-r before:from-[#1006C5] before:to-[#ffb800]">
            <h3 className="text-xl font-bold text-white mb-2">
              Can't find what you need?
            </h3>
            <p className="text-sm text-gray-400 max-w-xs mx-auto mb-5 leading-relaxed">
              Tell us what you are looking for and we will pass it on to our
              sellers in that category.
            </p>

            <button
              ref={accountRequestRef}
              onClick={() => setRequestOpen(true)}
              className="w-full bg-[#1006C5] hover:bg-[#0D0599] text-white font-bold py-3 px-4 rounded-xl shadow-md transition transform active:scale-[0.98]"
            >
              Make a request
            </button>
          </div>
        </div>
      </div>
      <RequestListingSheet
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
      />
      {accountTour.activeStep && (
        <Coachmark
          step={accountTour.activeStep}
          stepIndex={accountTour.stepIndex}
          totalSteps={accountTour.totalSteps}
          onNext={accountTour.next}
          onDismiss={accountTour.dismiss}
        />
      )}
    </div>
  );
}

// Hoisted out of EntryScreen (unlike a plain `const BuyerHome = () => {...}`
// declared inside that component's body would be) specifically so its
// identity as a component TYPE stays stable across renders. A function
// declared inline inside another component's body is a brand-new function
// object every single time that outer component re-renders — including on
// every theme toggle, since `theme` lived in EntryScreen's own state. React
// reconciles `<BuyerHome />` by comparing types, so a fresh function
// reference each render made it look like an entirely different component,
// forcing a full unmount + remount of everything inside — including
// ThemeToggleSwitch, whose Framer Motion `animate` prop only interpolates
// smoothly across renders of the SAME mounted instance. On mount it just
// draws the final frame directly, no transition — which is exactly why this
// screen's dark-mode toggle looked instant/static while the Account
// screen's (BuyerProfileView, already a stable top-level function) played
// its morph animation normally. `theme`, `navigateTo` and `runTileTransition`
// — EntryScreen closures this component used to reach into directly — are
// now passed in as ordinary props instead.
function BuyerHome({
  listings,
  onProductClick,
  theme,
  navigateTo,
  runTileTransition,
  selectedCounty,
  onCountyChange,
}: {
  listings: any[];
  onProductClick: (product: any) => void;
  theme: ThemeName;
  navigateTo: (screen: string) => void;
  runTileTransition: (cb: () => void, color: string) => void;
  selectedCounty: string;
  onCountyChange: (county: string) => void;
}) {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [isCountyDialogOpen, setIsCountyDialogOpen] = useState(false);
  // The div actually scrolling on this screen — see ScrollToTopButton.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const homeThemeToggleRef = useRef<HTMLDivElement>(null);
  const homeSearchBarRef = useRef<HTMLDivElement>(null);
  const homeFirstListingRef = useRef<HTMLDivElement>(null);
  const isNewBuyerHome = useIsNewBuyer();
  const homeTour = useCoachTour(
    "home",
    [
      {
        ref: homeThemeToggleRef,
        title: "Light / Dark Mode",
        text: "Tap this switch anytime to toggle between light and dark mode.",
      },
      {
        ref: homeSearchBarRef,
        title: "Search",
        text: "Look up a listing by location, institution, category, or the seller's name.",
      },
      {
        ref: homeFirstListingRef,
        title: "Listings",
        text: "Tap a listing to see all its details.",
      },
    ],
    isNewBuyerHome
  );

  // "I'm looking for..." tag filter — starts with every category active;
  // removing a tag's "x" excludes that category from the results below.
  const [activeCategoryIds, setActiveCategoryIds] = useState<Set<string>>(
    () => new Set((Object.values(CATEGORIES) as any[]).map((c) => c.id))
  );
  const toggleCategoryFilter = (catId: string) => {
    setActiveCategoryIds((prev) => {
      const next = new Set(prev);
      next.delete(catId);
      return next;
    });
  };
  const resetCategoryFilters = () =>
    setActiveCategoryIds(
      new Set((Object.values(CATEGORIES) as any[]).map((c) => c.id))
    );

  // Live Supabase lookup: when the query looks like a location or
  // institution name, this fetches every matching seller id straight
  // from the `seller_applications` table (debounced), so those sellers'
  // listings surface below even if the text search alone wouldn't have
  // matched (e.g. the buyer typed a neighborhood the listing title
  // itself never mentions).
  const [locationMatchIds, setLocationMatchIds] = useState<Set<string>>(
    new Set()
  );
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setLocationMatchIds(new Set());
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const ids = await searchSellersByLocationOrInstitution(trimmed);
      if (!cancelled) setLocationMatchIds(ids);
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery]);

  // Bypasses the debounce above so the magnifying-glass button (and the
  // overlay's "Search" button / Enter key) triggers the seller
  // location/institution lookup against Supabase right away.
  const runLocationSearchNow = async () => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setLocationMatchIds(new Set());
      return;
    }
    const ids = await searchSellersByLocationOrInstitution(trimmed);
    setLocationMatchIds(ids);
  };

  return (
    <div className="h-full w-full bg-[var(--bg-app)] text-[var(--text-primary)] flex flex-col max-w-md mx-auto relative overflow-hidden animate-in fade-in duration-300">
      <header className="border-b border-[var(--border-soft)] flex flex-col sticky top-0 bg-[var(--bg-app)]/90 backdrop-blur-md z-20">
        <div className="h-16 flex items-center px-4">
          <button
            onClick={() =>
              runTileTransition(() => navigateTo("entry"), "#1006C5")
            }
            className="p-2 text-[var(--text-primary)] hover:text-[var(--text-primary)] -ml-2 shrink-0"
            title="Back to Selection"
          >
            <ChevronLeft size={24} />
          </button>

          <div className="flex-1 ml-2">
            <div ref={homeSearchBarRef}>
              <MarketplaceSearchBar
                query={searchQuery}
                onQueryChange={setSearchQuery}
                filterLabel="I'm looking for..."
                filterItems={Object.values(CATEGORIES) as any[]}
                activeFilterIds={activeCategoryIds}
                onToggleFilter={toggleCategoryFilter}
                onResetFilters={resetCategoryFilters}
                onSearch={runLocationSearchNow}
                theme={theme}
              />
            </div>
          </div>
        </div>
      </header>

      <div
        ref={scrollContainerRef}
        className="flex-1 p-4 overflow-y-auto pb-24"
      >
        <div className="flex items-center justify-between mb-4 gap-2">
          <h3 className="min-w-0 flex-1 text-[15px] leading-tight font-bold">
            Trending Deals, Campus &amp; Beyond
          </h3>
          <CountyFilterButton
            compact
            selectedCounty={selectedCounty}
            onClick={() => setIsCountyDialogOpen(true)}
            theme={theme}
          />
          <div ref={homeThemeToggleRef}>
            <ThemeToggleSwitch className="size-9 p-1.5" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {listings
            .filter((listing: any) => {
              const sellerApp = getSellerApp(listing);
              const query = searchQuery.trim().toLowerCase();
              const sellerName =
                sellerApp?.display_name || // Buyers search/see display_name, never full_name
                "";
              // NOTE: "listings" has no "category" column in this schema —
              // category ALWAYS comes from the joined seller_applications row.
              const itemCategory = (sellerApp?.category || "").toLowerCase();

              // "I'm looking for..." tag filter — resolve subcategories
              // (e.g. "laundry") up to their parent ("home_services")
              // before checking against the active tag set, the same way
              // the rest of this file resolves category theming.
              const parentCategoryId =
                SUBCATEGORY_TO_PARENT[itemCategory] || itemCategory;
              if (!activeCategoryIds.has(parentCategoryId)) return false;

              // County filter (uses the listing's stored `city`).
              if (!listingMatchesCounty(resolveListingCounty(listing, getSellerApp(listing)), selectedCounty)) return false;

              if (!query) return true;

              const matchesText =
                listing.title?.toLowerCase().includes(query) ||
                itemCategory.includes(query) ||
                (getSubcategoryDisplayName(listing.subcategory) || "")
                  .toLowerCase()
                  .includes(query) ||
                resolveListingCounty(listing, sellerApp)
                  .toLowerCase()
                  .includes(query) ||
                sellerName.toLowerCase().includes(query) ||
                (sellerApp?.location || "").toLowerCase().includes(query) ||
                (sellerApp?.university || "").toLowerCase().includes(query);

              const matchesLocationLookup = locationMatchIds.has(
                listing.seller_id
              );

              return matchesText || matchesLocationLookup;
            })
            .map((listing: any, listingIdx: number) => {
              const sellerApp = getSellerApp(listing);

              // DEBUG: flag listings whose seller_applications row is missing
              // OR present-but-incomplete (blank display_name / non-canonical
              // category), so bad data rows are easy to spot in the console.
              if (!sellerApp) {
                console.warn(
                  `[Buyer Feed] "${listing.title}" (id=${listing.id}, seller_id=${listing.seller_id}): ` +
                    `no matching row in seller_applications. Confirm this seller_id exists as an "id" there.`
                );
              } else if (!sellerApp.display_name || !sellerApp.category) {
                console.warn(
                  `[Buyer Feed] "${listing.title}" (id=${listing.id}, seller_id=${listing.seller_id}): ` +
                    `seller_applications row found but incomplete — display_name="${sellerApp.display_name}", category="${sellerApp.category}". ` +
                    `Fix this seller's row in Supabase.`
                );
              }

              // DYNAMIC CATEGORY RESOLUTION (uses the literal string stored in
              // seller_applications.category — "listings" has no category column)
              const sellerCategory = sellerApp?.category || "*";
              const catKey = sellerCategory.toLowerCase();
              const catTheme = getThemeColorForRawCategory(catKey);
              const catAbbr = getCategoryAbbrForRawCategory(catKey);
              // The card body shows the listing's specific subcategory (e.g.
              // "Laundry") — the corner badge already shows the broad parent
              // category (e.g. "HS"). Older listings created before per-listing
              // subcategories existed fall back to the parent category label.
              const subcategoryLabel =
                getSubcategoryDisplayName(listing.subcategory) ||
                getCategoryDisplayLabel(sellerCategory);

              const displayImage =
                listing.photo_urls?.[0] ||
                (Array.isArray(listing.images)
                  ? listing.images[0]
                  : listing.images) ||
                listing.image_url;

              // DYNAMIC DISPLAY NAME RESOLUTION — buyers only ever see display_name
              const sellerDisplayName =
                sellerApp?.display_name || "Campus Seller";

              return (
                <div
                  key={listing.id}
                  ref={listingIdx === 0 ? homeFirstListingRef : undefined}
                  onClick={() => onProductClick(listing)}
                  className="bg-[var(--bg-container)] rounded-xl overflow-hidden cursor-pointer hover:-translate-y-1 transition-transform border border-[var(--border-soft)] relative group shadow-lg"
                >
                  {catAbbr && (
                    <span
                      className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center bg-black/40 backdrop-blur-sm"
                      style={{ border: `1.5px solid ${catTheme}` }}
                      title={
                        CATEGORIES[SUBCATEGORY_TO_PARENT[catKey] || catKey]
                          ?.name
                      }
                    >
                      <span
                        className="text-[10px] leading-none"
                        style={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontWeight: 300,
                          letterSpacing: "0.02em",
                          transform: "scaleX(0.85)",
                          color: catTheme,
                        }}
                      >
                        {catAbbr}
                      </span>
                    </span>
                  )}

                  <div
                    className="h-32 w-full flex items-center justify-center opacity-80 transition-opacity group-hover:opacity-100 overflow-hidden"
                    style={{ backgroundColor: `${catTheme}20` }}
                  >
                    {displayImage ? (
                      <img
                        src={displayImage}
                        alt={listing.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <span
                        className="font-bold tracking-widest opacity-30 text-xs uppercase"
                        style={{ color: catTheme }}
                      >
                        {subcategoryLabel}
                      </span>
                    )}
                  </div>

                  <div className="p-3">
                    {/* SUBCATEGORY TAG — corner badge already shows the broad category */}
                    <p
                      className="text-[10px] font-bold font-mono mb-1 uppercase tracking-wider truncate"
                      style={{ color: catTheme }}
                    >
                      {subcategoryLabel}
                    </p>
                    <h4 className="font-bold text-sm leading-tight mb-2 truncate text-[var(--text-primary)]">
                      {listing.title}
                    </h4>
                    <div className="flex justify-between items-end gap-2">
                      <p
                        className="font-black text-sm shrink-0"
                        style={{ color: catTheme }}
                      >
                        {typeof listing.price === "number"
                          ? `KES ${listing.price.toLocaleString()}`
                          : listing.price}
                      </p>
                      {/* DYNAMIC SELLER DISPLAY NAME */}
                      <p className="text-[10px] text-[var(--text-muted)] truncate min-w-0 text-right font-medium">
                        {sellerDisplayName}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          {selectedCounty !== ALL_COUNTIES_LABEL &&
            !listings.some((l: any) =>
              listingMatchesCounty(resolveListingCounty(l, getSellerApp(l)), selectedCounty)
            ) && (
              <div className="col-span-2 py-16 text-center">
                <p className="font-bold text-sm">
                  No listings in {selectedCounty} yet
                </p>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  Try a different county or check back later.
                </p>
              </div>
            )}
        </div>
      </div>
      <CountySelectDialog
        isOpen={isCountyDialogOpen}
        onClose={() => setIsCountyDialogOpen(false)}
        onSelectCounty={onCountyChange}
        selectedCounty={selectedCounty}
        theme={theme}
      />
      <ScrollToTopButton scrollRef={scrollContainerRef} />
      {homeTour.activeStep && (
        <Coachmark
          step={homeTour.activeStep}
          stepIndex={homeTour.stepIndex}
          totalSteps={homeTour.totalSteps}
          onNext={homeTour.next}
          onDismiss={homeTour.dismiss}
        />
      )}
    </div>
  );
}

function EntryScreen({
  runTileTransition,
  handleBuyerClick,
  runSellerEnterTransition,
  showSellerLockedNote,
  initialSide,
  onAssetsReady,
}: any) {
  // --- REFS (imperative, per-frame DOM writes — kept out of React state
  // for smoothness, mirroring how a real 3D viewer would be driven) ---
  const heroRef = useRef<HTMLDivElement>(null);
  // `objRef` is now just the plain positioning/blur wrapper around the two
  // <model-viewer> elements — it no longer carries rotation (that moved to
  // each viewer's `cameraOrbit`, which is real 3D, not a CSS fake-out).
  const objRef = useRef<HTMLDivElement>(null);
  const cardViewerRef = useRef<any>(null);
  const boxViewerRef = useRef<any>(null);
  // Gates each object's visibility until its .glb has actually finished
  // loading. Before that, model-viewer can briefly show its own generic
  // default framing (ignoring our camera-orbit attribute until the model's
  // bounding sphere is known) — which looked like the box flashing a
  // top-down view before snapping to the correct branded angle. Keeping it
  // invisible until `load` fires means that wrong frame is never seen.
  const [cardLoaded, setCardLoaded] = useState(false);
  const [boxLoaded, setBoxLoaded] = useState(false);

  // Tells the boot-time preloader (mounted up in App) that the two models
  // visible the instant EntryScreen appears are actually ready to be seen —
  // this is what lets the preloader's wipe wait for real readiness instead
  // of firing on a fixed timer regardless of load state.
  useEffect(() => {
    if (cardLoaded && boxLoaded) onAssetsReady?.();
  }, [cardLoaded, boxLoaded, onAssetsReady]);

  const particleRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Ambient coin/sale-tag particles: previously a single <model-viewer>
  // per slot had its `src` swapped between the coin and sale-tag glb on
  // side-switch. On src change, model-viewer keeps rendering the OLD
  // model's last frame until the new one finishes loading — so tapping
  // "Seller" right after a reload (before the sale-tag glb had loaded)
  // showed coins for a few seconds before snapping to sale tags.
  // Fix: mount BOTH models per slot at all times, each its own gated
  // opacity (exactly like cardLoaded/boxLoaded below), and only toggle
  // which one is visible — never swap `src` on an existing element.
  const coinParticleRefs = useRef<any[]>([]);
  const tagParticleRefs = useRef<any[]>([]);
  // Manual drag-to-spin: lets phones (which have no hover) spin the object
  // by swiping a finger across it. Accumulates without limit — several
  // swipes can carry it through multiple full rotations, unlike the
  // hover-tilt below which is clamped to a small range.
  const isDraggingRef = useRef(false);
  const lastDragPosRef = useRef({ x: 0, y: 0 });
  const manualOrbitRef = useRef({ theta: 0, phi: 0 });
  const rafRef = useRef<number | null>(null);
  const mouseRef = useRef({ x: 0, y: 0 }); // normalized -0.5..0.5
  const currentMouseRef = useRef({ x: 0, y: 0 }); // lerped/smoothed
  const pointerRawRef = useRef({ x: -9999, y: -9999 }); // raw client coords
  const isSwitchingRef = useRef(false);
  const switchTimelineRef = useRef<gsap.core.Timeline | null>(null);
  const switchSafetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const lastBurstRef = useRef(0);
  const burstIdRef = useRef(0);

  // `selectedSide` is the committed target — updates the instant a side is
  // tapped (drives the Enter button + active pill), matching how the Soda
  // reference immediately flags the clicked flavor card as .active while
  // the rest of the transition is still playing out underneath it.
  //
  // Initialized from `initialSide` (not hardcoded to "buyer") so that
  // re-entering this screen — e.g. clicking Home from the seller
  // dashboard — lands back on whichever side the person actually came
  // from, rather than always resetting to Buyer.
  const [selectedSide, setSelectedSide] = useState<"buyer" | "seller">(
    initialSide === "seller" ? "seller" : "buyer"
  );
  // The app-wide mega menu (Marketplace/Support/Company/Legal) — shared by
  // both sides since it lives in this screen's persistent header, above
  // the buyer/seller switch, not inside either side's own content.
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  // `displayedSide` only updates at the peak of the spin — this is the
  // "texture swap" moment, so the card/cart icon, headlines and particle
  // set all change together while everything is mid-spin-blur and briefly
  // invisible, exactly like the can's base-color swap in the reference.
  const [displayedSide, setDisplayedSide] = useState<"buyer" | "seller">(
    initialSide === "seller" ? "seller" : "buyer"
  );
  const [isSwitching, setIsSwitching] = useState(false);
  // `strokeLabel` drives the stroke-text headline specifically. Unlike
  // `displayedSide` (which swaps mid-spin, hidden inside the blur), this
  // only updates once the hyperspin choreography has fully closed —
  // i.e. inside the outer timeline's onComplete below — so the stroke
  // draw-in animation never starts while the spin is still settling.
  const [strokeLabel, setStrokeLabel] = useState<"buyer" | "seller">(
    initialSide === "seller" ? "seller" : "buyer"
  );
  const [bursts, setBursts] = useState<
    Array<{
      id: number;
      x: number;
      y: number;
      tx: number;
      ty: number;
      icon: "coin" | "bag";
    }>
  >([]);

  const THEME = {
    buyer: {
      accent: "#1006C5",
      soft: "#c7c3ff",
      inner: "#1006C5",
      mid: "#0a0450",
      outer: "#040218",
      label: "Buyer",
      sub: "Browse and Discover",
      description:
        "Find what young enterprenuers are offering — fast, verified, and close by.",
      icon: CreditCard,
      particleIcon: Coins,
      badge: "BUYER ACCOUNT",
    },
    seller: {
      accent: "#FFB800",
      soft: "#ffe6a8",
      inner: "#FFB800",
      mid: "#7a4b00",
      outer: "#140d00",
      label: "Seller",
      sub: "List and Earn",
      description:
        "Set up your workspace, list your services, and start earning.",
      icon: ShoppingCart,
      particleIcon: ShoppingBag,
      badge: "SELLER VERIFICATION",
    },
  } as const;

  // Each object's resting camera angle — calibrated by test-rendering the
  // actual .glb files offline: the card's front (vs. back) sits at
  // theta=180°, and the box's INUA top decal only comes into view once the
  // camera lifts above the equator (phi < 90°). Cursor tilt and the
  // hyperspin both add their deltas on top of these, per object.
  const BASE_ORBIT = {
    buyer: { theta: 180, phi: 125 },
    seller: { theta: -320, phi: 79 },
  } as const;

  // Fixed ambient float slots for the coin/bag particles drifting around
  // the object — analogous to the reference's scattered berry models.
  const PARTICLE_SLOTS = [
    { top: "14%", left: "10%", size: 26 },
    { top: "72%", left: "14%", size: 20 },
    { top: "18%", left: "84%", size: 24 },
    { top: "66%", left: "86%", size: 18 },
    { top: "82%", left: "50%", size: 22 },
    { top: "6%", left: "52%", size: 16 },
  ];
  // One loaded flag per slot per model — see the comment on
  // coinParticleRefs/tagParticleRefs above for why each slot mounts both.
  const [coinLoadedFlags, setCoinLoadedFlags] = useState<boolean[]>(() =>
    PARTICLE_SLOTS.map(() => false)
  );
  const [tagLoadedFlags, setTagLoadedFlags] = useState<boolean[]>(() =>
    PARTICLE_SLOTS.map(() => false)
  );

  // Buyer-side coins use fixed, hand-placed positions — deliberately
  // hugging the card's corners/edges (like the reference's berries hug the
  // can) rather than random placement, which could occasionally land a
  // coin mid-card. Combined with the z-index bump below (see the ambient
  // layer's style), these render IN FRONT of the card for the "popping
  // out of the screen" effect, so slight overlap with the card's edges is
  // intentional rather than something to avoid.
  const COIN_SLOTS_BUYER = [
    { top: "22%", left: "8%" },
    { top: "20%", left: "76%" },
    { top: "40%", left: "2%" },
    { top: "44%", left: "90%" },
    { top: "58%", left: "14%" },
    { top: "56%", left: "80%" },
  ];
  // Each coin also gets its own fixed starting tilt (like the reference's
  // berries each having their own camera-orbit) so they don't all look
  // identical, PLUS a per-instance auto-rotate speed (see `auto-rotate` /
  // `rotation-per-second` on the <model-viewer> below) so they're never
  // dormant — a slow, gentle spin, staggered enough per-coin that they
  // don't all turn in visible lockstep.
  const [coinOrbits] = useState(() =>
    PARTICLE_SLOTS.map(() => ({
      theta: Math.floor(Math.random() * 360),
      phi: 55 + Math.random() * 40,
      // deg/second — kept in a narrow "slow but clearly alive" band.
      spinSpeed: 7 + Math.random() * 4,
    }))
  );
  // Coins render noticeably larger than the seller's bag icons (which stay
  // at PARTICLE_SLOTS' original small sizes) — big and confident, like the
  // reference's prominent berries, to sell the pop-out illusion.
  const COIN_SIZES = [72, 58, 66, 54, 62, 48];

  // Seller-side sale tags are rendered above the box (see the z-index logic
  // below) so they pop out in front of it. Their positions are defined after
  // SALE_TAG_SIZES further down, because they're placed by their CENTER.
  // Used to be a single shared orbit for every instance (this model has
  // "SALE" text baked into its texture, only legible from one specific
  // angle, so every tag looked visually identical/frozen). Now each
  // instance gets its own randomized starting theta/phi — close enough to
  // the original calibrated phi that the tag still reads as a tag, not
  // wildly upside-down — plus its own slow auto-rotate speed, same as the
  // coins above. Trade-off worth knowing: since they now continuously
  // spin, the "SALE" text won't stay front-facing/legible the whole
  // time — it'll swing in and out of view as each tag turns, rather than
  // holding a single frozen "legible" pose.
  const [saleTagOrbits] = useState(() =>
    PARTICLE_SLOTS.map(() => ({
      theta: Math.floor(Math.random() * 360),
      phi: 100 + Math.random() * 40,
      spinSpeed: 7 + Math.random() * 4,
    }))
  );
  // Bigger overall, and more spread between the smallest and largest so a
  // few clearly read as "closer" than the others — plus a per-instance
  // z-index stagger below reinforces that some are meant to sit in front.
  // Bumped ~50% up from the original [82, 56, 72, 48, 66, 40] — this is
  // just the container box each model-viewer renders into (always square,
  // width===height), so scaling it up magnifies the model uniformly with
  // no stretching/distortion; model-viewer's own "auto" framing (see
  // camera-orbit above) keeps the tag+stand's real proportions intact
  // regardless of box size.
  const SALE_TAG_SIZES = [123, 84, 108, 72, 99, 60];

  // Sale tags are placed by their CENTER, mirrored left/right of the box, so
  // every pair sits the same distance from it — like the buyer-side coins.
  // (They used to be anchored by their top-left corner, so the big tags on the
  // left had their centers pushed toward the box while the right-hand ones
  // floated far away: the whole ring looked like it was drifting right.)
  //
  // `dx` is the distance of the tag's center from the middle of the screen.
  // It is capped to a percentage of the width (454px = the layout this was
  // tuned on), so on a narrow phone the ring shrinks proportionally instead
  // of the outer tags sliding off-screen. Plain left/top (no transform), since
  // the pointer-repel animation owns `transform` on these elements.
  const tagSlot = (side: -1 | 1, dx: number, top: string, size: number) => ({
    top,
    left: `calc(50% ${side < 0 ? "-" : "+"} min(${dx}px, ${(
      (dx / 454) *
      100
    ).toFixed(1)}%) - ${size / 2}px)`,
  });
  const SALE_TAG_SLOTS_SELLER = [
    // upper pair — clear of the box, either side of the "Seller" headline
    tagSlot(-1, 150, "20%", SALE_TAG_SIZES[0]),
    tagSlot(1, 150, "18%", SALE_TAG_SIZES[1]),
    // middle pair — level with the box, so they sit furthest out
    tagSlot(-1, 182, "38%", SALE_TAG_SIZES[2]),
    tagSlot(1, 182, "42%", SALE_TAG_SIZES[3]),
    // lower pair — beside the box's bottom corners
    tagSlot(-1, 170, "60%", SALE_TAG_SIZES[4]),
    tagSlot(1, 170, "58%", SALE_TAG_SIZES[5]),
  ];

  const theme = THEME[displayedSide];

  // Load the display font used for the wordmark/headline, once.
  useEffect(() => {
    if (document.getElementById("entry-display-font-link")) return;
    const link = document.createElement("link");
    link.id = "entry-display-font-link";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;800&family=Manrope:wght@400;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  // The <model-viewer> script + .glb preloads already kicked off at module
  // load time, well before this component mounted (see
  // preloadEntryScreenAssets above). This call is just a cheap, idempotent
  // safety net in case that ever didn't run for some reason — every check
  // inside it bails out instantly if its tag already exists.
  useEffect(() => {
    preloadEntryScreenAssets();
  }, []);

  // Reveal each object only once it has actually finished loading (see the
  // comment on cardLoaded/boxLoaded above for why this matters). Retries
  // briefly in case the refs aren't attached to the upgraded custom element
  // yet on the very first effect pass.
  useEffect(() => {
    let cancelled = false;
    let cardAttached = false;
    let boxAttached = false;
    const onCardLoad = () => setCardLoaded(true);
    const onBoxLoad = () => setBoxLoaded(true);

    const attach = () => {
      if (cancelled) return;
      const cardEl = cardViewerRef.current;
      const boxEl = boxViewerRef.current;
      if (cardEl && !cardAttached) {
        cardEl.addEventListener("load", onCardLoad);
        cardAttached = true;
      }
      if (boxEl && !boxAttached) {
        boxEl.addEventListener("load", onBoxLoad);
        boxAttached = true;
      }
      if (!cardAttached || !boxAttached) {
        // Refs not ready yet (custom element still registering) — try again
        // shortly rather than silently never attaching.
        setTimeout(attach, 100);
      }
    };
    attach();

    return () => {
      cancelled = true;
      cardViewerRef.current?.removeEventListener?.("load", onCardLoad);
      boxViewerRef.current?.removeEventListener?.("load", onBoxLoad);
    };
  }, []);

  // Same gating as above, applied per-slot to the ambient coin and
  // sale-tag particles (see the comment on coinParticleRefs/tagParticleRefs
  // for why each slot mounts both models rather than swapping one `src`).
  useEffect(() => {
    let cancelled = false;
    const slotCount = PARTICLE_SLOTS.length;
    const coinAttached = new Array(slotCount).fill(false);
    const tagAttached = new Array(slotCount).fill(false);

    const markCoinLoaded = (i: number) => {
      setCoinLoadedFlags((prev) => {
        if (prev[i]) return prev;
        const next = [...prev];
        next[i] = true;
        return next;
      });
    };
    const markTagLoaded = (i: number) => {
      setTagLoadedFlags((prev) => {
        if (prev[i]) return prev;
        const next = [...prev];
        next[i] = true;
        return next;
      });
    };
    const coinHandlers = Array.from(
      { length: slotCount },
      (_, i) => () => markCoinLoaded(i)
    );
    const tagHandlers = Array.from(
      { length: slotCount },
      (_, i) => () => markTagLoaded(i)
    );

    const attach = () => {
      if (cancelled) return;
      let allAttached = true;
      for (let i = 0; i < slotCount; i++) {
        const coinEl = coinParticleRefs.current[i];
        const tagEl = tagParticleRefs.current[i];
        if (coinEl && !coinAttached[i]) {
          coinEl.addEventListener("load", coinHandlers[i]);
          coinAttached[i] = true;
        }
        if (tagEl && !tagAttached[i]) {
          tagEl.addEventListener("load", tagHandlers[i]);
          tagAttached[i] = true;
        }
        if (!coinAttached[i] || !tagAttached[i]) allAttached = false;
      }
      if (!allAttached) setTimeout(attach, 100);
    };
    attach();

    return () => {
      cancelled = true;
      for (let i = 0; i < slotCount; i++) {
        coinParticleRefs.current[i]?.removeEventListener?.(
          "load",
          coinHandlers[i]
        );
        tagParticleRefs.current[i]?.removeEventListener?.(
          "load",
          tagHandlers[i]
        );
      }
    };
  }, []);

  // Set the initial background theme variables once on mount.
  useEffect(() => {
    if (!heroRef.current) return;
    const t = THEME[selectedSide];
    heroRef.current.style.setProperty("--bg-inner", t.inner);
    heroRef.current.style.setProperty("--bg-mid", t.mid);
    heroRef.current.style.setProperty("--bg-outer", t.outer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track pointer (mouse move OR touch drag) anywhere on screen so the
  // object keeps tilting toward it even slightly outside the card itself.
  //
  // The listener attachment is deliberately delayed: this is a single-page
  // app, so navigating to this screen (e.g. tapping "Home" or a nav item)
  // doesn't reload the page — the same tap/click that triggered navigation
  // can leave a trailing pointer event that this screen's listener would
  // otherwise catch immediately on mount, producing an unwanted tilt/spin
  // before the user has actually touched anything here. Waiting briefly
  // guarantees the object starts at its clean resting angle every time.
  useEffect(() => {
    const handleMove = (e: PointerEvent) => {
      mouseRef.current.x = e.clientX / window.innerWidth - 0.5;
      mouseRef.current.y = e.clientY / window.innerHeight - 0.5;
      pointerRawRef.current.x = e.clientX;
      pointerRawRef.current.y = e.clientY;

      if (isDraggingRef.current) {
        const dx = e.clientX - lastDragPosRef.current.x;
        const dy = e.clientY - lastDragPosRef.current.y;
        lastDragPosRef.current = { x: e.clientX, y: e.clientY };
        // Pixel-to-degree sensitivity — tuned so a full swipe across a
        // typical phone width is roughly one full spin.
        manualOrbitRef.current.theta += dx * 0.5;
        manualOrbitRef.current.phi = Math.max(
          -60,
          Math.min(60, manualOrbitRef.current.phi + dy * 0.3)
        );
      }
    };
    const stopDragging = () => {
      isDraggingRef.current = false;
    };
    const attachTimeout = setTimeout(() => {
      window.addEventListener("pointermove", handleMove, { passive: true });
    }, 450);
    window.addEventListener("pointerup", stopDragging);
    window.addEventListener("pointercancel", stopDragging);
    return () => {
      clearTimeout(attachTimeout);
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", stopDragging);
      window.removeEventListener("pointercancel", stopDragging);
    };
  }, []);

  // Starts a manual spin drag — attached to the object itself so it
  // doesn't hijack swipes anywhere else on the hero (those still spawn
  // coin/bag bursts via the hero's own onPointerDown).
  const handleObjectPointerDown: React.PointerEventHandler<HTMLDivElement> = (
    e
  ) => {
    isDraggingRef.current = true;
    lastDragPosRef.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };

  // Main animation loop: tilts the 3D object toward the cursor/finger and
  // drifts + repels the ambient coin/bag particles away from it — paused
  // whenever a side-switch spin is playing so it never fights with GSAP.
  useEffect(() => {
    const animate = () => {
      currentMouseRef.current.x +=
        (mouseRef.current.x - currentMouseRef.current.x) * 0.08;
      currentMouseRef.current.y +=
        (mouseRef.current.y - currentMouseRef.current.y) * 0.08;

      const t = Date.now() * 0.001;
      const bob = Math.sin(t * 1.2) * 8;

      if (!isSwitchingRef.current) {
        // Bobbing float stays a plain CSS transform on the wrapper — it's
        // just vertical DOM movement, not a rotation, so there's nothing
        // "fake 3D" about faking it this way.
        if (objRef.current) {
          objRef.current.style.transform = `translateY(${bob}px)`;
        }
        // Real 3D tilt: feed the cursor position straight into each
        // <model-viewer>'s camera orbit (azimuth from x, polar from y) —
        // this actually re-renders the model from a new angle, unlike a
        // CSS transform which would just skew the flat rendered frame.
        // Each object tilts around its own resting angle (see BASE_ORBIT)
        // rather than a shared 0° — the card and box aren't framed the
        // same way by default. While a manual drag is in progress, the
        // absolute hover-tilt is suppressed so it can't fight the swipe —
        // only the accumulated drag rotation applies.
        const tiltTheta = isDraggingRef.current
          ? 0
          : currentMouseRef.current.x * 40;
        const tiltPhi = isDraggingRef.current
          ? 0
          : currentMouseRef.current.y * 20;
        const orbitTheta = tiltTheta + manualOrbitRef.current.theta;
        const orbitPhi = tiltPhi + manualOrbitRef.current.phi;
        if (cardViewerRef.current) {
          cardViewerRef.current.cameraOrbit = `${
            BASE_ORBIT.buyer.theta + orbitTheta
          }deg ${BASE_ORBIT.buyer.phi + orbitPhi}deg 140%`;
        }
        if (boxViewerRef.current) {
          boxViewerRef.current.cameraOrbit = `${
            BASE_ORBIT.seller.theta + orbitTheta
          }deg ${BASE_ORBIT.seller.phi + orbitPhi}deg 140%`;
        }
      }

      if (!isSwitchingRef.current) {
        particleRefs.current.forEach((el, i) => {
          if (!el) return;
          const rect = el.getBoundingClientRect();
          const cx = rect.left + rect.width / 2;
          const cy = rect.top + rect.height / 2;
          const dx = pointerRawRef.current.x - cx;
          const dy = pointerRawRef.current.y - cy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          let rxp = 0;
          let ryp = 0;
          const radius = 150;
          if (dist < radius && dist > 0.001) {
            const force = (radius - dist) / radius;
            rxp = (dx / dist) * force * -24;
            ryp = (dy / dist) * force * -24;
          }
          // No idle bobbing here anymore — particles now sit perfectly
          // still at rest (rxp/ryp both 0 when the pointer's out of
          // range) and only move via the repel force above when the
          // mouse is close enough to push them around their orbit.
          el.style.transform = `translate(${rxp}px, ${ryp}px)`;
        });
      }

      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      // Same safety net as in switchSide: if a hyperspin transition is
      // still mid-flight when this screen unmounts, its GSAP timeline
      // would otherwise keep ticking (or simply stop) without ever
      // reaching onComplete, leaving objRef's blur wherever it last was.
      // Kill it and force a clean reset so nothing carries over if this
      // screen is remounted later (e.g. after logging out and back in).
      switchTimelineRef.current?.kill();
      switchTimelineRef.current = null;
      if (switchSafetyTimeoutRef.current) {
        clearTimeout(switchSafetyTimeoutRef.current);
        switchSafetyTimeoutRef.current = null;
      }
      isSwitchingRef.current = false;
      if (objRef.current) {
        objRef.current.style.filter = "none";
        objRef.current.style.transform = "translateY(0px)";
      }
    };
  }, []);

  // SECOND safety net, independent of the 900ms timer above. Background
  // tabs get their timers throttled by the browser (sometimes to once a
  // minute or less) — so if a side-switch happens to be mid-flight right
  // as the tab is backgrounded, the 900ms `setTimeout` safety net can
  // itself be delayed well past 900ms, leaving the blur/low-res frame
  // sitting there for however long the tab stays hidden. `visibilitychange`
  // isn't subject to that throttling — it fires the instant the tab comes
  // back, so this catches exactly the case the timer-based net can miss.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && isSwitchingRef.current) {
        forceClearSwitchState();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  // Spawns a short-lived burst of coins (buyer) or bags (seller) from a
  // touch/click point — works identically for mouse clicks and mobile taps
  // since it's driven off the pointerdown event's coordinates.
  const spawnBurst = (clientX: number, clientY: number) => {
    const now = performance.now();
    if (now - lastBurstRef.current < 220) return; // throttle rapid taps
    lastBurstRef.current = now;
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const icon: "coin" | "bag" = displayedSide === "buyer" ? "coin" : "bag";
    const count = 7;
    const newOnes = Array.from({ length: count }).map(() => {
      const angle = Math.random() * Math.PI * 2;
      const dist = 55 + Math.random() * 65;
      return {
        id: burstIdRef.current++,
        x,
        y,
        tx: Math.cos(angle) * dist,
        ty: Math.sin(angle) * dist,
        icon,
      };
    });
    setBursts((prev) => [...prev, ...newOnes]);
    const ids = newOnes.map((n) => n.id);
    setTimeout(() => {
      setBursts((prev) => prev.filter((b) => !ids.includes(b.id)));
    }, 700);
  };

  const handleHeroPointerDown: React.PointerEventHandler<HTMLDivElement> = (
    e
  ) => {
    spawnBurst(e.clientX, e.clientY);
  };

  // The side-switch choreography: quick hyperspeed spin + motion blur,
  // icon/theme swap at the peak (hidden inside the blur), settling with a
  // smooth spring — while the whole background morphs to the new side's
  // color and the ambient particles implode/re-emerge as the new icon set.
  //
  // Shared by every place that can end a switch (normal completion, the
  // 900ms safety timeout, unmount cleanup, and the tab-visibility guard
  // below) so all four paths reset things identically. Beyond just zeroing
  // the CSS blur, this also nudges the two <model-viewer> canvases to
  // recompute their backing resolution: `<model-viewer>` renders through
  // its own internal canvas/WebGL context, and toggling `filter: blur()`
  // on its ancestor can leave that canvas sized/scaled for the blurred
  // frame even after the filter itself is set back to `none` — which
  // reads as the model staying soft/low-res rather than sharp, separately
  // from the blur value itself. Dispatching a `resize` is the standard
  // way to make a canvas-based renderer (model-viewer's included) re-check
  // its container and redraw at full resolution.
  const forceClearSwitchState = () => {
    switchTimelineRef.current?.kill();
    switchTimelineRef.current = null;
    if (switchSafetyTimeoutRef.current) {
      clearTimeout(switchSafetyTimeoutRef.current);
      switchSafetyTimeoutRef.current = null;
    }
    isSwitchingRef.current = false;
    setIsSwitching(false);
    if (objRef.current) {
      objRef.current.style.filter = "none";
      objRef.current.style.transform = "translateY(0px)";
    }
    // Let any in-flight onUpdate from the killed timeline settle before
    // forcing the resize nudge, so it isn't immediately overwritten.
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });
  };

  const switchSide = (nextSide: "buyer" | "seller") => {
    if (isSwitchingRef.current || nextSide === selectedSide) return;
    isSwitchingRef.current = true;
    setIsSwitching(true);
    setSelectedSide(nextSide);

    // Defensive: if a previous switch's timeline somehow never reached its
    // own onComplete (e.g. this screen got swapped out mid-spin), its last
    // written blur value would otherwise sit on objRef forever, since
    // nothing else ever resets it. Kill any stale tween and force the
    // blur back to 0 before starting a fresh one, so every switch begins
    // from a guaranteed-clean state.
    switchTimelineRef.current?.kill();
    if (objRef.current) objRef.current.style.filter = "none";
    if (switchSafetyTimeoutRef.current) {
      clearTimeout(switchSafetyTimeoutRef.current);
    }
    // Belt-and-suspenders on top of the kill-before-start above: no matter
    // what interrupts this specific run — a cause not yet identified, not
    // just the two already guarded against (overlapping switches, this
    // screen unmounting) — the blur can never visibly stick for longer
    // than this. The timeline's own onComplete (below) clears this same
    // timeout the instant it finishes normally, so on the ordinary path
    // this timer never actually fires; it only matters when something
    // unexpected stalls the animation.
    switchSafetyTimeoutRef.current = setTimeout(() => {
      forceClearSwitchState();
      setStrokeLabel(nextSide);
    }, 900);

    // A manual spin in progress shouldn't carry into the automatic
    // hyperspin transition — start every switch from a clean base.
    isDraggingRef.current = false;
    manualOrbitRef.current = { theta: 0, phi: 0 };

    const target = THEME[nextSide];
    if (heroRef.current) {
      gsap.to(heroRef.current, {
        duration: 0.6,
        ease: "power2.inOut",
        "--bg-inner": target.inner,
        "--bg-mid": target.mid,
        "--bg-outer": target.outer,
      } as any);
    }

    const spin = { val: 0, blur: 0 };

    // Freeze the bob at neutral for the duration of the spin — the main
    // animate loop won't touch objRef again until isSwitchingRef clears.
    if (objRef.current) objRef.current.style.transform = "translateY(0px)";

    const tiltTheta = currentMouseRef.current.x * 40;
    const tiltPhi = currentMouseRef.current.y * 20;

    const applyOrbit = () => {
      if (cardViewerRef.current) {
        cardViewerRef.current.cameraOrbit = `${
          BASE_ORBIT.buyer.theta + tiltTheta + spin.val
        }deg ${BASE_ORBIT.buyer.phi + tiltPhi}deg 140%`;
      }
      if (boxViewerRef.current) {
        boxViewerRef.current.cameraOrbit = `${
          BASE_ORBIT.seller.theta + tiltTheta + spin.val
        }deg ${BASE_ORBIT.seller.phi + tiltPhi}deg 140%`;
      }
      if (objRef.current) objRef.current.style.filter = `blur(${spin.blur}px)`;
    };

    switchTimelineRef.current = gsap
      .timeline({
        onComplete: () => {
          forceClearSwitchState();
          // The hyperspin has now fully closed (not merely stopped mid-
          // flight) — safe to reveal the new label and let its stroke
          // draw itself on.
          setStrokeLabel(nextSide);
        },
      })
      .to(spin, {
        val: 180,
        blur: 14,
        duration: 0.2,
        ease: "power2.in",
        onUpdate: applyOrbit,
        onComplete: () => setDisplayedSide(nextSide),
      })
      .to(spin, {
        val: 360,
        blur: 0,
        duration: 0.32,
        ease: "back.out(1.6)",
        onUpdate: applyOrbit,
      });

    // Ambient coin/bag particles implode to nothing, then re-emerge —
    // their icon flips automatically because they render off
    // `displayedSide`, which swaps mid-flight above.
    particleRefs.current.forEach((el) => {
      if (!el) return;
      gsap
        .timeline()
        .to(el, {
          scale: 0.15,
          opacity: 0,
          duration: 0.16,
          ease: "power2.in",
        })
        .to(el, { duration: 0.06 })
        .to(el, {
          scale: 1,
          opacity: 1,
          duration: 0.3,
          ease: "back.out(1.7)",
        });
    });
  };

  const enterSide = () => {
    if (isSwitching) return;
    if (selectedSide === "buyer") {
      runTileTransition(handleBuyerClick, "#1006C5");
    } else {
      runSellerEnterTransition();
    }
  };

  return (
    <div
      ref={heroRef}
      onPointerDown={handleHeroPointerDown}
      className="h-screen w-full text-white flex flex-col p-6 relative max-w-md mx-auto shadow-2xl overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 50% 40%, var(--bg-inner) 0%, var(--bg-mid) 50%, var(--bg-outer) 100%)",
        transition: "background 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        fontFamily: "'Manrope', sans-serif",
      }}
    >
      <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-4px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn { animation: fadeIn 0.25s ease-out; }
          @keyframes burstOut {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(1); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(var(--tx), var(--ty)) scale(0.4); opacity: 0; }
          }
          .burst-particle {
            position: absolute;
            animation: burstOut 0.65s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
          .entry-pill { transition: all 0.25s ease; }
          .enter-btn { transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.3s ease, opacity 0.3s ease; }
          .enter-btn:active { transform: scale(0.97); }
        `}</style>

      {/* Ambient coin/bag particle layer, spans the whole hero. Both sides
          now render real 3D models above their object (z-30) so they pop
          out in front of it rather than risk sitting behind it. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 30 }}
      >
        {PARTICLE_SLOTS.map((_slot, i) => {
          const isBuyer = displayedSide === "buyer";
          // Position/size still switch instantly off `isBuyer` — that part
          // was never buggy, it's just CSS. Only the 3D model itself is
          // now split into two permanently-mounted instances below.
          const pos = isBuyer ? COIN_SLOTS_BUYER[i] : SALE_TAG_SLOTS_SELLER[i];
          const size = isBuyer ? COIN_SIZES[i] : SALE_TAG_SIZES[i];
          const coinOrbit = coinOrbits[i];
          const saleTagOrbit = saleTagOrbits[i];
          return (
            <div
              key={i}
              ref={(el) => (particleRefs.current[i] = el)}
              className="absolute"
              style={{
                top: pos.top,
                left: pos.left,
                width: size,
                height: size,
                // Bigger instances render on top — a simple, cheap way to
                // sell "some are closer than others" without real 3D depth
                // sorting between separate model-viewer instances.
                zIndex: 30 + Math.round(size / 4),
                opacity: 0.96,
                filter: `drop-shadow(0 12px 22px rgba(0,0,0,0.55)) drop-shadow(0 4px 10px ${theme.accent}70)`,
              }}
            >
              {/* Gold coin instance — stays mounted with a fixed `src` the
                  whole time (never swapped), gated only by opacity. Kept
                  ready even while the seller side is showing, so flipping
                  back to Buyer never has to wait on it either. */}
              <model-viewer
                ref={(el: any) => (coinParticleRefs.current[i] = el)}
                src={GOLD_COIN_GLB_URL}
                camera-controls={true}
                disable-zoom
                disable-pan
                interaction-prompt="none"
                auto-rotate
                auto-rotate-delay="0"
                rotation-per-second={`${coinOrbit.spinSpeed.toFixed(2)}deg`}
                exposure="1.1"
                shadow-intensity="0"
                environment-image="neutral"
                camera-orbit={`${coinOrbit.theta}deg ${coinOrbit.phi}deg 120%`}
                field-of-view="30deg"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  opacity: isBuyer && coinLoadedFlags[i] ? 1 : 0,
                  transition: "opacity 0.05s linear",
                  "--poster-color": "transparent",
                  "--progress-bar-color": "transparent",
                }}
              />
              {/* Sale tag instance — same idea, fixed `src`, own gated
                  opacity. This is the half that fixes the seller-side
                  glitch: since it's been loading in the background since
                  the page opened (never fetched on demand at click time),
                  tapping "Seller" right after a reload shows it
                  immediately instead of showing stale coins for a few
                  seconds while a fresh sale-tag fetch completes. */}
              <model-viewer
                ref={(el: any) => (tagParticleRefs.current[i] = el)}
                src={SALE_TAG_GLB_URL}
                camera-controls={true}
                disable-zoom
                disable-pan
                interaction-prompt="none"
                auto-rotate
                auto-rotate-delay="0"
                rotation-per-second={`${saleTagOrbit.spinSpeed.toFixed(2)}deg`}
                exposure="1.1"
                shadow-intensity="0"
                environment-image="neutral"
                // "auto" (instead of a fixed distance like "70%") always
                // frames the model's FULL bounding box — stand included —
                // no matter which theta/phi it's currently spun to. A
                // fixed distance only fits the whole model at the exact
                // angle it was tuned for; at other angles (which is most
                // of them, now that these spin) the tall stand pokes
                // outside the frame and gets clipped, which is why some
                // instances were showing tag+stand and others just the
                // tag.
                camera-orbit={`${saleTagOrbit.theta}deg ${saleTagOrbit.phi}deg auto`}
                field-of-view="30deg"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  opacity: !isBuyer && tagLoadedFlags[i] ? 1 : 0,
                  transition: "opacity 0.05s linear",
                  "--poster-color": "transparent",
                  "--progress-bar-color": "transparent",
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Burst particles (coins/bags scattering from touch) */}
      <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
        {bursts.map((b) => {
          const BIcon = b.icon === "coin" ? Coins : ShoppingBag;
          return (
            <div
              key={b.id}
              className="burst-particle"
              style={
                {
                  left: b.x,
                  top: b.y,
                  "--tx": `${b.tx}px`,
                  "--ty": `${b.ty}px`,
                  color:
                    b.icon === "coin"
                      ? THEME.buyer.accent
                      : THEME.seller.accent,
                } as React.CSSProperties
              }
            >
              <BIcon size={16} />
            </div>
          );
        })}
      </div>

      <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/60 pointer-events-none z-0" />

      {/* HEADER: wordmark + Buyer/Seller switcher pills */}
      <div className="relative z-30 flex items-center justify-between pt-2">
        <div className="flex items-center gap-2">
          <svg
            width="30"
            height="25"
            viewBox="0 0 110 90"
            className="shrink-0"
            aria-hidden="true"
          >
            <circle cx="42" cy="45" r="30" fill="#1006C5" />
            <path d="M72 18 L98 45 L72 72 L46 45 Z" fill="#FFB800" />
          </svg>
          <div className="text-left">
            <h1
              className="text-lg tracking-wide leading-none"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}
            >
              INUA
            </h1>
            <p className="text-[8px] tracking-[0.2em] text-[#8888A8] font-mono">
              MARKETPLACE
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div
            className="flex gap-1.5 p-1 rounded-full"
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
              backdropFilter: "blur(10px)",
            }}
          >
            {(["buyer", "seller"] as const).map((side) => {
              const t = THEME[side];
              const Icon = t.icon;
              const active = selectedSide === side;
              return (
                <button
                  key={side}
                  onClick={() => switchSide(side)}
                  disabled={isSwitching}
                  className="entry-pill flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-bold"
                  style={{
                    background: active ? `${t.accent}2a` : "transparent",
                    color: active ? t.accent : "rgba(255,255,255,0.55)",
                  }}
                >
                  <Icon size={12} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* MEGA MENU TRIGGER — same translucent chip styling as the
              buyer/seller pill above, so it reads as part of the same
              header group rather than a bolted-on extra. Shared by both
              sides since it sits here in the persistent header. */}
          <button
            type="button"
            onClick={() => setIsMegaMenuOpen(true)}
            aria-label="Open menu"
            aria-expanded={isMegaMenuOpen}
            className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-colors"
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Menu size={14} />
          </button>
        </div>
      </div>

      <AppMegaMenu
        isOpen={isMegaMenuOpen}
        onClose={() => setIsMegaMenuOpen(false)}
      />

      {/* CENTER: big headline + 3D object + sub headline */}
      <div className="relative z-30 flex-1 flex flex-col items-center justify-center text-center gap-2">
        <div
          className="relative z-10 w-full"
          style={{
            filter: `drop-shadow(0 0 40px ${theme.accent}88)`,
            transition: "filter 0.4s ease",
          }}
        >
          <div style={{ width: "100%", height: 140 }}>
            <div
              className="w-full h-full flex items-center justify-center"
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 800,
                fontSize: "clamp(2.75rem, 11vw, 4.5rem)",
                color: "#ffffff",
                lineHeight: 1,
              }}
            >
              <BlurText
                key={strokeLabel}
                text={THEME[strokeLabel].label}
                delay={70}
                animateBy="letters"
                direction="top"
                className="justify-center"
              />
            </div>
          </div>
        </div>

        {/* Radial glow behind the object — reinforces the "about to come
            through the screen" pop-out illusion together with the strong
            drop-shadow below. */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 340,
            height: 340,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.accent}55 0%, transparent 70%)`,
            filter: "blur(20px)",
            transition: "background 0.4s ease",
            zIndex: 15,
          }}
        />

        <div
          ref={objRef}
          onPointerDown={handleObjectPointerDown}
          className="relative z-20 -mt-14 w-72 h-72"
          style={{
            filter: `drop-shadow(0 30px 45px ${theme.accent}70) drop-shadow(0 8px 14px rgba(0,0,0,0.5))`,
            touchAction: "none",
            cursor: "grab",
          }}
        >
          {/* Credit card — visible on the buyer side */}
          <model-viewer
            ref={cardViewerRef}
            src={CREDIT_CARD_GLB_URL}
            camera-controls={false}
            disable-zoom
            disable-pan
            interaction-prompt="none"
            exposure="1.2"
            shadow-intensity="0.7"
            environment-image="neutral"
            camera-orbit="180deg 90deg 140%"
            field-of-view="30deg"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              opacity: displayedSide === "buyer" && cardLoaded ? 1 : 0,
              pointerEvents: "none",
              transition: "opacity 0.05s linear",
              "--poster-color": "transparent",
              "--progress-bar-color": "transparent",
            }}
          />
          {/* Delivery box — visible on the seller side */}
          <model-viewer
            ref={boxViewerRef}
            src={DELIVERY_BOX_GLB_URL}
            camera-controls={false}
            disable-zoom
            disable-pan
            interaction-prompt="none"
            exposure="1.2"
            shadow-intensity="0.7"
            environment-image="neutral"
            camera-orbit="90deg 60deg 140%"
            field-of-view="30deg"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              opacity: displayedSide === "seller" && boxLoaded ? 1 : 0,
              pointerEvents: "none",
              transition: "opacity 0.05s linear",
              "--poster-color": "transparent",
              "--progress-bar-color": "transparent",
            }}
          />
        </div>

        <h3
          className="text-2xl leading-tight"
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 600,
            color: theme.soft,
          }}
        >
          {theme.sub}
        </h3>
        <p className="text-xs text-[#8888A8] max-w-[260px] leading-relaxed mt-1">
          {theme.description}
        </p>
      </div>

      {/* FOOTER: Enter CTA */}
      <div className="relative z-30 pb-4">
        <button
          onClick={enterSide}
          disabled={isSwitching}
          className="enter-btn w-full flex items-center justify-center gap-3 py-4 rounded-full font-bold text-sm"
          style={{
            background: theme.accent,
            color: "#ffffff",
            opacity: isSwitching ? 0.6 : 1,
          }}
        >
          Enter
          <span
            className="w-7 h-7 rounded-full flex items-center justify-center text-base font-black"
            style={{ background: "rgba(255,255,255,0.25)" }}
          >
            +
          </span>
        </button>

        {displayedSide === "seller" && showSellerLockedNote && (
          <div className="w-full mt-3 animate-fadeIn">
            <p
              className="text-xs text-center rounded-xl px-4 py-3 leading-relaxed"
              style={{
                color: theme.accent,
                background: `${theme.accent}14`,
                border: `1px solid ${theme.accent}33`,
              }}
            >
              🔒 Please sign in as a buyer first — that unlocks seller access.
            </p>
          </div>
        )}

        {/* Copyright + public legal links on ONE row (like a site footer), so
            the copyright adds no extra height and the page layout above it
            doesn't move. flex-wrap only matters on very narrow phones, where
            it drops to a second line instead of overflowing. The links are
            plain links to the static pages in /public/privacy and
            /public/terms (open in a new tab so the chooser keeps its state).
            Same on both sides on purpose. */}
        <div className="mt-4 text-center">
          <p className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-[11px] text-[#8888A8]">
            <span className="text-[10px] text-[#7C7CA0]">
              © {new Date().getFullYear()} INUA Marketplace
            </span>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-white"
            >
              Privacy Policy
            </a>
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-white"
            >
              Terms of Use
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

// --- ADDRESS-BAR ROUTES + BROWSER BACK BUTTON ---
// The app is one page that swaps screens with state. This section gives each
// screen its own address (so the bar reads inuamarketplace.com/home,
// /listings/sneaker-cleaning-1a2b3c4d, ...) and a browser history entry
// (so Back / a phone's back swipe steps through the app instead of leaving
// the site).
//
//   /                    landing page          /home         buyer home
//   /sign-in             buyer sign-in         /categories   explore tab
//   /bookings            bookings tab          /account      account tab
//   /category/<id>[/<sub>]  a category page    /store/<code> a seller's store
//   /listings/<slug>     a listing (opens over whatever screen is underneath)
//   /seller, /seller/apply, /seller/submitted   address only (see below)
//
// - A listing's address is its title plus the first 8 characters of its id.
//   The id part is what makes two sellers' "Sneaker cleaning" listings
//   different addresses; the page finds the listing by it.
// - Opening an address directly (a refresh, or a shared link): buyer screens
//   open straight away when signed in. Signed out, the visitor lands on the
//   start page and the address is remembered for 30 minutes, so after
//   signing in they arrive where the link pointed. Seller addresses show in
//   the bar but a refresh still starts at the landing page (on the Seller
//   side), exactly as before.
// - Each entry's state is just { inua: <session>, idx }; the full screen
//   snapshots (including the product object) are kept in memory, keyed by idx.
// - Switching between the four bottom tabs REPLACES the entry instead of
//   adding one, so Back leaves the tab group rather than walking every tab.
// - "category-transition" is only an animation step and never gets an entry.
// - The photo viewer pushes its own entry ({ lightboxOpen }); those have no
//   `inua` marker and are ignored here, so Back still just closes the viewer.
// - Signing out starts a fresh session id, so entries from the signed-in
//   session can't restore screens that need an account; they land on "entry".
type NavSnapshot = {
  screen: string;
  product: ProductListing | null;
  storeCode: string | null;
  category: any;
  subcategory: { id: string; name: string } | null;
};
type NavEntry = { snapshot: NavSnapshot; key: string };

const NAV_UNTRACKED_SCREENS = ["category-transition"];
const NAV_TAB_SCREENS = [
  "buyer-home",
  "buyer-categories",
  "buyer-orders",
  "buyer-profile",
];
const SCREEN_PATHS: Record<string, string> = {
  entry: "/",
  "buyer-auth": "/sign-in",
  "buyer-home": "/home",
  "buyer-categories": "/categories",
  "buyer-orders": "/bookings",
  "buyer-profile": "/account",
  "seller-portal": "/seller",
  "seller-flow": "/seller/apply",
  "seller-success": "/seller/submitted",
};

function slugifyForUrl(text: unknown): string {
  return String(text ?? "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48)
    .replace(/-+$/g, "");
}

function listingShortId(id: unknown): string {
  return String(id ?? "")
    .replace(/-/g, "")
    .toLowerCase()
    .slice(0, 8);
}

function listingUrlSlug(product: any): string {
  return `${slugifyForUrl(product?.title) || "listing"}-${listingShortId(
    product?.id
  )}`;
}

function listingShortIdFromSlug(slug: string): string | null {
  const m = slug.toLowerCase().match(/-([0-9a-f]{4,8})$/);
  return m ? m[1] : null;
}

function listingMatchesShortId(listing: any, shortId: string): boolean {
  const full = String(listing?.id ?? "")
    .replace(/-/g, "")
    .toLowerCase();
  return full.length > 8 ? full.startsWith(shortId) : full === shortId;
}

function navSnapshotPath(s: NavSnapshot): string {
  if (s.product) return `/listings/${listingUrlSlug(s.product)}`;
  if (s.screen === "store" && s.storeCode) return `/store/${s.storeCode}`;
  if (s.screen === "category" && s.category?.id) {
    const sub = s.subcategory?.id
      ? `/${encodeURIComponent(String(s.subcategory.id))}`
      : "";
    return `/category/${encodeURIComponent(String(s.category.id))}${sub}`;
  }
  return SCREEN_PATHS[s.screen] || "/";
}

// Keeps the ?debugNewBuyer=1 testing switch alive across screen changes.
function navKeepSearch(): string {
  return typeof window !== "undefined" &&
    window.location.search.includes("debugNewBuyer=1")
    ? "?debugNewBuyer=1"
    : "";
}

function navSnapshotKey(s: NavSnapshot): string {
  return [
    s.screen,
    (s.product as any)?.id ?? "",
    s.screen === "store" ? s.storeCode ?? "" : "",
    s.category?.id ?? "",
    s.subcategory?.id ?? "",
  ].join("|");
}

function newNavSession(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// What a directly-opened address means. `null` = not a buyer address (the
// landing page, a store link, seller addresses, anything unknown).
type InitialRoute = {
  path: string;
  screen: string;
  productShortId: string | null;
  category: any;
  subcategory: { id: string; name: string } | null;
};

function parseBuyerRoute(pathname: string): InitialRoute | null {
  const path = (pathname || "").replace(/\/+$/, "") || "/";
  const base = { path, productShortId: null, category: null, subcategory: null };
  const tabs: Record<string, string> = {
    "/home": "buyer-home",
    "/categories": "buyer-categories",
    "/bookings": "buyer-orders",
    "/account": "buyer-profile",
  };
  if (tabs[path]) return { ...base, screen: tabs[path] };

  const listing = path.match(/^\/listings\/([A-Za-z0-9-]+)$/);
  if (listing) {
    return {
      ...base,
      screen: "buyer-home",
      productShortId: listingShortIdFromSlug(listing[1]),
    };
  }

  const cat = path.match(/^\/category\/([^/]+)(?:\/([^/]+))?$/);
  if (cat) {
    try {
      const category = (CATEGORIES as any)[decodeURIComponent(cat[1]).toLowerCase()];
      if (!category) return { ...base, screen: "buyer-categories" };
      const subId = cat[2] ? decodeURIComponent(cat[2]) : "";
      const subcategory =
        (category.subcategories || []).find(
          (s: any) => String(s.id) === subId
        ) || null;
      return { ...base, screen: "category", category, subcategory };
    } catch {
      return { ...base, screen: "buyer-categories" };
    }
  }
  return null;
}

// A buyer address opened while signed out is remembered across the Google
// sign-in redirect (which reloads the page at "/") so the visitor ends up
// where the link pointed. Expires like the pending store link does.
const PENDING_ROUTE_KEY = "pending_deep_link";
const PENDING_ROUTE_TTL_MS = 30 * 60 * 1000;

function setPendingDeepLink(path: string) {
  try {
    localStorage.setItem(
      PENDING_ROUTE_KEY,
      JSON.stringify({ path, ts: Date.now() })
    );
  } catch {}
}

function readPendingDeepLink(): string | null {
  try {
    const raw = localStorage.getItem(PENDING_ROUTE_KEY);
    if (!raw) return null;
    const { path, ts } = JSON.parse(raw);
    if (!path || Date.now() - ts > PENDING_ROUTE_TTL_MS) {
      localStorage.removeItem(PENDING_ROUTE_KEY);
      return null;
    }
    return String(path);
  } catch {
    return null;
  }
}

function clearPendingDeepLink() {
  try {
    localStorage.removeItem(PENDING_ROUTE_KEY);
  } catch {}
}

function useBackButtonHistory(opts: {
  screen: string;
  product: ProductListing | null;
  storeCode: string | null;
  category: any;
  subcategory: { id: string; name: string } | null;
  // While true nothing is recorded and the address bar is left alone (used
  // while a directly-opened address is still being resolved).
  paused: boolean;
  onRestore: (snapshot: NavSnapshot, leavingScreen: string) => void;
}) {
  const screen = opts.screen;
  const paused = opts.paused;
  const snapshot: NavSnapshot = {
    screen,
    product: opts.product,
    storeCode: opts.storeCode,
    category: opts.category,
    subcategory: opts.subcategory,
  };
  const key = navSnapshotKey(snapshot);
  const latestRef = useRef({ snapshot, key, onRestore: opts.onRestore });
  latestRef.current = { snapshot, key, onRestore: opts.onRestore };
  const sessionRef = useRef(newNavSession());
  const idxRef = useRef(0);
  const entriesRef = useRef<Record<number, NavEntry>>({});
  const readyRef = useRef(false);
  const [resetTick, setResetTick] = useState(0);

  // Record every screen change as a history entry, and show its address.
  useEffect(() => {
    if (
      paused ||
      typeof window === "undefined" ||
      NAV_UNTRACKED_SCREENS.includes(screen)
    )
      return;
    const { snapshot: snap, key: snapKey } = latestRef.current;
    const entries = entriesRef.current;
    const mark = (idx: number, replace: boolean) => {
      const state = { inua: sessionRef.current, idx };
      const url = navSnapshotPath(snap) + navKeepSearch();
      try {
        if (replace) window.history.replaceState(state, "", url);
        else window.history.pushState(state, "", url);
      } catch {}
    };
    if (!readyRef.current) {
      // First tracked screen: tag the entry the browser already has (and
      // tidy its address), so Back from here still leaves the site.
      readyRef.current = true;
      entries[idxRef.current] = { snapshot: snap, key: snapKey };
      mark(idxRef.current, true);
      return;
    }
    const here = entries[idxRef.current];
    // Same screen as the current entry: nothing changed, or we just arrived
    // here through Back/Forward.
    if (here && here.key === snapKey) return;
    const tabToTab =
      !!here &&
      NAV_TAB_SCREENS.includes(here.snapshot.screen) &&
      NAV_TAB_SCREENS.includes(snap.screen) &&
      !here.snapshot.product &&
      !snap.product;
    if (tabToTab) {
      entries[idxRef.current] = { snapshot: snap, key: snapKey };
      mark(idxRef.current, true);
      return;
    }
    // A new forward step drops any "forward" entries, like the browser does.
    Object.keys(entries).forEach((k) => {
      if (Number(k) > idxRef.current) delete entries[Number(k)];
    });
    idxRef.current += 1;
    entries[idxRef.current] = { snapshot: snap, key: snapKey };
    mark(idxRef.current, false);
  }, [paused, screen, key, resetTick]);

  // Back / Forward: put the app back on the screen that entry recorded (the
  // browser has already put that entry's address in the bar).
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      const st: any = e.state;
      if (!st || !st.inua) return; // not ours (e.g. the photo viewer's entry)
      const sameSession = st.inua === sessionRef.current;
      if (sameSession && st.idx === idxRef.current) return; // same entry
      const leaving = latestRef.current.snapshot.screen;
      const target = sameSession ? entriesRef.current[st.idx] : undefined;
      if (target) {
        idxRef.current = st.idx;
        latestRef.current.onRestore(target.snapshot, leaving);
        return;
      }
      // An entry this session knows nothing about (from before a sign-out):
      // land on the start screen and adopt the entry as that screen.
      const fallback: NavSnapshot = {
        screen: "entry",
        product: null,
        storeCode: null,
        category: null,
        subcategory: null,
      };
      const next =
        Math.max(
          idxRef.current,
          ...Object.keys(entriesRef.current).map(Number)
        ) + 1;
      idxRef.current = next;
      entriesRef.current[next] = {
        snapshot: fallback,
        key: navSnapshotKey(fallback),
      };
      try {
        window.history.replaceState(
          { inua: sessionRef.current, idx: next },
          "",
          navSnapshotPath(fallback) + navKeepSearch()
        );
      } catch {}
      latestRef.current.onRestore(fallback, leaving);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Sign-out: forget the signed-in entries (see the note above).
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_OUT") return;
      sessionRef.current = newNavSession();
      entriesRef.current = {};
      idxRef.current = 0;
      readyRef.current = false;
      setResetTick((n) => n + 1);
    });
    return () => subscription.unsubscribe();
  }, []);

  // The product screen's on-screen back arrow: when the entry before this
  // one is the screen underneath, step back through the browser history so
  // the arrow and the browser's Back button stay in sync. Returns false when
  // there is no such entry, and the caller just closes the product itself.
  const goBackFromProduct = React.useCallback((): boolean => {
    const prev = entriesRef.current[idxRef.current - 1];
    if (readyRef.current && idxRef.current > 0 && prev && !prev.snapshot.product) {
      window.history.back();
      return true;
    }
    return false;
  }, []);

  return { goBackFromProduct };
}

export default function App() {
  // --- BOOT PRELOADER ---
  // Covers the screen from the very first paint (a page reload always
  // starts here, regardless of `currentScreen`) while EntryScreen mounts
  // and its .glb models load underneath, hidden. `bootAssetsReady` is set
  // by EntryScreen once the card + box models it displays have actually
  // fired their `load` events (see onAssetsReady below) — the preloader
  // holds on its last word until this flips true, so the wipe never
  // reveals the entry screen before its models are actually in place.
  // A store link (/store/<code>) skips the entry screen entirely, so the
  // preloader must not wait for EntryScreen's models in that case.
  const [initialStoreCode] = useState<string | null>(() =>
    getInitialStoreCode()
  );
  const [storeCode, setStoreCode] = useState<string | null>(initialStoreCode);
  // A buyer address opened directly (see parseBuyerRoute) or remembered from
  // before sign-in. The screen opens optimistically while the preloader is
  // still covering everything; a quick session check then either confirms it
  // or sends a signed-out visitor to the landing page.
  const [initialRoute] = useState<InitialRoute | null>(() => {
    if (typeof window === "undefined" || initialStoreCode) return null;
    return (
      parseBuyerRoute(window.location.pathname) ||
      parseBuyerRoute(readPendingDeepLink() || "")
    );
  });
  const [routePending, setRoutePending] = useState(!!initialRoute);
  const [routeSessionOk, setRouteSessionOk] = useState(false);
  const [showBootPreloader, setShowBootPreloader] = useState(true);
  // Like a store link, a direct buyer address skips the entry screen, so the
  // preloader must not wait for EntryScreen's models.
  const [bootAssetsReady, setBootAssetsReady] = useState(
    !!initialStoreCode || !!initialRoute
  );

  // --- THEME (dark / light) ---
  const [theme, setTheme] = useState<ThemeName>(getInitialTheme);
  const toggleTheme = () => {
    setTheme((prev) => {
      const next: ThemeName = prev === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // Storage can fail (private browsing, quota) — theme just won't
        // persist across reloads, the toggle itself still works fine.
      }
      return next;
    });
  };

  // Our theme colours only live on the CSS vars scoped to this component's
  // own root div (`data-theme="..."` set below). The raw <html>/<body>
  // behind it are still whatever the page's base stylesheet left them —
  // often plain white, sometimes an old brand-colour accent from a
  // <meta name="theme-color"> tag. On mobile browsers, rubber-band/elastic
  // overscroll at the very top or edges of the page can briefly reveal
  // that mismatched colour as a thin coloured line before the bounce
  // settles back — which is exactly the stray line seen along the top/
  // edges of screenshots. Keeping <html>/<body> synced to the current
  // theme's own app background makes any such peek-through blend in
  // instead of standing out, regardless of what the base stylesheet set.
  useEffect(() => {
    const bg = theme === "dark" ? "#060609" : "#F0F2F5";
    const prevHtmlBg = document.documentElement.style.backgroundColor;
    const prevBodyBg = document.body.style.backgroundColor;
    const prevHtmlOverscroll = (document.documentElement.style as any)
      .overscrollBehaviorY;
    const prevBodyOverscroll = (document.body.style as any).overscrollBehaviorY;
    const prevBodyMargin = document.body.style.margin;
    document.documentElement.style.backgroundColor = bg;
    document.body.style.backgroundColor = bg;
    (document.documentElement.style as any).overscrollBehaviorY = "none";
    (document.body.style as any).overscrollBehaviorY = "none";
    document.body.style.margin = "0";
    return () => {
      document.documentElement.style.backgroundColor = prevHtmlBg;
      document.body.style.backgroundColor = prevBodyBg;
      (document.documentElement.style as any).overscrollBehaviorY =
        prevHtmlOverscroll;
      (document.body.style as any).overscrollBehaviorY = prevBodyOverscroll;
      document.body.style.margin = prevBodyMargin;
    };
  }, [theme]);

  // --- STATE MANAGEMENT ---
  const [currentScreen, setCurrentScreen] = useState(
    initialStoreCode ? "store" : initialRoute ? initialRoute.screen : "entry"
  );
  // Which side EntryScreen should land on next time it (re)mounts — set
  // right before navigating back to "entry" from wherever the person
  // actually came from (buyer screens set "buyer", the seller dashboard's
  // Home button sets "seller"), rather than always defaulting to Buyer.
  const [entrySide, setEntrySide] = useState<"buyer" | "seller">(() =>
    typeof window !== "undefined" && window.location.pathname.startsWith("/seller")
      ? "seller"
      : "buyer"
  );
  const [selectedCounty, setSelectedCounty] = useState(ALL_COUNTIES_LABEL);
  const [selectedCategory, setSelectedCategory] = useState<any>(
    initialRoute?.category ?? null
  );
  const [selectedSubcategory, setSelectedSubcategory] = useState<{
    id: string;
    name: string;
  } | null>(initialRoute?.subcategory ?? null);

  // Stairs preloader: fires when a buyer hits "Explore" on a category card.
  // The real category listing mounts immediately underneath (so its data
  // starts loading right away); this overlay just plays on top for a beat
  // then clears itself via onComplete.
  const [stairsActive, setStairsActive] = useState(false);
  const [stairsCategory, setStairsCategory] = useState<any>(null);

  // `allListings` is whatever the database sent; `listings` (what every screen
  // reads) leaves out sub-categories that are switched off in categoryTheme.ts
  // (currently Baby Sitting and Pet Sitting). Filtering here, once, covers the
  // home feed, category pages, search and the product view alike.
  const [allListings, setListings] = React.useState<ListingRow[]>([]);
  const listings = React.useMemo(
    () => allListings.filter((l) => !isListingHiddenFromMarketplace(l)),
    [allListings]
  );
  const [isLoading, setIsLoading] = React.useState(true);
  const [selectedProduct, setSelectedProduct] = useState<ProductListing | null>(
    null
  );

  // Browser Back / phone back-swipe support — see useBackButtonHistory.
  const { goBackFromProduct } = useBackButtonHistory({
    screen: currentScreen,
    product: selectedProduct,
    storeCode,
    category: selectedCategory,
    subcategory: selectedSubcategory,
    paused: routePending,
    onRestore: (snap, leavingScreen) => {
      // Back to the landing page should reopen on the side we came from,
      // the same way navigateTo("entry") does.
      if (snap.screen === "entry") {
        setEntrySide(leavingScreen.startsWith("seller") ? "seller" : "buyer");
      }
      setSelectedProduct(snap.product);
      setStoreCode(snap.storeCode);
      setSelectedCategory(snap.category);
      setSelectedSubcategory(snap.subcategory);
      setCurrentScreen(snap.screen);
    },
  });

  // App-wide toast replacing native browser alert() popups — same visual
  // language as the rest of the app (e.g. the seller dashboard's toasts).
  // Auto-dismisses on its own.
  const [appToast, setAppToast] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showAppToast = (
    toast: { title: string; message: string },
    durationMs = 6000
  ) => {
    setAppToast(toast);
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => {
      setAppToast(null);
    }, durationMs);
  };

  // --- ENTRY-SCREEN AUTH GATE (Seller access requires a buyer session first) ---
  const [hasSession, setHasSession] = useState(false);
  const [showSellerLockedNote, setShowSellerLockedNote] = useState(false);

  // --- TILE PAGE TRANSITION (blue tile wipe) ---
  // Generic runner: tiles sweep in to fully cover the screen, then the
  // supplied `action` (a navigation call, usually) runs hidden underneath,
  // then the tiles retract to reveal wherever `action` navigated to.
  // Used by: the buyer "Enter" button, any "back to Buyer/Seller selection"
  // button, and any "Home" button reached from deeper screens (categories,
  // subcategories, etc). NOT used by the bottom Home/Explore/Account tab
  // bar, or the Account screen's own back arrow — those should stay instant.
  const [tileActive, setTileActive] = useState(false);
  const [tileColor, setTileColor] = useState("#1006C5");
  // Optional caption shown under the INUA mark while tiles hold — only
  // the seller-enter flow below actually sets this (its wait time is
  // unpredictable since it depends on a real data fetch finishing).
  const [tileMessage, setTileMessage] = useState<string | undefined>(undefined);
  const tileActionRef = useRef<(() => void | Promise<void>) | null>(null);

  const runTileTransition = (
    action: () => void | Promise<void>,
    color = "#1006C5"
  ) => {
    if (tileActive) return; // ignore re-triggers mid-transition
    tileActionRef.current = action;
    setTileColor(color);
    setTileMessage(undefined);
    setTileActive(true);
  };

  // --- BOTTOM NAV HOVER/ACTIVE GLOW COLOR ---
  // Each nav button gets a randomly chosen accent color (orange or royal blue)
  // the moment it's hovered. That same color then persists as the "active" glow
  // once the button is clicked.
  const [navGlowColors, setNavGlowColors] = useState<
    Record<string, "orange" | "blue">
  >({});

  const pickRandomNavColor = (): "orange" | "blue" =>
    Math.random() < 0.5 ? "orange" : "blue";

  const handleNavHover = (id: string) => {
    setNavGlowColors((prev) => ({ ...prev, [id]: pickRandomNavColor() }));
  };

  const ensureNavColor = (id: string) => {
    setNavGlowColors((prev) =>
      prev[id] ? prev : { ...prev, [id]: pickRandomNavColor() }
    );
  };

  const NAV_GLOW_CLASSES: Record<
    "orange" | "blue",
    { text: string; hoverText: string; iconGlow: string }
  > = {
    orange: {
      text: "text-orange-400",
      hoverText: "hover:text-orange-400",
      iconGlow: "drop-shadow-[0_0_10px_rgba(251,146,60,0.9)]",
    },
    blue: {
      text: "text-blue-500",
      hoverText: "hover:text-blue-500",
      iconGlow: "drop-shadow-[0_0_10px_rgba(65,105,225,0.9)]",
    },
  };

  // --- BOTTOM NAV "WARP SPIN" ---
  // Each icon accumulates +360° every time it's triggered (hover on PC,
  // click/tap on either PC or mobile) so it always plays a fresh full
  // rotation even if triggered again mid-spin, rather than snapping back.
  const [navSpin, setNavSpin] = useState<Record<string, number>>({});
  const triggerNavSpin = (id: string) => {
    setNavSpin((prev) => ({ ...prev, [id]: (prev[id] || 0) + 360 }));
  };

  useEffect(() => {
    if (currentScreen) {
      localStorage.setItem("current_buyer_screen", currentScreen);
    }
  }, [currentScreen]);

  // Load Montserrat (Light 300) once for the category tag badges. Google
  // Fonts doesn't host a true "Montserrat Condensed" cut, so the badge style
  // pairs this weight with a slight horizontal scale to approximate one.
  useEffect(() => {
    if (document.getElementById("montserrat-font-link")) return;
    const link = document.createElement("link");
    link.id = "montserrat-font-link";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Montserrat:wght@300&display=swap";
    document.head.appendChild(link);
  }, []);

  // --- SUPABASE LIVE LISTINGS FETCH & REALTIME SUBSCRIPTION ---
  useEffect(() => {
    async function fetchBuyerListings() {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("listings")
          .select(
            // Changed from display_name to full_name for seller_applications
            "*, seller_applications:seller_id(display_name, phone_number, whatsapp_number, avatar_id, category, university, location, county)"
          )
          .eq("status", "active")
          .order("created_at", { ascending: false });

        if (error) {
          const { data: fallbackData } = await supabase
            .from("listings")
            .select("*")
            .eq("status", "active")
            .order("created_at", { ascending: false });

          if (fallbackData && fallbackData.length > 0) {
            setListings(fallbackData);
          } else {
            setListings([]);
          }
        } else if (data && data.length > 0) {
          setListings(data);
        } else {
          setListings([]);
        }
      } catch (err) {
        console.error("Error loading buyer feed from Supabase:", err);
        setListings([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchBuyerListings();

    // Setup dynamic Realtime listener for Instant Feed Updates
    const listingsSubscription = supabase
      .channel("public:listings")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "listings" },
        // Typed as `any`: a stricter local type here (see ListingsChangePayload
        // above) doesn't structurally match what the real `.on("postgres_changes", ...)`
        // overload expects, which makes TypeScript fail to match ANY overload
        // and report the error against the unrelated last "system" overload
        // instead. `any` lets overload resolution succeed; runtime logic below
        // (which only reads eventType/new/old) is unchanged.
        async (payload: any) => {
          if (payload.eventType === "INSERT") {
            // Fetch newly inserted listing with seller_applications joins (using full_name)
            const { data: newListing } = await supabase
              .from("listings")
              .select(
                "*, seller_applications:seller_id(display_name, phone_number, whatsapp_number, avatar_id, category, university, location, county)"
              )
              .eq("id", payload.new.id)
              .maybeSingle();

            if (newListing && newListing.status === "active") {
              setListings((prev) => [newListing, ...prev]);
            } else if (payload.new.status === "active") {
              setListings((prev) => [payload.new, ...prev]);
            }
          } else if (payload.eventType === "UPDATE") {
            if (payload.new.status !== "active") {
              setListings((prev) =>
                prev.filter((item) => item.id !== payload.new.id)
              );
            } else {
              const { data: updatedListing } = await supabase
                .from("listings")
                .select(
                  "*, seller_applications:seller_id(display_name, phone_number, whatsapp_number, avatar_id, category, university, location, county)"
                )
                .eq("id", payload.new.id)
                .maybeSingle();

              setListings((prev) =>
                prev.map((item) =>
                  item.id === payload.new.id
                    ? updatedListing || { ...item, ...payload.new }
                    : item
                )
              );
            }
          } else if (payload.eventType === "DELETE") {
            setListings((prev) =>
              prev.filter((item) => item.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(listingsSubscription);
    };
    // Re-run when the person signs in or out. Listings are only readable
    // once signed in, so the fetch that ran at page load (signed out) came
    // back empty. Signing in with Google reloads the page, which hid this;
    // the emailed code does not reload it, so the feed has to refetch.
  }, [hasSession]);

  const handleSellerClick = async (): Promise<boolean> => {
    // Re-check live rather than trusting only the cached hasSession state,
    // in case the session expired since the last auth event fired.
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      setShowSellerLockedNote(true);
      return false;
    }

    setShowSellerLockedNote(false);
    // SellerPortal's own effect already correctly checks seller_applications
    // (no application -> registration form, pending -> review screen,
    // approved -> dashboard) — no need to duplicate that logic here.
    setCurrentScreen("seller-portal");
    return true;
  };

  const handleBuyerClick = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", session.user.id)
        .maybeSingle();

      if (profile) {
        setCurrentScreen("buyer-home");
      } else {
        setCurrentScreen("buyer-auth");
      }
    } else {
      setCurrentScreen("buyer-auth");
    }
  };

  // --- SELLER TILE TRANSITIONS ---
  // Unlike the buyer flow (fixed ~1.5s cover, then reveal), the seller
  // dashboard's data can take an unpredictable amount of time to load, so
  // these two flows manage tileActive/tileColor by hand instead of going
  // through the generic runTileTransition() timer.
  //
  // Enter (buyer/seller select screen -> dashboard):
  //   1. Tiles sweep in, #ffb800.
  //   2. Once covered, navigate to the seller portal (hidden underneath).
  //   3. Tiles just sit there — spinner visible — until SellerPortal
  //      reports real readiness via onSellerReady (wired below).
  //   4. If a category theme hex came back with that signal, flash the
  //      tiles to it and hold briefly so the swap actually registers.
  //   5. Retract, revealing the dashboard (or whichever screen was ready).
  //
  // Home (dashboard -> buyer/seller select screen):
  //   1. Tiles sweep in using the seller's own category color.
  //   2. A brief loading hold (there's no real fetch on the way out, but
  //      this keeps the pacing symmetric with the way in).
  //   3. Flash to #ffb800, hold briefly, then reveal the entry screen.
  const sellerReadyResolverRef = useRef<((themeHex?: string) => void) | null>(
    null
  );

  const runSellerEnterTransition = async () => {
    if (tileActive) return;
    setTileColor("#ffb800");
    setTileMessage(undefined);
    setTileActive(true);

    await new Promise((resolve) => setTimeout(resolve, 1500)); // cover duration

    const navigated = await handleSellerClick();
    if (!navigated) {
      // No session — handleSellerClick already surfaced the "sign in as a
      // buyer first" note. Nothing new to reveal but the entry screen
      // itself, so retract right away instead of waiting on a signal that
      // will never come.
      setTileActive(false);
      return;
    }

    // SellerPortal is now mounted behind the opaque tiles and fetching.
    // The tiles hold here — spinner visible — until it calls back. This
    // wait has no fixed duration, so surface a caption once we're into
    // it so it doesn't just look stalled on a slow connection.
    setTileMessage("Fetching your data…");
    const themeHex = await new Promise<string | undefined>((resolve) => {
      sellerReadyResolverRef.current = resolve;
    });
    setTileMessage(undefined);

    if (themeHex) {
      setTileColor(themeHex);
      await new Promise((resolve) => setTimeout(resolve, 400));
    }

    requestAnimationFrame(() => setTileActive(false));
  };

  // After a correct verification code (SellerPortal's code screen):
  //   1. Tiles sweep in over the code screen, #ffb800.
  //   2. Once covered, `reveal` mounts the dashboard underneath.
  //   3. Tiles hold — "Fetching your data…" — until the dashboard has loaded
  //      and reports the seller's category color (same signal as Enter).
  //   4. Flash to that color, then retract onto the dashboard.
  // A 15s ceiling means a failed load can't leave the tiles stuck forever.
  const runSellerUnlockTransition = async (reveal: () => void) => {
    if (tileActive) return;
    setTileColor("#ffb800");
    setTileMessage(undefined);
    setTileActive(true);

    await new Promise((resolve) => setTimeout(resolve, 1500)); // cover duration

    setTileMessage("Fetching your data…");
    const ready = new Promise<string | undefined>((resolve) => {
      sellerReadyResolverRef.current = resolve; // set BEFORE the dashboard mounts
    });
    reveal();
    const themeHex = await Promise.race([
      ready,
      new Promise<undefined>((resolve) => setTimeout(resolve, 15000)),
    ]);
    sellerReadyResolverRef.current = null;
    setTileMessage(undefined);

    if (themeHex) {
      setTileColor(themeHex);
      await new Promise((resolve) => setTimeout(resolve, 400));
    }
    requestAnimationFrame(() => setTileActive(false));
  };

  // Passed to SellerPortal as onSellerReady.
  const handleSellerPortalReady = (themeHex?: string) => {
    sellerReadyResolverRef.current?.(themeHex);
    sellerReadyResolverRef.current = null;
  };

  // Passed to SellerPortal (which forwards it to ActiveSellerDashboard's
  // Home button) as onRequestHomeTransition.
  const runSellerHomeTransition = async (themeHex: string) => {
    if (tileActive) return;
    setTileColor(themeHex);
    setTileMessage(undefined);
    setTileActive(true);

    await new Promise((resolve) => setTimeout(resolve, 1500 + 800)); // cover + brief loading hold

    setTileColor("#ffb800");
    await new Promise((resolve) => setTimeout(resolve, 400));

    navigateTo("entry");
    requestAnimationFrame(() => setTileActive(false));
  };

  // Drives the tile transition: once `tileActive` flips true (tiles start
  // sweeping in), wait for them to finish covering the screen, run the
  // queued action hidden behind the opaque tiles, then retract them once
  // the destination screen has had a frame to paint.
  useEffect(() => {
    // Only the generic runTileTransition() path queues an action here — the
    // bespoke seller enter/home flows below manage tileActive/tileColor
    // themselves and never populate tileActionRef, so this is a no-op for
    // them (guarded explicitly rather than just relying on tileActive,
    // since both flows share that same piece of state).
    if (!tileActive || !tileActionRef.current) return;
    let cancelled = false;

    // Matches TileTransition.css: last tile starts at 0.8s and takes 0.7s
    // to finish filling => screen is fully covered at ~1.5s.
    const coverMs = 1500;
    const timer = window.setTimeout(async () => {
      try {
        await tileActionRef.current?.();
      } finally {
        tileActionRef.current = null;
      }
      if (cancelled) return;
      requestAnimationFrame(() => {
        if (!cancelled) setTileActive(false);
      });
    }, coverMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileActive]);

  useEffect(() => {
    // Initial check on mount, so the Seller button's locked/unlocked state
    // is correct immediately rather than defaulting to "locked" for a beat.
    supabase.auth.getSession().then(({ data: { session } }) => {
      setHasSession(!!session);
    });

    const ensureUserProfile = async (sessionUser: any) => {
      try {
        if (!sessionUser) return;

        const { data: existingProfile, error: fetchError } = await supabase
          .from("profiles")
          .select("id")
          .eq("id", sessionUser.id)
          .single();

        if (
          !existingProfile &&
          (fetchError?.code === "PGRST116" || !existingProfile)
        ) {
          const { error: insertError } = await supabase
            .from("profiles")
            .insert([
              {
                id: sessionUser.id,
                full_name: sessionUser.user_metadata?.full_name || "",
                email: sessionUser.email,
                avatar_url: sessionUser.user_metadata?.avatar_url || "",
                role: "buyer",
              },
            ]);

          if (insertError) throw insertError;
        }
      } catch (err: any) {
        console.error("Profile synchronization error:", err.message);
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setHasSession(!!session);
      if (session) {
        await ensureUserProfile(session.user);
        // A successful sign-in clears the "sign in as a buyer first" note,
        // since the Seller button is now genuinely unlocked.
        setShowSellerLockedNote(false);
      } else if (event === "SIGNED_OUT") {
        setCurrentScreen("entry");
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // --- NAVIGATION HANDLERS ---
  const navigateTo = (screen: string) => {
    // Whenever heading back to the Buyer/Seller selection screen, remember
    // which side we're leaving FROM (based on the screen prior to this
    // call) so EntryScreen lands back on that same side instead of always
    // defaulting to Buyer. Centralized here rather than at each individual
    // "back to entry" button so it automatically covers every path,
    // including SellerPortal's own back buttons on its pending/rejected/
    // no-session screens.
    if (screen === "entry") {
      setEntrySide(currentScreen.startsWith("seller") ? "seller" : "buyer");
    }
    // Someone who opened a store link while signed out and then signed in
    // lands here on buyer-home — take them to the store they came for.
    if (screen === "buyer-home") {
      const pending = readPendingStore();
      if (pending) {
        clearPendingStore();
        setStoreCode(pending);
        screen = "store";
      }
    }
    localStorage.setItem("current_buyer_screen", screen);
    setCurrentScreen(screen);
  };

  // --- SELLER STORES ---
  const closeStore = async () => {
    clearPendingStore();
    // (The address bar now follows the screen automatically — see
    // useBackButtonHistory — so there is nothing to reset here.)
    const {
      data: { session },
    } = await supabase.auth.getSession();
    setCurrentScreen(session ? "buyer-home" : "entry");
  };

  const signInForStore = () => {
    if (storeCode) setPendingStore(storeCode);
    setCurrentScreen("buyer-auth");
  };

  // Opened from the seller name on the product screen. The listing doesn't
  // carry the seller's random store code, so look it up (falls back to the
  // seller id if the store_code column hasn't been added yet).
  const openSellerStore = async (product: any) => {
    const sellerId = product?.seller_id;
    if (!sellerId) return;
    let code = "";
    try {
      const { data, error } = await supabase
        .from("seller_applications")
        .select("store_code")
        .eq("id", sellerId)
        .maybeSingle();
      if (!error && data?.store_code) code = data.store_code;
    } catch {
      /* handled below */
    }
    if (!code) {
      showAppToast({
        title: "Store unavailable",
        message: "This seller's store isn't available right now.",
      });
      return;
    }
    setStoreCode(code);
    setSelectedProduct(null);
    setCurrentScreen("store");
  };

  // --- BOTTOM-TAB SWIPE TRANSITION ---
  // Home / Explore / Account share one horizontal "swipe deck" — sliding
  // right when moving to a tab further right in this order, left when
  // moving back, like flipping between photos on a phone rather than the
  // old instant snap-cut.
  const tabOrder = [
    "buyer-home",
    "buyer-categories",
    "buyer-orders",
    "buyer-profile",
  ];
  const prevTabIndexRef = useRef(0);
  const currentTabIndex = tabOrder.indexOf(currentScreen);
  const tabSwipeDirection =
    currentTabIndex === -1
      ? 0
      : currentTabIndex > prevTabIndexRef.current
      ? 1
      : currentTabIndex < prevTabIndexRef.current
      ? -1
      : 0;
  useEffect(() => {
    if (currentTabIndex !== -1) prevTabIndexRef.current = currentTabIndex;
  }, [currentTabIndex]);
  const tabSwipeVariants = {
    enter: (dir: number) => ({ x: dir >= 0 ? "100%" : "-100%", opacity: 0.7 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir >= 0 ? "-100%" : "100%", opacity: 0.7 }),
  };

  const handleCategoryClick = (catId: string) => {
    const foundCategory = CATEGORIES[catId.toLowerCase()] || {
      id: catId,
      name: catId.toUpperCase(),
      abbr: catId.slice(0, 2).toUpperCase(),
      color: DEFAULT_CATEGORY_THEME.color,
      statement: DEFAULT_CATEGORY_THEME.statement,
      subcategories: [],
    };
    setSelectedCategory(foundCategory);
    setSelectedSubcategory(null);
    setStairsCategory(foundCategory);
    setStairsActive(true);
    navigateTo("category");
  };

  const handleSubcategoryClick = (subcategory: {
    id: string;
    name: string;
  }) => {
    // Previously this navigated to a dedicated "subcategory-transition"
    // screen — a full-bleed loading splash (category color zoom + progress
    // bar) that played for ~1.7s before landing back on "category". That
    // screen has been removed entirely: StaggeredMenu's own close animation
    // (panel retreats, category color sweep follows) already gives the
    // subcategory switch a transition of its own, so all this needs to do
    // now is update which subcategory is selected — CategoryScreen re-filters
    // its listings against it immediately, and the result is just sitting
    // there, already rendered, by the time the menu finishes collapsing.
    setSelectedSubcategory(subcategory);
  };

  // --- SCREENS ---

  const BuyerCategories = () => {
    const [expandedCatId, setExpandedCatId] = useState<string | null>(null);
    const gridRef = useRef<HTMLDivElement>(null);
    const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // Framer Motion's `layout` prop drives its animation by comparing a
    // "before" rect against an "after" rect that it measures itself. On
    // a fresh visit, this screen mounts mid-transition (its own
    // `animate-in fade-in` entrance, right after a GSAP tile transition),
    // so Framer's own first measurement can be unreliable and the very
    // first expand snaps instead of animating. Every later toggle is
    // measured once things have settled, which is why only that first
    // expand ever looked broken.
    //
    // Fix: don't rely on Framer's own measurement for that first expand
    // at all. Grab the real "before" rect ourselves with
    // `getBoundingClientRect()` at the moment of the click, let React
    // commit the expanded layout, then grab the real "after" rect too.
    // Both of those are live DOM measurements taken at exact, known
    // instants — accurate no matter what else is still settling on
    // screen. From the two rects we hardcode a manual transform (the
    // classic FLIP "Invert" step) that plays the card visually growing
    // from its old spot to its new one. Once that one hardcoded
    // animation finishes, we hand control back to Framer's own `layout`
    // system for every toggle from then on — which is exactly the path
    // that was already smooth on click #2 onward.
    const hasExpandedOnceRef = useRef(false);
    const pendingFirstFlipIdRef = useRef<string | null>(null);
    const beforeRectRef = useRef<DOMRect | null>(null);
    const [manualFirstFlip, setManualFirstFlip] = useState<{
      id: string;
      dx: number;
      dy: number;
      scaleX: number;
      scaleY: number;
    } | null>(null);

    // Fixed order, always — the expanded card grows IN PLACE instead of
    // jumping to the top. `gridAutoFlow: "dense"` (below) lets the grid
    // backfill any gap left behind, so the cards above/below reflow
    // around it automatically instead of leaving a hole.
    const allCats = Object.values(CATEGORIES) as any[];
    const exploreFirstCardRef = useRef<HTMLDivElement | null>(null);
    const isNewBuyerExplore = useIsNewBuyer();
    const exploreTour = useCoachTour(
      "explore",
      [
        {
          ref: exploreFirstCardRef,
          title: "Categories",
          text: "This is a category. Tap the ••• button or the card itself to expand it, then tap Explore to see the listings inside.",
        },
      ],
      isNewBuyerExplore
    );

    // NOTE: there used to be an "outside click" handler here that collapsed
    // the expanded card on any `mousedown` outside the grid. The bottom
    // Home/Explore/Account nav sits outside the grid entirely (it's a
    // sibling, fixed-position element — see `showBottomNav` below), so that
    // handler fired — and collapsed the card — the instant the pointer even
    // pressed down over the nav bar. Removed: the card should only collapse
    // when its own "X" is clicked or another card is opened, both of which
    // already go through `toggleExpand` below.

    // Right after React commits the DOM for a *newly expanded* card (but
    // before the browser paints it), check whether this was the guarded
    // very-first expand. If so, measure the real "after" rect, diff it
    // against the "before" rect captured at click-time, and hand that
    // hardcoded delta to the render below as a manual transform
    // animation instead of Framer's automatic layout measurement.
    useLayoutEffect(() => {
      const id = pendingFirstFlipIdRef.current;
      if (!id || expandedCatId !== id) return;
      const node = cardRefs.current[id];
      const before = beforeRectRef.current;
      pendingFirstFlipIdRef.current = null;
      beforeRectRef.current = null;
      // From this point on, every future toggle (even a very fast
      // second click) goes through the normal Framer `layout` path.
      hasExpandedOnceRef.current = true;
      if (!node || !before) return;

      const after = node.getBoundingClientRect();
      if (after.width === 0 || after.height === 0) return;

      setManualFirstFlip({
        id,
        dx: before.left - after.left,
        dy: before.top - after.top,
        scaleX: before.width / after.width,
        scaleY: before.height / after.height,
      });
    }, [expandedCatId]);

    // Clicking the card itself OR the "•••"/"X" button both expand it.
    // Clicking a different card while one is already open just switches
    // straight to the newly clicked one — only the last click ever wins,
    // so two cards can never be open at once.
    const toggleExpand = (catId: string) => {
      setExpandedCatId((prev) => {
        const next = prev === catId ? null : catId;
        // Only the very first collapsed -> expanded transition in this
        // component's lifetime gets the hardcoded manual FLIP; capture
        // its true "before" rect right now, synchronously, before the
        // state change re-renders anything.
        if (next && !hasExpandedOnceRef.current) {
          const node = cardRefs.current[catId];
          if (node) {
            beforeRectRef.current = node.getBoundingClientRect();
            pendingFirstFlipIdRef.current = catId;
          }
        }
        return next;
      });
    };

    return (
      <div className="h-full w-full bg-[var(--bg-app)] text-[var(--text-primary)] flex flex-col max-w-md mx-auto relative overflow-hidden animate-in fade-in duration-300">
        <header className="h-16 border-b border-[var(--border-soft)] flex items-center px-4 sticky top-0 bg-[var(--bg-app)]/90 backdrop-blur-md z-20">
          <button
            onClick={() =>
              runTileTransition(() => navigateTo("entry"), "#1006C5")
            }
            className="p-2 -ml-2 text-[var(--text-primary)] hover:text-[var(--text-primary)]"
            title="Back to Selection"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold ml-2">Explore Categories</h1>
        </header>

        <div
          ref={gridRef}
          className="flex-1 p-4 overflow-y-auto pb-24 mt-2 grid grid-cols-2 auto-rows-max items-start content-start gap-3"
          style={{ gridAutoFlow: "dense" }}
        >
          {allCats.map((cat: any, catIdx: number) => {
            const isExpanded = cat.id === expandedCatId;
            const isManualFlip = manualFirstFlip?.id === cat.id;
            const tileBgAlpha = theme === "light" ? "40" : "18";
            const tileBorderAlpha = theme === "light" ? "60" : "30";
            return (
              <motion.div
                key={cat.id}
                ref={(el) => {
                  cardRefs.current[cat.id] = el;
                  if (catIdx === 0) exploreFirstCardRef.current = el;
                }}
                // The one hardcoded first expand plays a manual transform
                // instead of Framer's own measured layout animation, so
                // `layout` is switched off just for that single render.
                layout={!isManualFlip}
                style={{
                  transformOrigin: "top left",
                  backgroundColor: isExpanded
                    ? cat.color
                    : `${cat.color}${tileBgAlpha}`,
                  border: isExpanded
                    ? "none"
                    : `1px solid ${cat.color}${tileBorderAlpha}`,
                }}
                animate={
                  isManualFlip
                    ? {
                        x: [manualFirstFlip!.dx, 0],
                        y: [manualFirstFlip!.dy, 0],
                        scaleX: [manualFirstFlip!.scaleX, 1],
                        scaleY: [manualFirstFlip!.scaleY, 1],
                      }
                    : undefined
                }
                onAnimationComplete={() => {
                  if (isManualFlip) setManualFirstFlip(null);
                }}
                onClick={() => toggleExpand(cat.id)}
                transition={{ type: "spring", stiffness: 340, damping: 30 }}
                className={`rounded-3xl relative overflow-hidden cursor-pointer ${
                  isExpanded ? "col-span-2 p-5" : "p-4 aspect-square"
                }`}
              >
                {/* TOP ROW: badge + menu/close button */}
                <div className="flex items-start justify-between">
                  <div
                    className={`rounded-2xl flex items-center justify-center font-black tracking-wide shrink-0 ${
                      isExpanded
                        ? "w-11 h-11 text-sm bg-white/20 text-white"
                        : "w-9 h-9 text-xs"
                    }`}
                    style={
                      isExpanded
                        ? {}
                        : {
                            backgroundColor: `${cat.color}30`,
                            color: cat.color,
                          }
                    }
                  >
                    {cat.abbr}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(cat.id);
                    }}
                    className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                      isExpanded
                        ? "bg-black/20 hover:bg-black/30 text-white"
                        : "bg-[var(--surface-soft)] hover:bg-[var(--surface-soft-hover)] text-[var(--text-secondary)]"
                    }`}
                    title={isExpanded ? "Collapse" : "Expand"}
                  >
                    {isExpanded ? (
                      <X size={14} />
                    ) : (
                      <span className="text-xs tracking-widest leading-none">
                        •••
                      </span>
                    )}
                  </button>
                </div>

                {/* BOTTOM: name / statement (+ Explore button when expanded) */}
                <div
                  className={
                    isExpanded
                      ? "mt-8 flex items-end justify-between gap-3"
                      : "mt-3"
                  }
                >
                  <div className="min-w-0">
                    <p
                      className={`font-black leading-tight truncate ${
                        isExpanded ? "text-xl text-white" : "text-sm"
                      }`}
                      style={isExpanded ? {} : { color: cat.color }}
                    >
                      {cat.name}
                    </p>
                    <p
                      className={`font-mono truncate mt-1 ${
                        isExpanded
                          ? "text-xs text-white/70"
                          : "text-[10px] text-[var(--text-primary)]"
                      }`}
                    >
                      {cat.statement}
                    </p>
                  </div>

                  {isExpanded && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryClick(cat.id);
                      }}
                      className="shrink-0 bg-white/25 hover:bg-white/35 backdrop-blur-sm px-5 py-2.5 rounded-full font-bold text-sm transition-colors"
                    >
                      Explore
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
        <ScrollToTopButton scrollRef={gridRef} />
        {exploreTour.activeStep && (
          <Coachmark
            step={exploreTour.activeStep}
            stepIndex={exploreTour.stepIndex}
            totalSteps={exploreTour.totalSteps}
            onNext={exploreTour.next}
            onDismiss={exploreTour.dismiss}
          />
        )}
      </div>
    );
  };

  // Stairs preloader — fires on top of the app when a buyer taps "Explore"
  // on a category card. The category name + statement wipe in together
  // (fade-to-bright, left-to-right), hold for a beat, then a row of
  // vertical "stair" strips (colored in the category's own color) peel
  // away one after another to reveal the category listing screen that's
  // already loaded underneath. Always mounted (like TileTransition below)
  // so it can layer on top instantly — renders nothing while inactive.
  const STAIR_COUNT = 7;

  const CategoryStairsPreloader = ({
    active,
    category,
    onComplete,
  }: {
    active: boolean;
    category: any;
    onComplete: () => void;
  }) => {
    const [phase, setPhase] = useState<"idle" | "text" | "stairs">("idle");
    const onCompleteRef = useRef(onComplete);

    useEffect(() => {
      onCompleteRef.current = onComplete;
    }, [onComplete]);

    useEffect(() => {
      if (!active) {
        setPhase("idle");
        return;
      }
      setPhase("text");
      const stairsTimer = setTimeout(() => setPhase("stairs"), 1300);
      const lastStripDelay = (STAIR_COUNT - 1) * 70;
      const doneTimer = setTimeout(() => {
        onCompleteRef.current();
      }, 1300 + lastStripDelay + 550 + 100);
      return () => {
        clearTimeout(stairsTimer);
        clearTimeout(doneTimer);
      };
    }, [active]);

    if (!active) return null;

    const bgColor = category?.color || DEFAULT_CATEGORY_THEME.color;
    const name = category?.name || "";
    const statement = category?.statement || "";
    const revealed = phase === "text" || phase === "stairs";

    return (
      <div className="fixed inset-0 z-[100] pointer-events-none">
        <div className="h-full w-full max-w-md mx-auto relative overflow-hidden">
          {/* Stair strips — the solid colored "cover"; each one slides up
              off-screen with a staggered delay to create the staircase.
              Each strip is 1px wider than its slot and shifted 1px left
              (via calc()), so neighbors overlap slightly — that overlap
              is what hides the seam/gap line you'd otherwise see between
              them while they're animating. */}
          <div className="absolute inset-0">
            {Array.from({ length: STAIR_COUNT }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute top-0 h-full"
                style={{
                  left: `calc(${(i / STAIR_COUNT) * 100}% - 1px)`,
                  width: `calc(${100 / STAIR_COUNT}% + 2px)`,
                  backgroundColor: bgColor,
                }}
                initial={{ y: "0%" }}
                animate={{ y: phase === "stairs" ? "-100%" : "0%" }}
                transition={{
                  duration: 0.55,
                  ease: [0.76, 0, 0.24, 1],
                  delay: phase === "stairs" ? i * 0.07 : 0,
                }}
              />
            ))}
          </div>

          {/* Category name + statement — wipe in together, left to right,
              fading from dim to bright as the clip reveals them, then
              fading OUT again as soon as the stairs start lifting — so
              by the time the strips have cleared, the text is already
              gone and can't sit on top of the listings underneath. */}
          <div className="absolute inset-0 flex flex-col items-center justify-center px-8">
            <div className="overflow-hidden max-w-full">
              <motion.h1
                className="text-4xl font-black italic text-white tracking-widest text-center"
                initial={{
                  clipPath: "inset(0 100% 0 0)",
                  filter: "brightness(0.35)",
                  opacity: 1,
                }}
                animate={{
                  clipPath: revealed ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)",
                  filter: revealed ? "brightness(1)" : "brightness(0.35)",
                  opacity: phase === "stairs" ? 0 : 1,
                }}
                transition={
                  phase === "stairs"
                    ? { opacity: { duration: 0.45, ease: "easeInOut" } }
                    : { duration: 0.85, ease: "easeOut" }
                }
              >
                {name}
              </motion.h1>
            </div>
            <div className="overflow-hidden max-w-full mt-3">
              <motion.p
                className="text-white/90 text-sm font-mono tracking-wide text-center"
                initial={{
                  clipPath: "inset(0 100% 0 0)",
                  filter: "brightness(0.35)",
                  opacity: 1,
                }}
                animate={{
                  clipPath: revealed ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)",
                  filter: revealed ? "brightness(1)" : "brightness(0.35)",
                  opacity: phase === "stairs" ? 0 : 1,
                }}
                transition={
                  phase === "stairs"
                    ? { opacity: { duration: 0.45, ease: "easeInOut" } }
                    : { duration: 0.85, ease: "easeOut", delay: 0.05 }
                }
              >
                {statement}
              </motion.p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CategoryTransitionScreen = () => {
    const [phase, setPhase] = useState("bg");
    const [progress, setProgress] = useState(0);

    useEffect(() => {
      setTimeout(() => setPhase("text"), 400);
      setTimeout(() => setPhase("loading"), 1000);

      const interval = setInterval(() => {
        setProgress((p) => (p >= 100 ? 100 : p + 2));
      }, 50);

      setTimeout(() => setPhase("text2"), 2500);
      setTimeout(() => navigateTo("category"), 3800);

      return () => clearInterval(interval);
    }, []);

    const bgColor = selectedCategory?.color || DEFAULT_CATEGORY_THEME.color;

    return (
      <div
        className="fixed inset-0 z-[200] flex flex-col items-center justify-center p-6 transition-colors duration-700"
        style={{ backgroundColor: bgColor }}
      >
        <div className="w-full max-w-md mx-auto flex flex-col items-center">
          <h1
            className="text-4xl font-black italic text-white tracking-widest transition-all duration-1000 ease-out"
            style={{
              opacity:
                phase === "text" || phase === "loading" || phase === "text2"
                  ? 1
                  : 0,
              transform:
                phase === "text" || phase === "loading" || phase === "text2"
                  ? "translateY(0)"
                  : "translateY(20px)",
            }}
          >
            {selectedCategory?.name}
          </h1>

          <div
            className="w-48 h-1 bg-black/20 rounded-full mt-12 overflow-hidden transition-opacity duration-500"
            style={{
              opacity: phase === "loading" || phase === "text2" ? 1 : 0,
            }}
          >
            <div
              className="h-full bg-white transition-all duration-75"
              style={{ width: `${progress}%` }}
            />
          </div>

          <p
            className="text-white/90 text-sm mt-6 font-mono tracking-wide transition-all duration-1000 text-center"
            style={{
              opacity: phase === "text2" ? 1 : 0,
              transform:
                phase === "text2" ? "translateY(0)" : "translateY(10px)",
            }}
          >
            {selectedCategory?.statement}
          </p>
        </div>
      </div>
    );
  };

  // CategoryScreen is declared inside App, so it is a brand-new function on
  // every App render. Rendering it as <CategoryScreen /> made React treat it as
  // a different component each time and REMOUNT the whole page — which closed
  // the search overlay after every letter and reset the filters. This wrapper
  // has one permanent identity (created once) and just calls the latest
  // CategoryScreen, so the page (and its hooks/state) survives App re-renders.
  const categoryScreenRef = useRef<() => React.ReactElement | null>(
    () => null
  );
  const StableCategoryScreen = useRef(function StableCategoryScreen() {
    return categoryScreenRef.current();
  }).current;

  const CategoryScreen = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    // The div actually scrolling on this screen — see ScrollToTopButton.
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Same search bar as the main marketplace feed — see
    // MarketplaceSearchBar / BuyerHome above — but filtering by this
    // category's own subcategories rather than the top-level category
    // list, since the buyer is already inside this category. That filter
    // only makes sense while browsing "All [Category]": once a single
    // subcategory is selected there's nothing left to filter, so the
    // search bar drops the filter UI entirely (see filterItems below).
    // IMPORTANT: this must stay LOCAL state. CategoryScreen is a component
    // defined inside App, so any App-level state change re-creates it and
    // remounts the whole screen — which closes the search overlay and drops
    // focus. Typing must therefore only touch state that lives in here.
    const [catSearchQuery, setCatSearchQuery] = useState("");
    // Local too, so opening the county dialog doesn't remount the page.
    const [isCountyDialogOpen, setIsCountyDialogOpen] = useState(false);
    const [activeSubcategoryIds, setActiveSubcategoryIds] = useState<
      Set<string>
    >(
      () =>
        new Set(
          (selectedCategory?.subcategories || []).map(
            (s: { id: string; name: string }) => s.id
          )
        )
    );
    // Reset the active subcategory filter whenever the buyer lands on a
    // different top-level category, so leftover removed chips from a
    // previous category don't silently carry over and hide everything.
    useEffect(() => {
      setActiveSubcategoryIds(
        new Set(
          (selectedCategory?.subcategories || []).map(
            (s: { id: string; name: string }) => s.id
          )
        )
      );
    }, [selectedCategory?.id]);
    const toggleSubcategoryFilter = (subId: string) => {
      setActiveSubcategoryIds((prev) => {
        const next = new Set(prev);
        next.delete(subId);
        return next;
      });
    };
    const resetSubcategoryFilters = () =>
      setActiveSubcategoryIds(
        new Set(
          (selectedCategory?.subcategories || []).map(
            (s: { id: string; name: string }) => s.id
          )
        )
      );

    const [catLocationMatchIds, setCatLocationMatchIds] = useState<Set<string>>(
      new Set()
    );
    useEffect(() => {
      const trimmed = catSearchQuery.trim();
      if (trimmed.length < 2) {
        setCatLocationMatchIds(new Set());
        return;
      }
      let cancelled = false;
      const timer = setTimeout(async () => {
        const ids = await searchSellersByLocationOrInstitution(trimmed);
        if (!cancelled) setCatLocationMatchIds(ids);
      }, 350);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }, [catSearchQuery]);

    // Bypasses the debounce above so the magnifying-glass button (and the
    // overlay's "Search" button / Enter key) triggers the seller
    // location/institution lookup against Supabase right away.
    const runCatLocationSearchNow = async () => {
      const trimmed = catSearchQuery.trim();
      if (trimmed.length < 2) {
        setCatLocationMatchIds(new Set());
        return;
      }
      const ids = await searchSellersByLocationOrInstitution(trimmed);
      setCatLocationMatchIds(ids);
    };

    // A parent category's subcategory ids — used to show "all" listings
    // under the broad group when no specific subcategory is selected yet.
    const subcategoryIds: string[] = (
      selectedCategory?.subcategories || []
    ).map((s: { id: string; name: string }) => s.id);

    const categoryListings = listings.filter((l: any) => {
      const sellerApp = getSellerApp(l);
      // The seller's stored category is usually the PARENT category
      // (e.g. "furniture_home_goods"); the specific type (e.g.
      // "kitchenware") is saved on the LISTING as `subcategory` (see ASD
      // Create Listing). So the subcategory comes from the listing first,
      // and only falls back to the seller's category for older listings
      // where that holds a subcategory id.
      const itemCategory = (
        sellerApp?.category ||
        l.category ||
        ""
      ).toLowerCase();
      const listingSub = String(l.subcategory || "").toLowerCase();
      const subId = subcategoryIds.includes(listingSub)
        ? listingSub
        : subcategoryIds.includes(itemCategory)
        ? itemCategory
        : "";
      const parentId = selectedCategory?.id?.toLowerCase();
      const matchesCategory = selectedSubcategory
        ? subId === selectedSubcategory.id.toLowerCase()
        : subId !== "" ||
          itemCategory === parentId ||
          SUBCATEGORY_TO_PARENT[itemCategory] === selectedCategory?.id;

      // Subcategory chip filter only applies on "All [Category]" — once a
      // specific subcategory is selected, matchesCategory above already
      // pins results to it, so there's nothing left to filter. A listing
      // tagged with a recognized subcategory must be in the active set;
      // one tagged only with the bare parent category (no subcategory)
      // always shows, since it isn't represented by any chip.
      const matchesSubcategoryFilter = selectedSubcategory
        ? true
        : subId === "" || activeSubcategoryIds.has(subId);
      if (!matchesSubcategoryFilter) return false;

      const query = catSearchQuery.trim().toLowerCase();
      const matchesSearch =
        !query ||
        l.title?.toLowerCase().includes(query) ||
        (sellerApp?.display_name || "").toLowerCase().includes(query) ||
        (sellerApp?.location || "").toLowerCase().includes(query) ||
        (sellerApp?.university || "").toLowerCase().includes(query) ||
        (getSubcategoryDisplayName(l.subcategory) || "")
          .toLowerCase()
          .includes(query) ||
        String(l.subcategory || "")
          .toLowerCase()
          .replace(/_/g, " ")
          .includes(query) ||
        (selectedCategory?.name || "").toLowerCase().includes(query) ||
        resolveListingCounty(l, sellerApp).toLowerCase().includes(query) ||
        catLocationMatchIds.has(l.seller_id);

      return (
        matchesCategory &&
        matchesSearch &&
        listingMatchesCounty(resolveListingCounty(l, getSellerApp(l)), selectedCounty)
      );
    });

    const headerLabel = selectedSubcategory
      ? selectedSubcategory.name.toUpperCase()
      : selectedCategory?.name;

    const handleBackToCategories = () => {
      setIsSidebarOpen(false);
      setSelectedSubcategory(null);
      navigateTo("buyer-categories");
    };

    return (
      <div className="min-h-screen w-full bg-[var(--bg-app)] text-[var(--text-primary)] flex flex-col max-w-md mx-auto relative shadow-2xl animate-in fade-in duration-500 overflow-hidden">
        <header className="h-20 px-4 flex items-center justify-between border-b border-[var(--border-soft)] bg-[var(--bg-header)] sticky top-0 z-20 gap-1">
          <div className="flex items-center gap-1 -ml-2 min-w-0">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 rounded-full hover:bg-[var(--surface-soft-hover)] active:scale-90 transition-all duration-200 shrink-0"
              style={{ color: selectedCategory?.color }}
              title="Browse subcategories"
              aria-label="Browse subcategories"
              aria-expanded={isSidebarOpen}
            >
              <StaggeredMenuBurgerIcon isOpen={isSidebarOpen} size={24} />
            </button>
            <h1
              className="text-lg md:text-xl font-black italic tracking-widest truncate"
              style={{ color: selectedCategory?.color }}
            >
              {headerLabel}
            </h1>
          </div>

          <button
            onClick={() =>
              runTileTransition(() => navigateTo("buyer-home"), "#1006C5")
            }
            className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center hover:bg-[var(--surface-soft-hover)] transition-colors"
            title="Go to Home"
          >
            <Home size={20} style={{ color: selectedCategory?.color }} />
          </button>
        </header>

        {/* --- SUBCATEGORY MENU --- */}
        {/* Wrapped in an absolute inset-0 layer scoped to this screen's own
            `relative` card (not the viewport) — see the positioning note in
            StaggeredMenu.tsx. `hideToggleButton` is set because this screen
            already has its own header Menu button driving `isSidebarOpen`;
            StaggeredMenu is used here purely as the animated panel. */}
        <div className="absolute inset-0 z-40 pointer-events-none">
          <StaggeredMenu
            open={isSidebarOpen}
            onOpenChange={setIsSidebarOpen}
            hideToggleButton
            position="left"
            panelTitle={selectedCategory?.name}
            accentColor={
              selectedCategory?.color || DEFAULT_CATEGORY_THEME.color
            }
            colors={getCategoryMenuColors(selectedCategory?.color)}
            displayItemNumbering
            displaySocials
            items={[
              {
                label: `All ${selectedCategory?.name || ""}`,
                ariaLabel: `Show all ${selectedCategory?.name || "listings"}`,
                isActive: !selectedSubcategory,
                onClick: () => setSelectedSubcategory(null),
              },
              ...(selectedCategory?.subcategories || []).map(
                (sub: { id: string; name: string }) => ({
                  label: sub.name,
                  ariaLabel: `Show ${sub.name}`,
                  isActive: selectedSubcategory?.id === sub.id,
                  onClick: () => handleSubcategoryClick(sub),
                })
              ),
            ]}
            socialItems={[
              {
                label: "Back to Categories",
                onClick: handleBackToCategories,
              },
              {
                label: "Home",
                onClick: () =>
                  runTileTransition(() => navigateTo("buyer-home"), "#1006C5"),
              },
            ]}
          />
        </div>

        <div className="px-5 pt-4 pb-1 bg-[var(--bg-header)] relative z-10">
          <MarketplaceSearchBar
            query={catSearchQuery}
            onQueryChange={setCatSearchQuery}
            filterLabel="Show me..."
            // Only "All [Category]" has anything left to filter by (its
            // subcategories) — once a specific subcategory is selected,
            // matchesCategory already pins results to it, so there's no
            // filter UI to show at all, just plain text search.
            filterItems={
              selectedSubcategory ? null : selectedCategory?.subcategories
            }
            activeFilterIds={activeSubcategoryIds}
            onToggleFilter={toggleSubcategoryFilter}
            onResetFilters={resetSubcategoryFilters}
            onSearch={runCatLocationSearchNow}
            theme={theme}
            greyInLight
          />
        </div>

        <div className="px-5 py-3 flex items-center gap-2 border-b border-[var(--border-soft)] bg-[var(--bg-header)] sticky top-20 z-10">
          <CountyFilterButton
            selectedCounty={selectedCounty}
            onClick={() => setIsCountyDialogOpen(true)}
            theme={theme}
            accentColor={selectedCategory?.color}
          />
        </div>

        <CountySelectDialog
          isOpen={isCountyDialogOpen}
          onClose={() => setIsCountyDialogOpen(false)}
          onSelectCounty={setSelectedCounty}
          selectedCounty={selectedCounty}
          theme={theme}
          accentColor={selectedCategory?.color}
        />

        <div ref={scrollContainerRef} className="flex-1 p-5 overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm font-medium text-[var(--text-secondary)]">
              Showing {categoryListings.length} results in{" "}
              <span className="font-bold">{selectedCounty}</span>
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {categoryListings.map((listing: any) => {
              const sellerApp = getSellerApp(listing);

              if (!sellerApp) {
                console.warn(
                  `[Category Feed] "${listing.title}" (id=${listing.id}, seller_id=${listing.seller_id}): ` +
                    `no matching row in seller_applications. Confirm this seller_id exists as an "id" there.`
                );
              } else if (!sellerApp.display_name || !sellerApp.category) {
                console.warn(
                  `[Category Feed] "${listing.title}" (id=${listing.id}, seller_id=${listing.seller_id}): ` +
                    `seller_applications row found but incomplete — display_name="${sellerApp.display_name}", category="${sellerApp.category}". ` +
                    `Fix this seller's row in Supabase.`
                );
              }

              const displayImage =
                listing.photo_urls?.[0] ||
                (Array.isArray(listing.images)
                  ? listing.images[0]
                  : listing.images) ||
                listing.image_url;

              // Buyers only ever see display_name, never the seller's legal full_name
              const sellerDisplayName =
                sellerApp?.display_name || "Campus Seller";
              const subcategoryLabel =
                getSubcategoryDisplayName(listing.subcategory) ||
                getCategoryDisplayLabel(sellerApp?.category || "");

              return (
                <div
                  key={listing.id}
                  onClick={() => setSelectedProduct(listing)}
                  className="bg-[var(--bg-container)] rounded-2xl p-4 shadow-sm border border-[var(--border-soft)] flex gap-4 cursor-pointer hover:shadow-md transition-shadow group"
                >
                  <div
                    className="w-24 h-24 rounded-xl flex flex-col items-center justify-center shrink-0 transition-colors overflow-hidden"
                    style={{ backgroundColor: `${selectedCategory?.color}15` }}
                  >
                    {displayImage ? (
                      <img
                        src={displayImage}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span
                        className="font-bold text-xs opacity-50"
                        style={{ color: selectedCategory?.color }}
                      >
                        IMAGE
                      </span>
                    )}
                  </div>

                  <div className="flex-1 flex flex-col justify-center">
                    <p
                      className="text-[10px] font-bold uppercase tracking-wider mb-0.5 truncate"
                      style={{ color: selectedCategory?.color }}
                    >
                      {subcategoryLabel}
                    </p>
                    <h3 className="font-bold text-lg leading-tight mb-1">
                      {listing.title}
                    </h3>
                    <p className="text-xs text-[var(--text-secondary)] mb-3 font-mono">
                      {sellerDisplayName}
                    </p>
                    <p
                      className="font-black text-xl"
                      style={{ color: selectedCategory?.color }}
                    >
                      {listing.listing_kind === "service" ? "From " : ""}
                      {typeof listing.price === "number"
                        ? `KES ${listing.price.toLocaleString()}`
                        : listing.price}
                    </p>
                  </div>
                </div>
              );
            })}

            {categoryListings.length === 0 && (
              <div className="text-center py-20">
                <div
                  className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                  style={{
                    backgroundColor: `${selectedCategory?.color}15`,
                    color: selectedCategory?.color,
                  }}
                >
                  <Search size={24} />
                </div>
                <h3 className="font-bold text-lg mb-1">No matches found</h3>
                <p className="text-sm text-[var(--text-secondary)]">
                  Try a different county or check back later.
                </p>
              </div>
            )}
          </div>
        </div>
        <ScrollToTopButton scrollRef={scrollContainerRef} />
      </div>
    );
  };
  // Keep the wrapper pointed at this render's CategoryScreen (fresh closure).
  categoryScreenRef.current = CategoryScreen;

  // --- "REVIEW OUR TERMS" (buyer) ---
  // Shown once per account (and again whenever TERMS_VERSION is bumped) the
  // first time a signed-in buyer reaches the buyer home. It waits for the
  // tile transition to finish so it never appears underneath the cover.
  // Cancel returns to the Buyer/Seller screen; they can't use the buyer side
  // until they agree.
  // Once a signed-in visitor is on the store, the remembered pre-sign-in link
  // has done its job — drop it so it can't reopen the store on a later visit.
  useEffect(() => {
    if (currentScreen === "store" && hasSession) clearPendingStore();
  }, [currentScreen, hasSession]);

  // --- DIRECT ADDRESS: CONFIRM SESSION, THEN FIND THE LISTING ---
  // Runs once for a buyer address opened directly (see initialRoute). Signed
  // in: keep the screen it opened on. Signed out: remember the address, show
  // the landing page. Until this settles, useBackButtonHistory is paused so
  // the address in the bar isn't rewritten before we know where we are.
  useEffect(() => {
    if (!initialRoute) return;
    let cancelled = false;
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (cancelled) return;
      if (session) {
        clearPendingDeepLink();
        setRouteSessionOk(true);
        // A listing address still has to wait for the feed (next effect).
        if (!initialRoute.productShortId) setRoutePending(false);
        return;
      }
      setPendingDeepLink(initialRoute.path);
      setSelectedProduct(null);
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      // The landing page is showing after all, so let the preloader wait
      // for its models like a normal visit.
      setBootAssetsReady(false);
      setCurrentScreen("entry");
      setRoutePending(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !initialRoute?.productShortId ||
      !routeSessionOk ||
      !routePending ||
      isLoading
    )
      return;
    const match = listings.find((l) =>
      listingMatchesShortId(l, initialRoute.productShortId as string)
    );
    if (match) {
      setSelectedProduct(match as any);
    } else {
      showAppToast({
        title: "Listing unavailable",
        message: "That listing isn't available anymore.",
      });
    }
    setRoutePending(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSessionOk, routePending, isLoading, listings]);

  const [showBuyerTerms, setShowBuyerTerms] = useState(false);
  const termsUserIdRef = useRef<string | null>(null);
  // Remembers which account already passed the check, so moving between
  // screens doesn't ask the database again every time.
  const acceptedTermsUidRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      ![
        "buyer-home",
        "buyer-categories",
        "buyer-orders",
        "buyer-profile",
        "category",
        "store",
      ].includes(currentScreen) ||
      !hasSession ||
      tileActive
    )
      return;
    let cancelled = false;
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const uid = session?.user?.id;
      if (!uid || cancelled) return;
      termsUserIdRef.current = uid;
      if (acceptedTermsUidRef.current === uid) return;
      const accepted = await hasAcceptedTerms("buyer", uid);
      if (!cancelled && accepted) acceptedTermsUidRef.current = uid;
      if (!cancelled && !accepted) setShowBuyerTerms(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [currentScreen, hasSession, tileActive]);

  const showBottomNav =
    [
      "buyer-home",
      "buyer-categories",
      "buyer-orders",
      "buyer-profile",
    ].includes(currentScreen) && !selectedProduct;

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <div
        data-theme={theme}
        className="bg-[var(--bg-app)] min-h-screen w-full flex justify-center font-sans"
      >
        <style>{THEME_VARS_CSS}</style>
        <TermsAcceptanceSheet
          isOpen={showBuyerTerms}
          onAgree={() => {
            if (termsUserIdRef.current)
              void recordTermsAcceptance("buyer", termsUserIdRef.current);
            setShowBuyerTerms(false);
          }}
          onCancel={() => {
            setShowBuyerTerms(false);
            navigateTo("entry");
          }}
        />
        {showBootPreloader && (
          <Preloader_002
            ready={bootAssetsReady}
            onComplete={() => setShowBootPreloader(false)}
          />
        )}
        {/* APP TOAST — replaces native alert() popups app-wide. Floats
          independently above every screen, auto-dismisses on its own. */}
        {appToast && (
          <div className="fixed top-5 right-5 left-5 sm:left-auto sm:w-96 z-[300] animate-toastSlideIn">
            <style>{`
            @keyframes toastSlideIn {
              from { transform: translateY(-16px); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
            .animate-toastSlideIn { animation: toastSlideIn 0.3s ease-out; }
            @keyframes toastShrink {
              from { width: 100%; }
              to { width: 0%; }
            }
            .animate-toastShrink { animation: toastShrink 6s linear forwards; }
          `}</style>
            <div className="bg-[var(--bg-container)] border border-[#FF3B5C]/30 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden">
              <div className="flex items-start gap-3 p-4">
                <div className="w-9 h-9 rounded-full bg-[#FF3B5C]/15 flex items-center justify-center flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-[#FF3B5C]"
                  >
                    <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[var(--text-primary)]">
                    {appToast.title}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)] mt-0.5 leading-snug">
                    {appToast.message}
                  </p>
                </div>
                <button
                  onClick={() => setAppToast(null)}
                  className="text-[var(--text-muted)] hover:text-[var(--text-secondary)] flex-shrink-0"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="h-0.5 bg-white/10">
                <div className="h-full bg-[#FF3B5C] animate-toastShrink" />
              </div>
            </div>
          </div>
        )}
        <TileTransition
          active={tileActive}
          color={tileColor}
          message={tileMessage}
        />
        <CategoryStairsPreloader
          active={stairsActive}
          category={stairsCategory}
          onComplete={() => setStairsActive(false)}
        />
        {currentScreen === "entry" && (
          <EntryScreen
            runTileTransition={runTileTransition}
            handleBuyerClick={handleBuyerClick}
            runSellerEnterTransition={runSellerEnterTransition}
            showSellerLockedNote={showSellerLockedNote}
            initialSide={entrySide}
            onAssetsReady={() => setBootAssetsReady(true)}
          />
        )}
        {currentScreen === "buyer-auth" && (
          <BuyerAuth
            navigateTo={navigateTo}
            showAppToast={showAppToast}
            runTileTransition={runTileTransition}
          />
        )}
        {currentScreen === "store" && storeCode && (
          <StorePage
            storeCode={storeCode}
            onBack={closeStore}
            onSignIn={signInForStore}
            onProductClick={(product: any) => setSelectedProduct(product)}
            getAvatarColor={getAvatarColor}
          />
        )}
        {currentTabIndex !== -1 && (
          <div className="relative w-full min-h-screen overflow-hidden">
            <AnimatePresence
              initial={false}
              custom={tabSwipeDirection}
              mode="popLayout"
            >
              {currentScreen === "buyer-home" && (
                <motion.div
                  key="buyer-home"
                  custom={tabSwipeDirection}
                  variants={tabSwipeVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    type: "tween",
                    ease: [0.22, 1, 0.36, 1],
                    duration: 0.32,
                  }}
                  className="absolute inset-0"
                >
                  <BuyerHome
                    listings={listings}
                    onProductClick={(product) => setSelectedProduct(product)}
                    theme={theme}
                    navigateTo={navigateTo}
                    runTileTransition={runTileTransition}
                    selectedCounty={selectedCounty}
                    onCountyChange={setSelectedCounty}
                  />
                </motion.div>
              )}
              {currentScreen === "buyer-categories" && (
                <motion.div
                  key="buyer-categories"
                  custom={tabSwipeDirection}
                  variants={tabSwipeVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    type: "tween",
                    ease: [0.22, 1, 0.36, 1],
                    duration: 0.32,
                  }}
                  className="absolute inset-0"
                >
                  <BuyerCategories />
                </motion.div>
              )}
              {currentScreen === "buyer-orders" && (
                <motion.div
                  key="buyer-orders"
                  custom={tabSwipeDirection}
                  variants={tabSwipeVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    type: "tween",
                    ease: [0.22, 1, 0.36, 1],
                    duration: 0.32,
                  }}
                  className="absolute inset-0"
                >
                  <BuyerOrdersView onBack={() => navigateTo("buyer-home")} />
                </motion.div>
              )}
              {currentScreen === "buyer-profile" && (
                <motion.div
                  key="buyer-profile"
                  custom={tabSwipeDirection}
                  variants={tabSwipeVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    type: "tween",
                    ease: [0.22, 1, 0.36, 1],
                    duration: 0.32,
                  }}
                  className="absolute inset-0"
                >
                  <BuyerProfileView
                    onBack={() => navigateTo("buyer-home")}
                    navigateTo={navigateTo}
                    onProductClick={(product: any) =>
                      setSelectedProduct(product)
                    }
                    showAppToast={showAppToast}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
        {currentScreen === "category-transition" && (
          <CategoryTransitionScreen />
        )}
        {currentScreen === "category" && <StableCategoryScreen />}
        {/* ONE SellerPortal for both screens. It used to be rendered in two
            separate places ("seller-portal" and "seller-flow"), so the move
            from one to the other threw the first copy away and built a new
            one that drew nothing while it re-checked the seller's status —
            the 2-3 second black screen after the tiles opened. */}
        {(currentScreen === "seller-portal" ||
          currentScreen === "seller-flow") && (
          <SellerPortal
            navigateTo={navigateTo}
            currentScreen={currentScreen}
            setListings={setListings}
            onSellerReady={handleSellerPortalReady}
            onRequestHomeTransition={runSellerHomeTransition}
            onRequestDashboardTransition={runSellerUnlockTransition}
            runTileTransition={runTileTransition}
          />
        )}
        {currentScreen === "seller-success" && (
          <div className="flex flex-col items-center justify-center min-h-screen text-center px-6 bg-[var(--bg-app)] text-[var(--text-primary)]">
            <div className="mb-4 text-green-500 animate-bounce">
              <CheckCircle2 size={64} />
            </div>
            <h1 className="text-4xl font-extrabold tracking-wider text-[var(--text-primary)] mb-2">
              SUCCESS
            </h1>
            <p className="text-[var(--text-secondary)] text-sm max-w-xs mb-8 leading-relaxed">
              Your application is under review. Feel free to explore the
              marketplace while we verify your details.
            </p>
            <button
              onClick={() => navigateTo("seller-portal")}
              className="w-full max-w-xs bg-[#FFB800] hover:bg-[#E6A600] text-black font-semibold py-3 rounded-xl transition duration-200 active:scale-95 shadow-lg"
            >
              Go to Seller Portal
            </button>
          </div>
        )}

        {selectedProduct && (
          <ProductDetailsView
            product={selectedProduct}
            onBack={() => {
              if (!goBackFromProduct()) setSelectedProduct(null);
            }}
            navigateTo={navigateTo}
            showAppToast={showAppToast}
            onOpenStore={openSellerStore}
          />
        )}

        {showBottomNav && (
          <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-[var(--bg-container)]/95 backdrop-blur-xl border-t border-[var(--border-soft)] flex justify-around items-center py-3 px-2 z-50">
            <style>{`
            @keyframes navWarpSpin {
              0% { transform: rotateY(0deg) scale(1); }
              35% { transform: rotateY(126deg) scale(0.55); }
              70% { transform: rotateY(252deg) scale(1.15); }
              100% { transform: rotateY(360deg) scale(1); }
            }
            .nav-warp-spin {
              animation: navWarpSpin 0.6s ease-in-out;
              transform-style: preserve-3d;
            }
          `}</style>
            <button
              onMouseEnter={() => {
                handleNavHover("home");
                triggerNavSpin("home");
              }}
              onClick={() => {
                ensureNavColor("home");
                triggerNavSpin("home");
                navigateTo("buyer-home");
              }}
              className={`flex flex-col items-center gap-1 w-16 transition-colors duration-200 ${
                currentScreen === "buyer-home"
                  ? NAV_GLOW_CLASSES[navGlowColors.home || "blue"].text
                  : `text-[var(--text-primary)] ${
                      NAV_GLOW_CLASSES[navGlowColors.home || "blue"].hoverText
                    }`
              }`}
              style={{ perspective: 500 }}
            >
              <span
                key={navSpin.home || 0}
                className="inline-flex nav-warp-spin"
              >
                <Home
                  size={22}
                  className={
                    currentScreen === "buyer-home"
                      ? NAV_GLOW_CLASSES[navGlowColors.home || "blue"].iconGlow
                      : ""
                  }
                />
              </span>
              <span className="text-[10px] font-bold">Home</span>
            </button>

            <button
              onMouseEnter={() => {
                handleNavHover("explore");
                triggerNavSpin("explore");
              }}
              onClick={() => {
                ensureNavColor("explore");
                triggerNavSpin("explore");
                navigateTo("buyer-categories");
              }}
              className={`flex flex-col items-center gap-1 w-16 transition-colors duration-200 ${
                currentScreen === "buyer-categories"
                  ? NAV_GLOW_CLASSES[navGlowColors.explore || "blue"].text
                  : `text-[var(--text-primary)] ${
                      NAV_GLOW_CLASSES[navGlowColors.explore || "blue"]
                        .hoverText
                    }`
              }`}
              style={{ perspective: 500 }}
            >
              <span
                key={navSpin.explore || 0}
                className="inline-flex nav-warp-spin"
              >
                <Grid
                  size={22}
                  className={
                    currentScreen === "buyer-categories"
                      ? NAV_GLOW_CLASSES[navGlowColors.explore || "blue"]
                          .iconGlow
                      : ""
                  }
                />
              </span>
              <span className="text-[10px] font-bold">Explore</span>
            </button>

            <button
              onMouseEnter={() => {
                handleNavHover("orders");
                triggerNavSpin("orders");
              }}
              onClick={() => {
                ensureNavColor("orders");
                triggerNavSpin("orders");
                navigateTo("buyer-orders");
              }}
              className={`flex flex-col items-center gap-1 w-16 transition-colors duration-200 ${
                currentScreen === "buyer-orders"
                  ? NAV_GLOW_CLASSES[navGlowColors.orders || "blue"].text
                  : `text-[var(--text-primary)] ${
                      NAV_GLOW_CLASSES[navGlowColors.orders || "blue"].hoverText
                    }`
              }`}
              style={{ perspective: 500 }}
            >
              <span
                key={navSpin.orders || 0}
                className="inline-flex nav-warp-spin"
              >
                <ClipboardList
                  size={22}
                  className={
                    currentScreen === "buyer-orders"
                      ? NAV_GLOW_CLASSES[navGlowColors.orders || "blue"]
                          .iconGlow
                      : ""
                  }
                />
              </span>
              <span className="text-[10px] font-bold">Bookings</span>
            </button>

            <button
              onMouseEnter={() => {
                handleNavHover("account");
                triggerNavSpin("account");
              }}
              onClick={() => {
                ensureNavColor("account");
                triggerNavSpin("account");
                navigateTo("buyer-profile");
              }}
              className={`flex flex-col items-center gap-1 w-16 transition-colors duration-200 ${
                currentScreen === "buyer-profile"
                  ? NAV_GLOW_CLASSES[navGlowColors.account || "blue"].text
                  : `text-[var(--text-primary)] ${
                      NAV_GLOW_CLASSES[navGlowColors.account || "blue"]
                        .hoverText
                    }`
              }`}
              style={{ perspective: 500 }}
            >
              <span
                key={navSpin.account || 0}
                className="inline-flex nav-warp-spin"
              >
                <User
                  size={22}
                  className={
                    currentScreen === "buyer-profile"
                      ? NAV_GLOW_CLASSES[navGlowColors.account || "blue"]
                          .iconGlow
                      : ""
                  }
                />
              </span>
              <span className="text-[10px] font-bold">Account</span>
            </button>
          </div>
        )}
      </div>
    </ThemeContext.Provider>
  );
}

// --- FULL-SCREEN IMAGE LIGHTBOX COMPONENT ---
interface ImageLightboxProps {
  images: string[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  images,
  initialIndex = 0,
  isOpen,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  useEffect(() => {
    if (!isOpen) return;
    window.history.pushState({ lightboxOpen: true }, "");
    const handlePopState = () => onClose();
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      if (window.history.state?.lightboxOpen) {
        window.history.back();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen || !images || images.length === 0) return null;

  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;

    if (distance > minSwipeDistance) {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    } else if (distance < -minSwipeDistance) {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex flex-col justify-between bg-black/95 touch-none select-none">
      <div className="flex items-center justify-between p-4 text-white z-10">
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Close image viewer"
        >
          <X className="w-6 h-6" />
        </button>
        <span className="text-sm font-medium tracking-wide bg-black/40 px-3 py-1 rounded-full border border-white/10 font-mono">
          {currentIndex + 1} / {images.length}
        </span>
        <div className="w-10" />
      </div>

      <div
        className="relative flex-1 flex items-center justify-center w-full overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={images[currentIndex]}
          alt={`Product view ${currentIndex + 1}`}
          className="max-h-full max-w-full object-contain transition-all duration-200 ease-out"
        />
        {images.length > 1 && (
          <>
            <button
              onClick={() =>
                setCurrentIndex(
                  (prev) => (prev - 1 + images.length) % images.length
                )
              }
              className="hidden md:flex absolute left-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/80 transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() =>
                setCurrentIndex((prev) => (prev + 1) % images.length)
              }
              className="hidden md:flex absolute right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/80 transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="p-4 flex justify-center gap-2 overflow-x-auto z-10">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex
                  ? "border-white scale-105"
                  : "border-transparent opacity-50"
              }`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// --- PRODUCT DETAILS VIEW ---
// --- BOOKING CALENDAR WIDGET (buyer-facing, service listings only) ---
// Defined at module scope for the same reasons as BuyerProfileView — stays
// mounted across re-renders of ProductDetailsView.
const BOOKING_DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// A slot is "expired" once its date+time has already passed — this is what
// makes an open-but-never-booked slot stop being offerable automatically,
// with no DB write needed since it's just today's slots on the current date
// falling behind the clock as the day goes on.
function isSlotInPast(dateIso: string, time: string) {
  if (!dateIso || !time) return false;
  const [h, m] = time.split(":").map(Number);
  const slotDate = new Date(`${dateIso}T00:00:00`);
  slotDate.setHours(h, m, 0, 0);
  return slotDate.getTime() < Date.now();
}

function generateTimeSlots(
  startTime: string,
  endTime: string,
  slotMinutes: number
) {
  const slots: string[] = [];
  const [startH, startM] = (startTime || "09:00").split(":").map(Number);
  const [endH, endM] = (endTime || "17:00").split(":").map(Number);
  let cursor = startH * 60 + startM;
  const end = endH * 60 + endM;
  const step = slotMinutes || 60;

  while (cursor + step <= end) {
    const h = Math.floor(cursor / 60);
    const m = cursor % 60;
    slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    cursor += step;
  }
  return slots;
}

function BookingCalendarWidget({ product, catTheme }: any) {
  const availability = product?.availability || {};
  const availDays: string[] = availability.days || [];

  // Next 14 calendar days, marking which ones fall on a day the seller
  // offers this service.
  const upcomingDays = React.useMemo(() => {
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const abbr = BOOKING_DAY_ABBR[d.getDay()];
      days.push({
        date: d,
        iso: d.toISOString().slice(0, 10),
        abbr,
        dayNum: d.getDate(),
        isAvailable: availDays.includes(abbr),
      });
    }
    return days;
  }, [availability.days]);

  const firstAvailable = upcomingDays.find((d) => d.isAvailable);
  const [selectedDate, setSelectedDate] = React.useState<string | null>(
    firstAvailable?.iso || null
  );
  const [selectedTime, setSelectedTime] = React.useState<string | null>(null);
  const [takenSlots, setTakenSlots] = React.useState<string[]>([]);
  const [showBookingForm, setShowBookingForm] = React.useState(false);
  const [buyerName, setBuyerName] = React.useState("");
  const [buyerPhone, setBuyerPhone] = React.useState("");
  // Where the seller should show up (or drop off finished goods) —
  // matters most for home services (cleaning, pet sitting, errands, etc.)
  // where "your name and number" alone doesn't tell the seller WHERE to go.
  const [buyerLocation, setBuyerLocation] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [isSubmittingBooking, setIsSubmittingBooking] = React.useState(false);
  const [bookingSubmitted, setBookingSubmitted] = React.useState(false);
  const [bookingError, setBookingError] = React.useState("");

  const allSlots = generateTimeSlots(
    availability.start_time,
    availability.end_time,
    availability.slot_minutes
  );

  const refreshTakenSlots = React.useCallback(async () => {
    if (!selectedDate || !product?.id) return;
    const { data, error } = await supabase
      .from("bookings")
      .select("booking_time")
      .eq("listing_id", product.id)
      .eq("booking_date", selectedDate)
      .in("status", ["pending", "confirmed"]);

    if (!error) {
      setTakenSlots((data || []).map((b: any) => b.booking_time));
    }
  }, [selectedDate, product?.id]);

  React.useEffect(() => {
    if (!selectedDate || !product?.id) return;
    setSelectedTime(null);
    refreshTakenSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, product?.id]);

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) return;
    setIsSubmittingBooking(true);
    setBookingError("");

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setBookingError("Please sign in to request a booking.");
        return;
      }

      const { error } = await supabase.from("bookings").insert({
        listing_id: product.id,
        seller_id: product.seller_id,
        buyer_id: user.id,
        buyer_name: buyerName,
        buyer_phone: buyerPhone,
        buyer_location: buyerLocation,
        booking_date: selectedDate,
        booking_time: selectedTime,
        notes: notes || null,
        status: "pending",
      });

      if (error) {
        // Postgres unique_violation — another buyer grabbed this exact
        // slot a moment before this request landed. A DB-level partial
        // unique index (listing_id, booking_date, booking_time WHERE
        // status IN ('pending','confirmed')) is what makes this airtight
        // even when two requests arrive at nearly the same instant.
        if (error.code === "23505") {
          setBookingError(
            "This slot was just taken by another buyer — please pick another time."
          );
          setSelectedTime(null);
          setShowBookingForm(false);
          refreshTakenSlots();
          return;
        }
        throw error;
      }

      setBookingSubmitted(true);
    } catch (err: any) {
      console.error("Booking request failed:", err);
      setBookingError(
        err.message || "Something went wrong sending your booking request."
      );
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  if (!availDays.length || allSlots.length === 0) return null;

  return (
    <div className="bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-2xl p-4 space-y-4">
      <h4 className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">
        Book This Service
      </h4>

      {bookingSubmitted ? (
        <div className="text-center py-6 space-y-3">
          <CheckCircle2
            className="w-10 h-10 mx-auto"
            style={{ color: catTheme }}
          />
          <p className="text-[var(--text-primary)] font-semibold text-sm">
            Request sent to the seller
          </p>
          <p className="text-[var(--text-secondary)] text-xs max-w-xs mx-auto leading-relaxed">
            Your request has been submitted to the seller. Check under{" "}
            <span className="font-semibold text-stone-300">Account</span> for
            updates on whether they accepted or declined.
          </p>
        </div>
      ) : (
        <>
          {/* DATE PICKER */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {upcomingDays.map((d) => (
              <button
                key={d.iso}
                type="button"
                disabled={!d.isAvailable}
                onClick={() => setSelectedDate(d.iso)}
                className={`shrink-0 w-12 py-2 rounded-xl text-center transition-all ${
                  !d.isAvailable
                    ? "opacity-30 cursor-not-allowed bg-[var(--surface-soft)]"
                    : selectedDate === d.iso
                    ? "text-white shadow-md"
                    : "bg-[var(--surface-soft)] hover:bg-[var(--surface-soft-hover)] text-[var(--text-secondary)]"
                }`}
                style={
                  d.isAvailable && selectedDate === d.iso
                    ? { backgroundColor: catTheme }
                    : undefined
                }
              >
                <span className="block text-[10px] uppercase tracking-wider opacity-80">
                  {d.abbr}
                </span>
                <span className="block text-sm font-bold">{d.dayNum}</span>
              </button>
            ))}
          </div>

          {/* TIME SLOTS */}
          {selectedDate && (
            <div>
              <p className="text-[11px] text-stone-500 uppercase tracking-wider mb-2">
                Available Times
              </p>
              {bookingError && !showBookingForm && (
                <p className="text-xs text-red-400 mb-2">{bookingError}</p>
              )}
              <div className="flex flex-wrap gap-2">
                {allSlots.map((slot) => {
                  const isTaken = takenSlots.includes(slot);
                  const isExpired =
                    !isTaken && isSlotInPast(selectedDate, slot);
                  const isUnavailable = isTaken || isExpired;
                  return (
                    <button
                      key={slot}
                      type="button"
                      disabled={isUnavailable}
                      onClick={() => setSelectedTime(slot)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        isUnavailable
                          ? "bg-[var(--surface-soft)] text-[var(--text-muted)] line-through cursor-not-allowed"
                          : selectedTime === slot
                          ? "text-white"
                          : "bg-[var(--surface-soft)] hover:bg-[var(--surface-soft-hover)] text-[var(--text-secondary)]"
                      }`}
                      style={
                        !isUnavailable && selectedTime === slot
                          ? { backgroundColor: catTheme }
                          : undefined
                      }
                    >
                      {slot}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* BOOKING DETAILS FORM */}
          {selectedTime && !showBookingForm && (
            <button
              type="button"
              onClick={() => setShowBookingForm(true)}
              style={{ backgroundColor: catTheme }}
              className="w-full py-3 text-white font-bold rounded-xl shadow-lg transition-all hover:brightness-110 active:brightness-95 text-sm uppercase tracking-wider"
            >
              Continue to Book {selectedTime}
            </button>
          )}

          {selectedTime && showBookingForm && (
            <form onSubmit={handleBookingSubmit} className="space-y-3 pt-1">
              <SmoothInput
                type="text"
                required
                placeholder="Your name"
                value={buyerName}
                onChange={(e) => setBuyerName(e.target.value)}
                className="w-full bg-[var(--surface-soft)] text-[var(--text-primary)] border border-[var(--border-soft)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-white/30 placeholder:text-[var(--text-muted)]"
              />
              <SmoothInput
                type="tel"
                required
                placeholder="Phone number"
                value={buyerPhone}
                onChange={(e) => setBuyerPhone(e.target.value)}
                className="w-full bg-[var(--surface-soft)] text-[var(--text-primary)] border border-[var(--border-soft)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-white/30 placeholder:text-[var(--text-muted)]"
              />
              <SmoothInput
                type="text"
                required
                placeholder="Your location (address or landmark)"
                value={buyerLocation}
                onChange={(e) => setBuyerLocation(e.target.value)}
                className="w-full bg-[var(--surface-soft)] text-[var(--text-primary)] border border-[var(--border-soft)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-white/30 placeholder:text-[var(--text-muted)]"
              />
              <textarea
                rows={2}
                placeholder="Anything the seller should know? (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full bg-[var(--surface-soft)] text-[var(--text-primary)] border border-[var(--border-soft)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-white/30 placeholder:text-[var(--text-muted)] resize-none"
              />
              {bookingError && (
                <p className="text-xs text-red-400">{bookingError}</p>
              )}
              <button
                type="submit"
                disabled={isSubmittingBooking}
                style={{ backgroundColor: catTheme }}
                className="w-full py-3 text-white font-bold rounded-xl shadow-lg transition-all hover:brightness-110 active:brightness-95 text-sm uppercase tracking-wider disabled:opacity-60"
              >
                {isSubmittingBooking
                  ? "Sending Request..."
                  : `Request ${selectedDate} at ${selectedTime}`}
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
}

function ProductDetailsView({
  product,
  onBack,
  showAppToast,
  onOpenStore,
}: any) {
  const [activeImageIndex, setActiveImageIndex] = React.useState(0);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  // Seeded synchronously from the local cache (see WISHLIST_CACHE_KEY
  // above) so a listing already saved on this device renders as "Saved"
  // on the very first paint — no flash while we confirm with the server.
  const [isSaved, setIsSaved] = React.useState(() =>
    isListingSavedLocally(product?.id)
  );
  const [wishlistRowId, setWishlistRowId] = React.useState<string | null>(null);
  const [isTogglingSave, setIsTogglingSave] = React.useState(false);
  const [showContactSheet, setShowContactSheet] = React.useState(false);
  const [showRatingInfo, setShowRatingInfo] = React.useState(false);
  const productSaveBtnRef = useRef<HTMLButtonElement>(null);
  const productRatingInfoRef = useRef<HTMLButtonElement>(null);
  const productSellerBarRef = useRef<HTMLDivElement>(null);

  const isNewBuyerProduct = useIsNewBuyer();
  const productTour = useCoachTour(
    "product-details",
    [
      {
        ref: productSaveBtnRef,
        title: "Save for Later",
        text: "Tap Save to add this listing to your Wishlist so you can find it again from Account.",
      },
      {
        ref: productSellerBarRef,
        title: "Visit the Seller's Store",
        text: "Tap the seller bar to open this seller's store and browse everything else they offer.",
      },
      {
        ref: productRatingInfoRef,
        title: "Seller Rating Tiers",
        text: 'Tap the "i" to see what each rating tier means for this seller.',
      },
    ],
    isNewBuyerProduct
  );
  const contactSellerNameRef = useRef<HTMLHeadingElement>(null);
  const contactSellerLocationRef = useRef<HTMLParagraphElement>(null);
  const contactCardTour = useCoachTour(
    "contact-card",
    [
      {
        ref: contactSellerNameRef,
        title: "Seller Name",
        text: "This is the seller's display / business name.",
      },
      {
        ref: contactSellerLocationRef,
        title: "Institution / Location",
        text: "This shows the seller's institution or location.",
      },
    ],
    showContactSheet && isNewBuyerProduct
  );

  const [showReviewModal, setShowReviewModal] = React.useState(false);
  // 0 = not yet selected by the buyer — forces an intentional choice rather
  // than silently defaulting to a 5-star (which would count as "extreme").
  const [responseRating, setResponseRating] = React.useState(0);
  const [qualityRating, setQualityRating] = React.useState(0);
  const [reviewStatement, setReviewStatement] = React.useState("");
  const [reviewerEmail, setReviewerEmail] = React.useState("");
  const [reviewError, setReviewError] = React.useState("");
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = React.useState(false);

  // Rolling average / tier for this seller, computed from their last 10 reviews.
  const [sellerRollingAverage, setSellerRollingAverage] =
    React.useState<number>(5); // default: brand-new sellers start at 5/5
  const [isLoadingSellerRating, setIsLoadingSellerRating] =
    React.useState(true);

  const hasTrackedView = React.useRef<string | null>(null);

  // Derived seller application properties
  const sellerApp = getSellerApp(product);
  const sellerDisplayName =
    sellerApp?.display_name || // Buyers see display_name here too, never full_name
    "Seller";
  // "listings" has no category column — category always comes from seller_applications
  const sellerCategory = sellerApp?.category || "";
  const catKey = sellerCategory.toLowerCase();
  const catTheme = getThemeColorForRawCategory(catKey);
  const subcategoryLabel =
    getSubcategoryDisplayName(product?.subcategory) ||
    getCategoryDisplayLabel(sellerCategory);
  const avatarBg = getAvatarColor(sellerApp?.avatar_id);
  const sellerInitial = sellerDisplayName.charAt(0).toUpperCase() || "S";

  const phoneNumber =
    sellerApp?.phone_number ||
    sellerApp?.phone ||
    product?.sellerContact?.phone;
  const whatsappNumber =
    sellerApp?.whatsapp_number ||
    sellerApp?.phone_number ||
    sellerApp?.phone ||
    product?.sellerContact?.whatsapp;

  // Records that a buyer tapped Call or WhatsApp on this listing (fire and
  // forget — it must never delay or block opening the call / chat).
  const trackContact = (type: "contact_call" | "contact_whatsapp") => {
    if (!product?.id) return;
    supabase
      .rpc("track_listing_event", {
        p_listing_id: String(product.id),
        p_event_type: type,
      })
      .then(
        () => {},
        () => {}
      );
  };

  React.useEffect(() => {
    if (!product?.id || hasTrackedView.current === product.id) return;
    hasTrackedView.current = product.id;

    const trackListingView = async () => {
      try {
        await supabase.rpc("increment_listing_views", {
          listing_id: product.id,
        });
        // Timestamped copy of the view, so reports can count views per month.
        await supabase.rpc("track_listing_event", {
          p_listing_id: String(product.id),
          p_event_type: "view",
        });
      } catch (err) {
        console.error("Failed to trigger view tracking RPC:", err);
      }
    };

    trackListingView();
  }, [product?.id]);

  // Check whether this listing is already in the buyer's wishlist, so the
  // heart button opens in the right state instead of always starting empty.
  React.useEffect(() => {
    if (!product?.id) return;
    let isCancelled = false;

    async function checkWishlist() {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || isCancelled) return;

        const { data, error } = await supabase
          .from("wishlists")
          .select("id")
          .eq("buyer_id", user.id)
          .eq("listing_id", product.id)
          .maybeSingle();

        if (error) {
          console.error("[Wishlist] Error checking saved state:", error);
          return;
        }
        if (isCancelled) return;
        if (data) {
          setIsSaved(true);
          setWishlistRowId(data.id);
          setListingSavedLocally(product.id, true);
        } else {
          // Server says this listing isn't saved — trust it over a stale
          // local cache entry (e.g. it was unsaved from another device
          // or session since we last cached it here).
          setIsSaved(false);
          setWishlistRowId(null);
          setListingSavedLocally(product.id, false);
        }
      } catch (err) {
        console.error("Error checking wishlist:", err);
      }
    }

    checkWishlist();
    return () => {
      isCancelled = true;
    };
  }, [product?.id]);

  const handleToggleSave = async () => {
    if (!product?.id || isTogglingSave) return;
    setIsTogglingSave(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        showAppToast?.({
          title: "Sign in required",
          message: "Sign in to save listings to your wishlist.",
        });
        return;
      }

      if (isSaved) {
        // Optimistic remove — reflected in local state (this button),
        // the on-device cache other screens/visits read from, and the
        // DB, all up front rather than waiting on the DB round trip.
        const previousRowId = wishlistRowId;
        setIsSaved(false);
        setWishlistRowId(null);
        setListingSavedLocally(product.id, false);
        if (previousRowId) {
          const { error } = await supabase
            .from("wishlists")
            .delete()
            .eq("id", previousRowId);
          if (error) {
            console.error("Error removing from wishlist:", error);
            setIsSaved(true);
            setWishlistRowId(previousRowId);
            setListingSavedLocally(product.id, true);
            showAppToast?.({
              title: "Couldn't remove listing",
              message: error.message || "Please try again.",
            });
          }
        }
      } else {
        setIsSaved(true);
        setListingSavedLocally(product.id, true);
        const { data, error } = await supabase
          .from("wishlists")
          .insert({ buyer_id: user.id, listing_id: product.id })
          .select("id")
          .single();
        if (error) {
          // 23505 = unique_violation — this listing is already saved
          // (e.g. a previous attempt actually succeeded but the UI
          // hadn't caught up, or it was saved in another tab/session).
          // Treat that as success rather than surfacing a confusing
          // "couldn't save" error for something that's already true.
          if (error.code === "23505") {
            const { data: existing, error: fetchExistingError } = await supabase
              .from("wishlists")
              .select("id")
              .eq("buyer_id", user.id)
              .eq("listing_id", product.id)
              .maybeSingle();
            if (!fetchExistingError && existing) {
              setWishlistRowId(existing.id);
            } else {
              console.error(
                "[Wishlist] Duplicate save but couldn't fetch existing row:",
                fetchExistingError
              );
            }
          } else {
            // Logged in full (not just `.message`) since the most likely
            // real-world cause here is a Row Level Security policy on
            // the `wishlists` table blocking the insert for this user —
            // the `.message` alone often just says "new row violates
            // row-level security policy" without the table/policy name
            // that `error` (code/details/hint) makes visible.
            console.error("[Wishlist] Error saving listing:", error);
            setIsSaved(false);
            setListingSavedLocally(product.id, false);
            showAppToast?.({
              title: "Couldn't save listing",
              message: error.message || "Please try again.",
            });
          }
        } else {
          setWishlistRowId(data?.id || null);
        }
      }
    } catch (err: any) {
      console.error("Error toggling wishlist:", err);
      showAppToast?.({
        title: "Something went wrong",
        message: err?.message || "Please try again.",
      });
    } finally {
      setIsTogglingSave(false);
    }
  };

  // Fetch this seller's last 10 reviews and compute their rolling average.
  // Each review's "overall" contribution = average(response_score, quality_score).
  // A seller with no reviews yet defaults to 5/5 (Elite tier).
  React.useEffect(() => {
    let isCancelled = false;

    async function fetchSellerRating() {
      if (!product?.seller_id) {
        setSellerRollingAverage(5);
        setIsLoadingSellerRating(false);
        return;
      }
      setIsLoadingSellerRating(true);
      try {
        const { data, error } = await supabase
          .from("reviews")
          .select("response_score, quality_score")
          .eq("seller_id", product.seller_id)
          .order("created_at", { ascending: false })
          .limit(10);

        if (isCancelled) return;

        if (error || !data || data.length === 0) {
          setSellerRollingAverage(5);
        } else {
          const perReviewOverall = data.map(
            (r) => (Number(r.response_score) + Number(r.quality_score)) / 2
          );
          const avg =
            perReviewOverall.reduce((sum, v) => sum + v, 0) /
            perReviewOverall.length;
          setSellerRollingAverage(avg);
        }
      } catch (err) {
        console.error("Error computing seller rolling rating:", err);
        if (!isCancelled) setSellerRollingAverage(5);
      } finally {
        if (!isCancelled) setIsLoadingSellerRating(false);
      }
    }

    fetchSellerRating();
    return () => {
      isCancelled = true;
    };
  }, [product?.seller_id]);

  const images = React.useMemo(() => {
    if (
      product?.photo_urls &&
      Array.isArray(product.photo_urls) &&
      product.photo_urls.length > 0
    ) {
      return product.photo_urls;
    }
    if (!product?.image_url && !product?.images)
      return ["https://via.placeholder.com/600x375?text=No+Image"];
    if (Array.isArray(product.images) && product.images.length > 0)
      return product.images;
    if (Array.isArray(product.image_url)) return product.image_url;
    return [product.image_url || product.images];
  }, [product]);

  const sellerRatingTier = React.useMemo(
    () => getSellerRatingTier(sellerRollingAverage),
    [sellerRollingAverage]
  );

  const isJustificationRequired = reviewRequiresJustification(
    responseRating,
    qualityRating
  );
  const trimmedStatementLength = reviewStatement.trim().length;
  const isDescriptionValid =
    trimmedStatementLength >= 15 && trimmedStatementLength <= 180;
  const isEmailValid = /\S+@\S+\.\S+/.test(reviewerEmail.trim());
  const hasSelectedBothRatings = responseRating > 0 && qualityRating > 0;

  const canSubmitReview =
    hasSelectedBothRatings &&
    (!isJustificationRequired || (isEmailValid && isDescriptionValid));

  const handleReviewSubmit = async (e: any) => {
    e.preventDefault();
    setReviewError("");

    if (!hasSelectedBothRatings) {
      setReviewError("Please rate both response speed and quality.");
      return;
    }

    if (isJustificationRequired) {
      if (!isEmailValid) {
        setReviewError("Please enter a valid verification email address.");
        return;
      }
      if (!isDescriptionValid) {
        setReviewError("Please explain your rating in 15 to 180 characters.");
        return;
      }
    }

    setIsSubmittingReview(true);
    try {
      const { error } = await supabase.from("reviews").insert([
        {
          seller_id: product?.seller_id,
          listing_id: product?.id,
          response_score: responseRating,
          quality_score: qualityRating,
          // Only recorded when a 1-star or 5-star rating requires justification
          email: isJustificationRequired ? reviewerEmail.trim() : null,
          description: isJustificationRequired ? reviewStatement.trim() : null,
        },
      ]);
      if (error) {
        setReviewError(
          error.code === "23505"
            ? "You've already reviewed this listing. Each buyer can review a listing once."
            : "Database error: " + error.message
        );
        return;
      }
      setIsSubmitted(true);
    } catch (err) {
      console.error(err);
      setReviewError("An unexpected error occurred while saving your review.");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bg-app)] text-[var(--text-primary)] overflow-y-auto pb-32">
      {/* HEADER */}
      <div className="sticky top-0 z-10 flex items-center justify-between p-4 bg-[var(--bg-app)]/90 backdrop-blur-md border-b border-[var(--border-soft)]">
        <button
          onClick={onBack}
          className="p-2 hover:bg-[var(--surface-soft-hover)] rounded-full transition bg-[var(--surface-soft)]"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <span className="text-xs font-bold tracking-wider text-[var(--text-primary)] uppercase">
          Product Details
        </span>
        {/* Spacer to keep the title centered now that Save lives on the image */}
        <div className="w-10 h-10" />
      </div>

      <div className="max-w-md mx-auto space-y-4 p-4">
        {/* IMAGE DISPLAY */}
        <div
          className="relative aspect-[16/10] bg-stone-900 rounded-2xl overflow-hidden cursor-pointer border border-[var(--border-soft)] shadow-xl"
          onClick={() => setLightboxOpen(true)}
        >
          <img
            src={images[activeImageIndex]}
            alt={product?.title}
            className="w-full h-full object-cover transition duration-300"
          />
          <button
            ref={productSaveBtnRef}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleSave();
            }}
            disabled={isTogglingSave}
            className={`absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold backdrop-blur-sm border transition disabled:opacity-60 ${
              isSaved
                ? "bg-amber-500/90 border-amber-400 text-black"
                : "bg-black/60 border-white/20 text-white hover:bg-black/80"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 transition-colors ${
                isSaved ? "fill-black text-black" : "text-white"
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            {isSaved ? "Saved" : "Save"}
          </button>
          {images.length > 1 && (
            <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-mono tracking-wider text-stone-300 border border-[var(--border-soft)]">
              {activeImageIndex + 1} / {images.length}
            </div>
          )}
        </div>

        {images.length > 1 && (
          <div className="flex gap-2.5 overflow-x-auto pb-1">
            {images.map((img: string, idx: number) => (
              <button
                key={idx}
                onClick={() => setActiveImageIndex(idx)}
                style={
                  idx === activeImageIndex
                    ? { borderColor: catTheme }
                    : undefined
                }
                className={`w-14 h-10 rounded-lg overflow-hidden border-2 flex-shrink-0 transition ${
                  idx === activeImageIndex
                    ? "scale-95 shadow-md"
                    : "border-transparent opacity-60"
                }`}
              >
                <img
                  src={img}
                  alt="thumbnail"
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}

        {/* PRICE & DYNAMIC TITLE */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-black tracking-tight text-[var(--text-primary)]">
              {product?.listing_kind === "service" ? "From " : ""}
              {typeof product?.price === "string" &&
              product.price.includes("KES")
                ? product.price
                : `KES ${Number(product?.price || 0).toLocaleString()}`}
            </span>
            {product?.listing_kind !== "service" &&
              Number(product?.stock_quantity ?? 1) > 0 &&
              Number(product?.stock_quantity ?? 1) <= 2 && (
                <span className="bg-red-500/20 text-red-400 text-[10px] font-bold px-2 py-0.5 rounded border border-red-500/30 uppercase tracking-wider animate-pulse">
                  Only {product.stock_quantity} left
                </span>
              )}
          </div>

          <h1 className="text-xl font-bold tracking-wide text-[var(--text-primary)]">
            {product?.title || "Untitled Listing"}
          </h1>

          <span
            style={{
              backgroundColor: `${catTheme}1A`,
              color: catTheme,
              borderColor: `${catTheme}33`,
            }}
            className="inline-block text-[10px] font-bold px-2.5 py-0.5 rounded border uppercase tracking-widest mt-1"
          >
            {subcategoryLabel}
          </span>
        </div>

        {/* SELLER PROFILE BAR */}
        {/* The whole bar is the "open store" button, painted in the seller's
            category colour. It is a stretched <button> behind the content
            (no buttons nested inside buttons); the content layered on top is
            pointer-events-none so taps anywhere fall through to it, except on
            the "i" and "Rate Seller" buttons, which switch pointer events
            back on and keep doing their own thing. */}
        <div
          ref={productSellerBarRef}
          className="relative flex items-center justify-between gap-3 p-4 rounded-2xl border border-white/15"
          style={{
            backgroundColor: catTheme,
            boxShadow: `0 8px 24px -10px ${catTheme}99`,
          }}
        >
          <button
            type="button"
            onClick={() => onOpenStore?.(product)}
            aria-label={`Open ${sellerDisplayName}'s store`}
            className="absolute inset-0 rounded-2xl transition-colors hover:bg-white/5 active:bg-black/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
          />

          <div className="relative flex items-center gap-3 min-w-0 pointer-events-none">
            <div
              className={`w-11 h-11 rounded-full ${avatarBg} flex items-center justify-center font-bold text-white shadow-md border border-white/20 ring-2 ring-white/60 shrink-0 text-lg`}
            >
              {sellerInitial}
            </div>
            <div className="min-w-0">
              <span className="font-semibold text-white flex items-center gap-1 text-sm">
                <span className="truncate">{sellerDisplayName}</span>
                <ChevronRight size={14} className="shrink-0 text-white/80" />
              </span>
              {isLoadingSellerRating ? (
                <div className="flex items-center gap-1.5 mt-1 rounded-full bg-black/30 px-2 py-0.5 w-fit">
                  <span className="w-2 h-2 rounded-full bg-white/30 animate-pulse" />
                  <span className="h-3 w-16 rounded bg-white/20 animate-pulse" />
                </div>
              ) : (
                <div
                  className={`inline-flex items-center gap-1.5 text-xs font-medium mt-1 rounded-full bg-black/35 px-2 py-0.5 ${sellerRatingTier.textClass}`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${sellerRatingTier.dotClass}`}
                  />
                  {sellerRatingTier.label}
                </div>
              )}
            </div>
          </div>

          <div className="relative flex items-center gap-1.5 shrink-0 pointer-events-none">
            <div className="relative">
              <button
                ref={productRatingInfoRef}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRatingInfo(true);
                }}
                aria-label="What do seller rating tiers mean?"
                className="pointer-events-auto w-7 h-7 rounded-full flex items-center justify-center bg-[var(--bg-container)] hover:bg-[var(--bg-container-2)] text-[var(--text-primary)] border border-[var(--border-soft)] shadow-sm transition-transform hover:scale-110 active:scale-95"
              >
                <Info className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>

            {/* SELLER RATING TIERS POPOVER
                Rendered as a simple centered modal (same pattern as the
                "Business Contact Card" modal below) instead of an
                anchored, pixel-measured tooltip. The old version used
                getBoundingClientRect + createPortal to position itself
                next to the "i" button, which broke on this page: a
                transformed/animated ancestor made its "fixed"
                coordinates resolve against the wrong box, so the popover
                rendered oversized and mostly off-screen, forcing users
                to swipe sideways to read it (and made it look like the
                button "didn't work"). A centered, viewport-clamped
                modal can't run off-screen on any layout, mobile or
                desktop, so it fixes both the proportions and the click
                behavior at once. */}
            <AnimatePresence>
              {showRatingInfo && (
                <motion.div
                  key="rating-info-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="pointer-events-auto fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                  onPointerDown={() => setShowRatingInfo(false)}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="w-full max-w-xs sm:max-w-sm p-4 rounded-xl bg-[var(--bg-container-2)] border border-[var(--border-soft)] text-[var(--text-primary)] text-xs shadow-2xl relative max-h-[80vh] overflow-y-auto"
                  >
                    <div className="flex items-center justify-between mb-2.5">
                      <p className="font-bold uppercase tracking-wider text-[10px] text-[var(--text-secondary)]">
                        Seller Rating Tiers
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowRatingInfo(false)}
                        aria-label="Close"
                        className="p-1 -m-1 rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-soft-hover)] transition-colors shrink-0"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className="space-y-2.5 text-left">
                      <div className="flex items-start gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1 shrink-0" />
                        <span>
                          <strong className="text-emerald-400">
                            Top Rated
                          </strong>{" "}
                          — rolling average of 4.0★ and above over their last 10
                          reviews.
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-500 mt-1 shrink-0" />
                        <span>
                          <strong className="text-amber-400">
                            Established
                          </strong>{" "}
                          — rolling average between 2.0★ and 4.0★.
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="w-2 h-2 rounded-full bg-rose-500 mt-1 shrink-0" />
                        <span>
                          <strong className="text-rose-400">
                            Fairly Reliable
                          </strong>{" "}
                          — rolling average below 2.0★.
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => setShowReviewModal(true)}
              className="pointer-events-auto px-4 py-2 bg-[var(--bg-container)] hover:bg-[var(--bg-container-2)] border border-[var(--border-soft)] shadow-sm text-xs font-semibold text-[var(--text-primary)] rounded-xl transition-colors"
            >
              Rate Seller
            </motion.button>
          </div>
        </div>

        {/* SERVICE AREA (service listings only) — where the seller offers
            the service. Shown before the booking calendar so a buyer knows
            whether it covers them before choosing a slot. Older listings
            with no service area simply show nothing here. */}
        {product?.listing_kind === "service" &&
          String(product?.service_area || "").trim() && (
            <div className="flex items-start gap-3 p-3.5 rounded-2xl border border-[var(--border-soft)] bg-[var(--bg-container)]">
              <div
                style={{ backgroundColor: `${catTheme}1A`, color: catTheme }}
                className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              >
                <MapPin size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-secondary)]">
                  Service Area
                </p>
                <p className="text-sm font-semibold text-[var(--text-primary)] break-words">
                  {String(product.service_area).trim()}
                </p>
              </div>
            </div>
          )}

        {/* BOOKING CALENDAR (service listings only) */}
        {product?.listing_kind === "service" && (
          <BookingCalendarWidget product={product} catTheme={catTheme} />
        )}

        {/* OVERVIEW & DETAILS */}
        <div className="space-y-2 pt-2">
          <h4 className="text-xs font-bold uppercase tracking-wider text-[var(--text-primary)]">
            Overview & Details
          </h4>
          <p className="text-sm text-[var(--text-primary)] leading-relaxed whitespace-pre-wrap">
            {product?.description ||
              "No product description overview provided by the vendor for this listing yet."}
          </p>
        </div>

        {/* CONTACT BUTTON — hidden for services; the booking flow above replaces it */}
        {product?.listing_kind !== "service" && (
          <motion.button
            whileHover={{ scale: 1.015 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowContactSheet(true)}
            style={{ backgroundColor: catTheme }}
            className="w-full py-3.5 text-white font-bold rounded-xl shadow-lg transition-all hover:brightness-110 active:brightness-95 flex items-center justify-center gap-2 text-sm uppercase tracking-wider mt-4"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            Contact Seller
          </motion.button>
        )}
      </div>

      {/* REVIEW MODAL */}
      <AnimatePresence>
        {showReviewModal && (
          <motion.div
            key="review-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ type: "spring", stiffness: 400, damping: 32 }}
              className="bg-[var(--bg-container)] w-full max-w-md rounded-2xl p-6 border border-[var(--border-soft)] shadow-2xl flex flex-col max-h-[85vh]"
            >
              {!isSubmitted ? (
                <>
                  <div className="flex justify-between items-center pb-3 border-b border-[var(--border-soft)] mb-4">
                    <h3 className="text-base font-bold text-[var(--text-primary)]">
                      Leave an Honest Review
                    </h3>
                    <button
                      onClick={() => setShowReviewModal(false)}
                      className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] text-xs"
                    >
                      Cancel
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto space-y-5 pr-3 -mr-3">
                    <div>
                      <label className="text-xs font-semibold text-[var(--text-secondary)] block mb-2">
                        1. Response Speed Score
                      </label>
                      <StarRating
                        value={responseRating}
                        onChange={setResponseRating}
                        size={28}
                        ariaLabel="Response speed rating"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-[var(--text-secondary)] block mb-2">
                        2. Product / Service Quality
                      </label>
                      <StarRating
                        value={qualityRating}
                        onChange={setQualityRating}
                        size={28}
                        ariaLabel="Product or service quality rating"
                      />
                    </div>

                    {isJustificationRequired && (
                      <div className="space-y-3 animate-fadeIn">
                        <SmoothInput
                          type="text"
                          inputMode="email"
                          autoCapitalize="none"
                          autoCorrect="off"
                          value={reviewerEmail}
                          onChange={(e) => setReviewerEmail(e.target.value)}
                          placeholder="Verification Email Address"
                          className="w-full bg-[var(--surface-soft)] border border-[var(--border-soft)] rounded-xl px-3 py-2 text-sm text-[var(--text-primary)]"
                        />

                        <div>
                          <textarea
                            value={reviewStatement}
                            onChange={(e) =>
                              setReviewStatement(e.target.value.slice(0, 180))
                            }
                            placeholder="Explain why you gave this rating (15-180 characters)..."
                            maxLength={180}
                            rows={3}
                            className="w-full bg-[var(--surface-soft)] border border-[var(--border-soft)] rounded-xl px-3 py-2 text-sm text-[var(--text-primary)] resize-none"
                          />
                          <div className="flex justify-end mt-1">
                            <span
                              className={`text-[11px] ${
                                trimmedStatementLength > 0 &&
                                trimmedStatementLength < 15
                                  ? "text-rose-400"
                                  : "text-stone-500"
                              }`}
                            >
                              {trimmedStatementLength}/180
                              {trimmedStatementLength > 0 &&
                                trimmedStatementLength < 15 &&
                                ` (min 15)`}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {reviewError && (
                      <p className="text-xs text-rose-400 text-center">
                        {reviewError}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={handleReviewSubmit}
                    disabled={!canSubmitReview || isSubmittingReview}
                    className="mt-4 w-full bg-[#1006C5] hover:bg-[#0D0599] disabled:bg-stone-800 disabled:text-stone-500 py-3 rounded-xl font-bold text-sm text-white transition-colors"
                  >
                    {isSubmittingReview ? "Submitting..." : "Submit Review"}
                  </button>
                </>
              ) : (
                <div className="p-6 text-center space-y-3">
                  <div className="text-3xl font-bold text-emerald-400">✓</div>
                  <h3 className="text-lg font-bold text-[var(--text-primary)]">
                    Thanks for your review!
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      // Reset the modal's local state so the next review starts fresh,
                      // then close — returning the buyer to the product view underneath.
                      setShowReviewModal(false);
                      setIsSubmitted(false);
                      setResponseRating(0);
                      setQualityRating(0);
                      setReviewStatement("");
                      setReviewerEmail("");
                      setReviewError("");
                    }}
                    className="mt-2 w-full bg-[#1006C5] hover:bg-[#0D0599] py-3 rounded-xl font-bold text-sm text-white transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* BUSINESS CONTACT CARD MODAL */}
      <AnimatePresence>
        {showContactSheet && (
          <motion.div
            key="contact-sheet-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ type: "spring", stiffness: 400, damping: 32 }}
              className="w-full max-w-md bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-2xl p-6 shadow-2xl relative text-[var(--text-primary)]"
            >
              {/* HEADER */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-base font-bold text-[var(--text-primary)] tracking-wide">
                  Business Contact Card
                </h3>
                <button
                  onClick={() => setShowContactSheet(false)}
                  className="p-1 rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-soft-hover)] transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* SELLER AVATAR & NAME */}
              <div className="flex items-center gap-4 mb-6">
                <div
                  className={`w-14 h-14 rounded-2xl ${avatarBg} flex items-center justify-center text-2xl font-black text-white shadow-lg border border-white/20 shrink-0`}
                >
                  {sellerInitial}
                </div>
                <div>
                  <h2
                    ref={contactSellerNameRef}
                    className="text-lg font-bold text-[var(--text-primary)] leading-snug"
                  >
                    {sellerDisplayName}
                  </h2>
                  <p
                    ref={contactSellerLocationRef}
                    className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] font-medium"
                  >
                    <MapPin size={14} className="shrink-0" />
                    <span>
                      {sellerApp?.location ||
                        sellerApp?.university ||
                        "Active Campus Seller"}
                    </span>
                  </p>
                </div>
              </div>

              {/* CONTACT METHODS */}
              <div className="space-y-3 mb-6">
                <motion.a
                  href={phoneNumber ? `tel:${phoneNumber}` : "#"}
                  onClick={() => {
                    if (phoneNumber) trackContact("contact_call");
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.96 }}
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 30,
                    mass: 0.5,
                  }}
                  className="flex items-center gap-3.5 p-3.5 bg-[#3B82F6]/10 hover:bg-[#3B82F6]/20 rounded-xl border border-[#3B82F6]/30 transition-colors group relative overflow-hidden"
                  style={{ ["--shine" as any]: "rgba(96,165,250,0.55)" }}
                >
                  <svg
                    className="w-5 h-5 text-[var(--accent-blue)] shrink-0 relative z-10"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                    />
                  </svg>
                  <motion.div
                    className="flex flex-col relative z-10"
                    style={{
                      WebkitMaskImage:
                        "linear-gradient(-75deg, white calc(var(--mask-x) + 20%), transparent calc(var(--mask-x) + 30%), white calc(var(--mask-x) + 100%))",
                      maskImage:
                        "linear-gradient(-75deg, white calc(var(--mask-x) + 20%), transparent calc(var(--mask-x) + 30%), white calc(var(--mask-x) + 100%))",
                    }}
                    initial={{ ["--mask-x" as any]: "100%" } as any}
                    animate={{ ["--mask-x" as any]: "-100%" } as any}
                    transition={{
                      repeat: Infinity,
                      duration: 1.6,
                      ease: "linear",
                      repeatDelay: 1.4,
                    }}
                  >
                    <span className="text-[10px] uppercase tracking-wider text-[var(--accent-blue)] font-bold">
                      Phone Call
                    </span>
                    <span className="text-sm font-semibold text-[var(--text-primary)] tracking-wide">
                      {phoneNumber || "No phone provided"}
                    </span>
                  </motion.div>
                  {/* Shine sweep across the whole button surface */}
                  <motion.span
                    className="pointer-events-none block absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(-75deg, transparent 30%, var(--shine) 50%, transparent 70%)",
                      backgroundSize: "200% 100%",
                    }}
                    initial={{ backgroundPosition: "100% 0", opacity: 0 }}
                    animate={{
                      backgroundPosition: ["100% 0", "0% 0"],
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: 1.6,
                      repeat: Infinity,
                      ease: "linear",
                      repeatDelay: 1.4,
                    }}
                  />
                  {/* Traveling shine on the border ring */}
                  <motion.span
                    className="pointer-events-none block absolute inset-0 rounded-xl p-px"
                    style={{
                      background:
                        "linear-gradient(-75deg, transparent 30%, var(--shine) 50%, transparent 70%)",
                      backgroundSize: "200% 100%",
                      mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      maskComposite: "exclude",
                      WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                    }}
                    initial={{ backgroundPosition: "100% 0", opacity: 0 }}
                    animate={{
                      backgroundPosition: ["100% 0", "0% 0"],
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: 1.6,
                      repeat: Infinity,
                      ease: "linear",
                      repeatDelay: 1.4,
                    }}
                  />
                </motion.a>

                <motion.a
                  href={
                    whatsappNumber
                      ? `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, "")}`
                      : "#"
                  }
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    if (whatsappNumber) trackContact("contact_whatsapp");
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.96 }}
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 30,
                    mass: 0.5,
                  }}
                  className="flex items-center gap-3.5 p-3.5 bg-[#25D366]/10 hover:bg-[#25D366]/20 rounded-xl border border-[#25D366]/30 transition-colors group relative overflow-hidden"
                  style={{ ["--shine" as any]: "rgba(74,222,128,0.55)" }}
                >
                  <svg
                    className="w-5 h-5 text-[var(--accent-green)] shrink-0 relative z-10"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.572-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.99c-.002 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  <motion.div
                    className="flex flex-col relative z-10"
                    style={{
                      WebkitMaskImage:
                        "linear-gradient(-75deg, white calc(var(--mask-x) + 20%), transparent calc(var(--mask-x) + 30%), white calc(var(--mask-x) + 100%))",
                      maskImage:
                        "linear-gradient(-75deg, white calc(var(--mask-x) + 20%), transparent calc(var(--mask-x) + 30%), white calc(var(--mask-x) + 100%))",
                    }}
                    initial={{ ["--mask-x" as any]: "100%" } as any}
                    animate={{ ["--mask-x" as any]: "-100%" } as any}
                    transition={{
                      repeat: Infinity,
                      duration: 1.6,
                      ease: "linear",
                      repeatDelay: 1.4,
                    }}
                  >
                    <span className="text-[10px] uppercase tracking-wider text-[var(--accent-green)] font-bold">
                      WhatsApp
                    </span>
                    <span className="text-sm font-semibold text-[var(--text-primary)] tracking-wide">
                      {whatsappNumber || "No WhatsApp provided"}
                    </span>
                  </motion.div>
                  {/* Shine sweep across the whole button surface */}
                  <motion.span
                    className="pointer-events-none block absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(-75deg, transparent 30%, var(--shine) 50%, transparent 70%)",
                      backgroundSize: "200% 100%",
                    }}
                    initial={{ backgroundPosition: "100% 0", opacity: 0 }}
                    animate={{
                      backgroundPosition: ["100% 0", "0% 0"],
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: 1.6,
                      repeat: Infinity,
                      ease: "linear",
                      repeatDelay: 1.4,
                    }}
                  />
                  {/* Traveling shine on the border ring */}
                  <motion.span
                    className="pointer-events-none block absolute inset-0 rounded-xl p-px"
                    style={{
                      background:
                        "linear-gradient(-75deg, transparent 30%, var(--shine) 50%, transparent 70%)",
                      backgroundSize: "200% 100%",
                      mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      maskComposite: "exclude",
                      WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                    }}
                    initial={{ backgroundPosition: "100% 0", opacity: 0 }}
                    animate={{
                      backgroundPosition: ["100% 0", "0% 0"],
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: 1.6,
                      repeat: Infinity,
                      ease: "linear",
                      repeatDelay: 1.4,
                    }}
                  />
                </motion.a>
              </div>
              {/* SAFETY NOTICE */}
              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <div className="flex items-center gap-2 text-[var(--accent-amber)] text-xs font-bold uppercase tracking-wider mb-1">
                  <svg
                    className="w-4 h-4 text-[var(--accent-amber)] shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    />
                  </svg>
                  <span>SAFETY NOTICE</span>
                </div>
                <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                  Always meet sellers in open, public campus spaces to guarantee
                  safety.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ImageLightbox
        images={images}
        initialIndex={activeImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
      {productTour.activeStep && (
        <Coachmark
          step={productTour.activeStep}
          stepIndex={productTour.stepIndex}
          totalSteps={productTour.totalSteps}
          onNext={productTour.next}
          onDismiss={productTour.dismiss}
        />
      )}
      {contactCardTour.activeStep && (
        <Coachmark
          step={contactCardTour.activeStep}
          stepIndex={contactCardTour.stepIndex}
          totalSteps={contactCardTour.totalSteps}
          onNext={contactCardTour.next}
          onDismiss={contactCardTour.dismiss}
        />
      )}
    </div>
  );
}
